<template>
  <a-config-provider :locale="ruRU">
    <AppLogin
      v-if="showWebLoginForm"
      @login-success="onWebLoginSuccess"
    />
    <div v-else-if="accessDenied" class="access-denied-container">
      <h1>Access Denied</h1>
      <p>This application can only be accessed through the Telegram app.</p>
      <p>Please open the bot and launch the app from the menu.</p>
    </div>
    <div id="app" v-else-if="!showWebLoginForm && !accessDenied">
    <!-- Header -->
    <a-layout-header class="app-header">
      <div class="header-content">
        <!-- Main Screen Header -->
        <template v-if="currentScreen === 'main'">
          <div class="header-left">
                          <a-badge 
                v-if="isAdmin && totalPendingRequests > 0" 
                :count="totalPendingRequests" 
                :offset="[2, -2]"
                class="header-pending-badge"
              >
              <a-button type="text" @click="showDrawer" class="header-button menu-icon-button">
                <MenuOutlined />
              </a-button>
            </a-badge>
            <a-button v-else type="text" @click="showDrawer" class="header-button menu-icon-button">
              <MenuOutlined />
            </a-button>
            <div class="team-header-info">
              <h2 
                v-if="currentTeamName" 
                class="screen-title"
                @click="showTeamInfo"
                :class="{ 'clickable': currentTeamName }"
              >
                {{ currentTeamName }}
              </h2>
              <h2 v-else class="screen-title">Выберите команду</h2>
            </div>
          </div>
          <div class="header-right">
            <div class="profile-button-container">
              
              <a-button type="text" @click="showTeamInfo" class="header-button team-avatar-button">
                <a-avatar :src="currentTeamInfo?.logo_url" size="large">
                  <template #icon>
                    <TeamOutlined />
                  </template>
                </a-avatar>
              </a-button>
            </div>
          </div>
        </template>

  
        <!-- Other Screens Header -->
        <template v-else>
          <div class="header-left">
            <a-button type="text" @click="handleBackNavigation" class="header-button back-icon-button">
              <LeftOutlined />
            </a-button>
            <h2 class="screen-title">{{ getScreenTitle }}</h2>
          </div>
          <!-- Delete button for edit event screen -->
          <div v-if="currentScreen === 'editEvent' && editingEvent" class="header-right">
            <a-popconfirm
              placement="bottom"
              ok-text="Удалить"
              cancel-text="Отмена"
              @confirm="handleDeleteEvent"
            >
              <template #title>
                <div v-if="editingEvent.repeating_event_id">
                  <div style="margin-bottom: 12px; font-size: 16px; font-weight: 600;">
                    Удаление повторяющегося события
                  </div>
                  <div style="font-size: 14px; color: #595959; margin-bottom: 16px;">
                    Выберите, какие события удалить:
                  </div>
                </div>
                <div v-else>
                  <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">
                    Удаление события
                  </div>
                  <div style="font-size: 14px; color: #595959;">
                    Вы уверены, что хотите удалить это событие? Это действие нельзя отменить.
                  </div>
                </div>
              </template>
              <template #description>
                <div v-if="editingEvent.repeating_event_id">
                  <a-radio-group v-model:value="editModeValue" button-style="solid">
                    <a-radio-button value="single">Только это событие</a-radio-button>
                    <a-radio-button value="future">Все будущие события</a-radio-button>
                    <a-radio-button value="all">Все события в серии</a-radio-button>
                  </a-radio-group>
                </div>
              </template>
              <a-button type="text" class="header-button delete-icon-button">
                <DeleteOutlined />
              </a-button>
            </a-popconfirm>
          </div>
          <!-- Edit button for team info screen -->
          <div v-if="currentScreen === 'teamInfo' && isAdmin" class="header-right">
            <a-button type="text" @click="editCurrentTeam" class="header-button edit-icon-button">
              <EditOutlined />
            </a-button>
          </div>
          <!-- Settings button for payments screen (only for team admins) -->
          <div v-if="currentScreen === 'payments' && isTeamAdmin" class="header-right">
            <a-button type="text" @click="showPaymentTypesManagement" class="header-button settings-icon-button">
              <SettingOutlined />
            </a-button>
          </div>
          <!-- Add button for payment types management screen -->
          <div v-if="currentScreen === 'paymentTypesManagement' && canManagePayments" class="header-right">
            <a-button type="text" @click="showCreatePaymentTypeModal" class="header-button add-icon-button">
              <PlusOutlined />
            </a-button>
          </div>
        </template>
      </div>
    </a-layout-header>

    <!-- Main Content -->
    <div class="main-content">
      <div class="container">
        <!-- Main Screen Content -->
        <div v-if="currentScreen === 'main'" class="screen-content">
          <!-- Subscription Warning -->
          <SubscriptionWarning
            v-if="showSubscriptionWarning"
            :subscription-status="subscriptionStatus"
            @close="showSubscriptionWarning = false"
            @go-to-subscription="showSubscription"
          />
          
          <div class="header-actions">
            <h3 class="events-title">Предстоящие события</h3>
            <a-button type="text" @click="showScreen('calendar')" class="header-button calendar-icon-button">
              <CalendarOutlined />
            </a-button>
            <a-button v-if="isAdmin" type="text" @click="showScreen('createEvent')" class="header-button plus-icon-button">
              <PlusOutlined /> 
            </a-button>
          </div>
          
          <div v-if="currentTeamName" class="events-section">
            <!-- Birthday notification -->
            <div v-if="birthdayPlayers.length > 0" class="birthday-notification">
              <div class="birthday-header">
                <span class="birthday-icon">🎂</span>
                <span class="birthday-title">День рождения!</span>
              </div>
              <div class="birthday-players">
                <span v-for="(player, index) in birthdayPlayers" :key="player.id">
                  {{ player.last_name }} {{ player.first_name }}<template v-if="index < birthdayPlayers.length - 1">, </template>
                </span>
              </div>
            </div>
            
            <template v-if="events.length === 0">
              <div class="no-events">
                <h3>У вас нет событий</h3>
                <p>Создайте первое событие для вашей команды.</p>
                <a-button v-if="isAdmin" type="primary" size="large" block style="margin-top: 24px; font-size: 20px; height: 56px;" @click="showScreen('createEvent')">
                  <PlusOutlined /> Создать событие
                </a-button>
              </div>
            </template>
            <template v-else>
          <List :data-source="events" item-layout="horizontal">
            <template #renderItem="{ item }">
              <Card class="event-card">
                <!-- Event Type Badge and Participation Status -->
                <div class="event-card-header" @click="showScreenEvetPlayers(item)">
                    
                  <div class="event-type-badge" 
                  
                    :class="item?.user_participation_status === 2 ? 'status-participating' : item?.user_participation_status === 1 ? 'status-not-participating' : 'status-unknown'"
                    @click.stop="openEventParticipationDrawer(item)">
                  <span class="badge-icon">
                      <TrophyOutlined v-if="item.type === 'game'" />
                      <RiseOutlined  v-else />
                    </span>
                    <span class="badge-text">{{ item.type === 'game' ? 'Игра' : 'Тренировка' }}</span>
                </div>




                  

                 
                                  <!-- Event Actions -->
                <div class="event-actions">
                  <a-button v-if="isAdmin" type="text" @click.stop="editEvent(item)" class="edit-icon-button">
                    <EditOutlined />
                  </a-button>
                </div>
                </div>

                <!-- Event Content -->
                <div class="event-card-content" @click="showScreenEvetPlayers(item)">
                  <!-- Event Details -->
                  <div class="event-details">
                    <div class="event-info-row">
                      <ClockCircleOutlined class="event-icon" />
                      <span class="event-text event-date-highlight">{{ formatEventDate(new Date(item.date)) }}</span>
                    </div>
                    <div v-if="item.location && item.location !== ''" class="event-info-row">
                      <EnvironmentOutlined class="event-icon" />
                      <span class="event-text">{{ item.location }}</span>
                    </div>
                    <div v-if="item.type === 'game' && item.opponent" class="event-info-row">
                      <TeamOutlined class="event-icon" />
                      <span class="event-text opponent-text">vs {{ item.opponent }}</span>
                    </div>
                    <div v-if="item.comment" class="event-info-row">
                      <CommentOutlined class="event-icon" />
                      <span class="event-text comment-text">{{ item.comment }}</span>
                    </div>
                    <div v-if="item.roster_name" class="event-info-row">
                      <TeamOutlined class="event-icon" />
                      <span class="event-text roster-text">
                        Состав: {{ item.roster_name }}
                        <span v-if="item.roster_is_default" class="roster-default-badge">(основной)</span>
                      </span>
                    </div>
                  </div>

                  <!-- Event Statistics -->
                  <div class="event-stats">
                    <div class="stat-item participating">
                      <ParticipationIcon :status="2" />
                      <span class="stat-number">{{ item.stats?.participating || 0 }}</span>
                    </div>
                    <div class="stat-item unknown">
                      <ParticipationIcon :status="0" />
                      <span class="stat-number">{{ item.stats?.unknown || 0 }}</span>
                    </div>
                    <div class="stat-item not-participating">
                      <ParticipationIcon :status="1" />
                      <span class="stat-number">{{ item.stats?.not_participating || 0 }}</span>
                    </div>
                  </div>
                </div>


              </Card>
            </template>
          </List>
            </template>
          </div>
          
          <div v-else class="no-team-selected">
            <div class="no-team-message">
              <TeamOutlined class="no-team-icon" />
              <h3>Выберите команду</h3>
              <p>Откройте меню и выберите команду для просмотра событий</p>
            </div>
          </div>
        </div>

        <!-- Pending Requests Screen -->
        <div v-if="currentScreen === 'pendingRequests'" class="screen-content">
          <div class="pending-requests-container">
            <h3>Заявки на вступление в команду</h3>
            <div v-if="pendingRequests.length === 0" class="no-requests">
              <p>Нет заявок на рассмотрение</p>
            </div>
            <div v-else class="requests-list">
              <Card v-for="request in pendingRequests" :key="request.id" class="request-card">
                <div class="request-header">
                  <p class="request-date">{{ formatDate(new Date(request.created_at)) }}</p>
                  <a-avatar :src="request.photo_url" :size="48" />
                                      <div class="request-info">
                      <h4>{{ request.last_name }} {{ request.first_name }}</h4>
                      <a 
                        v-if="request.username" 
                        :href="`https://t.me/${request.username}`" 
                        target="_blank" 
                        class="username-link"
                      >
                        @{{ request.username }}
                      </a>
                    </div>
                </div>
                <div class="request-actions">
                  <a-button danger @click="rejectRequest(request.id)" class="reject-btn">
                    Отклонить
                  </a-button>
                  <a-button type="primary" @click="approveRequest(request.id)" class="approve-btn">
                    Одобрить
                  </a-button>
                 
                </div>
              </Card>
            </div>
          </div>
        </div>

        <!-- Other Screens Content -->
        <div v-else class="screen-content">
          <!-- Create Team Screen -->
          <div v-if="currentScreen === 'createTeam'" class="form-screen">
            <a-form
              :model="newTeam"
              layout="vertical"
              @submit.prevent="createTeam"
              class="team-form"
            >
              <a-form-item
                label="Название команды"
                name="name"
                :rules="[{ required: true, message: 'Введите название команды' }]"
              >
                <a-input v-model:value="newTeam.name" placeholder="Название команды" />
              </a-form-item>

              <a-form-item label="Логотип команды">
                <div class="logo-upload-container">
                  <div v-if="newTeam.logo_url" class="logo-preview">
                    <img :src="newTeam.logo_url" alt="Team Logo" class="logo-preview-image" />
                    <a-button type="text" @click="newTeam.logo_url = ''" class="delete-icon-button">
                      <DeleteOutlined />
                    </a-button>
                  </div>
                  <div v-else class="logo-upload">
                    <input
                      type="file"
                      ref="teamLogoInput"
                      accept="image/*"
                      style="display: none"
                      @change="handleTeamLogoUpload"
                    />
                    <a-button 
                      type="dashed"
                      @click="$refs.teamLogoInput.click()"
                      :loading="uploadingTeamLogo"
                      block
                    >
                      <UploadOutlined />
                      {{ uploadingTeamLogo ? 'Загрузка...' : 'Загрузить логотип' }}
                    </a-button>
                  </div>
                </div>
              </a-form-item>

              <a-form-item label="Город">
                <a-input v-model:value="newTeam.city" placeholder="Город" />
              </a-form-item>

              <a-form-item label="Описание">
                <a-textarea
                  v-model:value="newTeam.description"
                  placeholder="Описание команды"
                  :rows="4"
                />
              </a-form-item>

              <a-form-item label="Социальные сети">
                <a-textarea
                  v-model:value="newTeam.social_networks"
                  placeholder="Ссылки на социальные сети (каждая с новой строки)"
                  :rows="3"
                />
              </a-form-item>

              <a-form-item>
                <div class="drawer-footer">
                  <a-button @click="handleBackNavigation" style="margin-right: 8px">
                    Отмена
                  </a-button>
                  <a-button type="primary" html-type="submit" :loading="creatingTeam" >
                    {{ creatingTeam ? 'Создание...' : 'Создать команду' }}
                  </a-button>
                </div>
              </a-form-item>
            </a-form>
          </div>

          <!-- Edit Team Screen -->
          <div v-if="currentScreen === 'editTeam'" class="form-screen">
            <a-form
              :model="editingTeam"
              layout="vertical"
              @submit.prevent="saveTeamEdit"
              class="team-form"
            >
              <a-form-item
                label="Название команды"
                name="name"
                :rules="[{ required: true, message: 'Введите название команды' }]"
              >
                <a-input v-model:value="editingTeam.name" placeholder="Название команды" />
              </a-form-item>

              <a-form-item label="Логотип команды">
                <div class="logo-upload-container">
                  <div v-if="editingTeam.logo_url" class="logo-preview">
                    <img :src="editingTeam.logo_url" alt="Team Logo" class="logo-preview-image" />
                    <div class="logo-actions">
                      <a-button type="text" @click="uploadNewTeamLogo" class="edit-icon-button">
                        <EditOutlined />
                      </a-button>
                      <a-button type="text" @click="editingTeam.logo_url = ''" class="delete-icon-button">
                        <DeleteOutlined />
                      </a-button>
                    </div>
                  </div>
                  <div v-else class="logo-upload">
                    <input
                      type="file"
                      ref="editTeamLogoInput"
                      accept="image/*"
                      style="display: none"
                      @change="handleEditTeamLogoUpload"
                    />
                    <a-button 
                      type="dashed"
                      @click="$refs.editTeamLogoInput.click()"
                      :loading="uploadingTeamLogo"
                      block
                    >
                      <UploadOutlined />
                      {{ uploadingTeamLogo ? 'Загрузка...' : 'Загрузить логотип' }}
                    </a-button>
                  </div>
                </div>
              </a-form-item>

              <a-form-item label="Город">
                <a-input v-model:value="editingTeam.city" placeholder="Город" />
              </a-form-item>

              <a-form-item label="Описание">
                <a-textarea
                  v-model:value="editingTeam.description"
                  placeholder="Описание команды"
                  :rows="4"
                />
              </a-form-item>

              <a-form-item label="Социальные сети">
                <a-textarea
                  v-model:value="editingTeam.social_networks"
                  placeholder="Ссылки на социальные сети (каждая с новой строки)"
                  :rows="3"
                />
              </a-form-item>

              <a-form-item label="Позиции для участия в событиях">
                <div class="position-participation-settings">
                  <p class="setting-description">
                    Выберите позиции игроков, которые будут участвовать в событиях и отображаться в списках:
                  </p>
                  <a-checkbox-group v-model:value="editingTeam.participating_positions" class="position-checkboxes">
                    <a-checkbox value="forward">{{ $t("positions.forward") }}</a-checkbox>
                    <a-checkbox value="defender">{{ $t("positions.defender") }}</a-checkbox>
                    <a-checkbox value="goalkeeper">{{ $t("positions.goalkeeper") }}</a-checkbox>
                    <a-checkbox value="coach">{{ $t("positions.coach") }}</a-checkbox>
                    <a-checkbox value="service">{{ $t("positions.service") }}</a-checkbox>
                  </a-checkbox-group>
                </div>
              </a-form-item>

              <a-form-item>
                <div class="drawer-footer">
                  <a-button @click="handleBackNavigation" style="margin-right: 8px">
                    Отмена
                  </a-button>
                  <a-button type="primary" @click="saveTeamEdit" :loading="savingTeam" >
                    {{ savingTeam ? 'Сохранение...' : 'Сохранить' }}
                  </a-button>
                </div>
              </a-form-item>
            </a-form>
          </div>
          <!-- Team Info Screen -->
          <div v-if="currentScreen === 'teamInfo'" class="team-info-screen">
            <div class="team-info-header">
              <div class="team-logo-section">
                <img 
                  v-if="currentTeamInfo?.logo_url" 
                  :src="currentTeamInfo.logo_url" 
                  class="team-logo" 
                  alt="Team Logo"
                />
                <div v-else class="team-logo-placeholder">
                  <TeamOutlined />
                </div>
              </div>
              <div class="team-basic-info">
                <h2 class="team-name">{{ currentTeamInfo?.name }}</h2>
                <p v-if="currentTeamInfo?.city" class="team-city">
                  <EnvironmentOutlined /> {{ currentTeamInfo.city }}
                </p>
              </div>
            </div>

            <!-- Team Info Tabs -->
            <div class="team-info-tabs">
              <a-tabs v-model:activeKey="activeTeamTab" @change="onTeamTabChange">
                <a-tab-pane key="info" tab="Информация">
                  <div class="team-info-content">
                    <div v-if="currentTeamInfo?.description" class="info-section">
                      <h3>О команде</h3>
                      <p class="team-description">{{ currentTeamInfo.description }}</p>
                    </div>

                    <div v-if="currentTeamInfo?.social_networks" class="info-section">
                      <h3>Социальные сети</h3>
                      <div class="social-networks">
                        <a 
                          v-for="(link, index) in socialNetworksList" 
                          :key="index"
                          :href="link" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          class="social-link"
                        >
                          {{ link }}
                        </a>
                      </div>
                    </div>

                    <div class="info-section">
                      <h3>Статистика команды</h3>
                      <div class="team-stats">
                        <div class="stat-item">
                          <span class="stat-label">Всего событий:</span>
                          <span class="stat-value">{{ events.length }}</span>
                        </div>
                        <div class="stat-item">
                          <span class="stat-label">Игр:</span>
                          <span class="stat-value">{{ gameEventsCount }}</span>
                        </div>
                        <div class="stat-item">
                          <span class="stat-label">Тренировок:</span>
                          <span class="stat-value">{{ trainingEventsCount }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-if="isAdmin" class="info-section">
                      <h3>Управление ботом команды</h3>
                      <TeamBotManager
                        :team-id="currentTeamId"
                        :team-name="currentTeamInfo?.name"
                        @bot-updated="onBotUpdated"
                      />
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane key="players" tab="Состав">
                  <div class="team-info-content">
                    <div class="info-section">
                      <h3>Состав команды</h3>
                    <div v-if="loadingTeamPlayers" class="loading-players">
                    <a-spin size="large" />
                    <p>Загрузка состава команды...</p>
                    </div>

                  <div v-else-if="teamPlayersData" class="team-players-data">
                    <div class="players-summary">
                      <div class="total-players">
                        <TeamOutlined />
                        <span>Всего человек: {{ teamPlayersData.totalPlayers }}</span>
                      </div>
                    </div>

                    <div v-if="isTeamAdmin" class="web-credentials-section">
                      <Card size="small" class="web-credentials-card">
                        <div class="web-credentials-content">
                          <div class="web-credentials-info">
                            <span class="web-credentials-icon">🔐</span>
                            <div>
                              <div class="web-credentials-title">Вход в приложение вне Telegram</div>
                              <div class="web-credentials-desc">
                                {{ playersWithoutWebLoginCount }} игроков без логина для входа в браузере.
                                После создания откроется окно с логинами и паролями — передайте их игрокам, если сообщение в Telegram не дошло (в т.ч. из‑за блокировок).
                              </div>
                              <div
                                v-if="playersWithWebLoginCount > 0"
                                class="web-credentials-desc web-credentials-resend-hint"
                              >
                                Уже с веб-логином: {{ playersWithWebLoginCount }} — у каждого в карточке есть «Выдать новый пароль», если пароль утерян; или «Отправить напоминание в Telegram» (логин и ссылка, пароль не меняется).
                              </div>
                            </div>
                          </div>
                          <div class="web-credentials-actions">
                          <a-button
                            type="primary"
                            :loading="generatingWebCredentials"
                            :disabled="playersWithoutWebLoginCount === 0"
                            @click="generateWebCredentialsForTeam"
                          >
                            Создать логины
                          </a-button>
                          <a-button
                            :loading="resendingWebCredentials"
                            :disabled="playersWithWebLoginCount === 0"
                            @click="resendWebCredentialsToTelegram"
                          >
                            Отправить напоминание в Telegram
                          </a-button>
                          </div>
                        </div>
                      </Card>
                    </div>

                    <div class="positions-container">
                      <!-- Forwards -->
                      <div v-if="teamPlayersData.players.forward.length > 0" class="position-section">
                        <h3 class="position-title">
                          <span class="position-icon">🎯</span>
                          {{ $t("positions.forwards") }} ({{ teamPlayersData.players.forward.length }})
                        </h3>
                        <div class="players-grid">
                          <div 
                            v-for="player in teamPlayersData.players.forward" 
                            :key="player.id"
                            class="player-card"
                            @click="openPlayerProfile(player)"
                          >
                            <div class="player-avatar">
                              <a-avatar :src="player.photo_url" :size="48" />
                            </div>
                            <div class="player-info">
                              <div class="player-name">
                                <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                              </div>
                              <div>
                                <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                                <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                              </div>
                              <div class="player-number-with-edit">
                                <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                                <a-button
                                  v-if="isAdmin"
                                  class="edit-player-btn"
                                  type="text"
                                  size="small"
                                  @click="openEditPlayerModal(player, $event)"
                                >
                                  <EditOutlined />
                                </a-button>
                              </div>
                              <div class="player-username" v-if="player.username">
                                <a 
                                  :href="`https://t.me/${player.username}`" 
                                  target="_blank" 
                                  class="username-link"
                                >
                                  @{{ player.username }}
                                </a>
                              </div>
                              <TeamPlayerResetPasswordButton
                                v-if="isTeamAdmin"
                                :player="player"
                                :loading="resettingWebPasswordUserId === player.user_id"
                                @reset="resetWebPasswordForPlayer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Defenders -->
                      <div v-if="teamPlayersData.players.defender.length > 0" class="position-section">
                        <h3 class="position-title">
                          <span class="position-icon">🛡️</span>
                          {{ $t("positions.defenders") }} ({{ teamPlayersData.players.defender.length }})
                        </h3>
                        <div class="players-grid">
                          <div 
                            v-for="player in teamPlayersData.players.defender" 
                            :key="player.id"
                            class="player-card"
                            @click="openPlayerProfile(player)"
                          >
                            <div class="player-avatar">
                              <a-avatar :src="player.photo_url" :size="48" />
                            </div>
                            <div class="player-info">
                              <div class="player-name">
                                <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                              </div>
                              <div>
                                <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                                <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                              </div>
                              <div class="player-number-with-edit">
                                <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                                <a-button
                                  v-if="isAdmin"
                                  class="edit-player-btn"
                                  type="text"
                                  size="small"
                                  @click="openEditPlayerModal(player, $event)"
                                >
                                  <EditOutlined />
                                </a-button>
                              </div>
                              <div class="player-username" v-if="player.username">
                                <a 
                                  :href="`https://t.me/${player.username}`" 
                                  target="_blank" 
                                  class="username-link"
                                >
                                  @{{ player.username }}
                                </a>
                              </div>
                              <TeamPlayerResetPasswordButton
                                v-if="isTeamAdmin"
                                :player="player"
                                :loading="resettingWebPasswordUserId === player.user_id"
                                @reset="resetWebPasswordForPlayer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Goalkeepers -->
                      <div v-if="teamPlayersData.players.goalkeeper.length > 0" class="position-section">
                        <h3 class="position-title">
                          <span class="position-icon">🥅</span>
                          {{ $t("positions.goalkeepers") }} ({{ teamPlayersData.players.goalkeeper.length }})
                        </h3>
                        <div class="players-grid">
                          <div 
                            v-for="player in teamPlayersData.players.goalkeeper" 
                            :key="player.id"
                            class="player-card"
                            @click="openPlayerProfile(player)"
                          >
                            <div class="player-avatar">
                              <a-avatar :src="player.photo_url" :size="48" />
                            </div>
                            <div class="player-info">
                              <div class="player-name">
                                <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                              </div>
                              <div>
                                <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                                <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                              </div>
                              <div class="player-number-with-edit">
                                <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                                <a-button
                                  v-if="isAdmin"
                                  class="edit-player-btn"
                                  type="text"
                                  size="small"
                                  @click="openEditPlayerModal(player, $event)"
                                >
                                  <EditOutlined />
                                </a-button>
                              </div>
                              <div class="player-username" v-if="player.username">
                                <a 
                                  :href="`https://t.me/${player.username}`" 
                                  target="_blank" 
                                  class="username-link"
                                >
                                  @{{ player.username }}
                                </a>
                              </div>
                              <TeamPlayerResetPasswordButton
                                v-if="isTeamAdmin"
                                :player="player"
                                :loading="resettingWebPasswordUserId === player.user_id"
                                @reset="resetWebPasswordForPlayer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Coaches -->
                      <div v-if="teamPlayersData.players.coach.length > 0" class="position-section">
                        <h3 class="position-title">
                          <span class="position-icon">📋</span>
                          {{ $t("positions.coaches") }} ({{ teamPlayersData.players.coach.length }})
                        </h3>
                        <div class="players-grid">
                          <div 
                            v-for="player in teamPlayersData.players.coach" 
                            :key="player.id"
                            class="player-card"
                            @click="openPlayerProfile(player)"
                          >
                            <div class="player-avatar">
                              <a-avatar :src="player.photo_url" :size="48" />
                            </div>
                            <div class="player-info">
                              <div class="player-name">
                                <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                              </div>
                              <div>
                                <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                                <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                              </div>
                              <div class="player-number-with-edit">
                                <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                                <a-button
                                  v-if="isAdmin"
                                  class="edit-player-btn"
                                  type="text"
                                  size="small"
                                  @click="openEditPlayerModal(player, $event)"
                                >
                                  <EditOutlined />
                                </a-button>
                              </div>
                              <div class="player-username" v-if="player.username">
                                <a 
                                  :href="`https://t.me/${player.username}`" 
                                  target="_blank" 
                                  class="username-link"
                                >
                                  @{{ player.username }}
                                </a>
                              </div>
                              <TeamPlayerResetPasswordButton
                                v-if="isTeamAdmin"
                                :player="player"
                                :loading="resettingWebPasswordUserId === player.user_id"
                                @reset="resetWebPasswordForPlayer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Service -->
                      <div v-if="teamPlayersData.players.service.length > 0" class="position-section">
                        <h3 class="position-title">
                          <span class="position-icon">🔧</span>
                          {{ $t("positions.services") }} ({{ teamPlayersData.players.service.length }})
                        </h3>
                        <div class="players-grid">
                          <div 
                            v-for="player in teamPlayersData.players.service" 
                            :key="player.id"
                            class="player-card"
                            @click="openPlayerProfile(player)"
                          >
                            <div class="player-avatar">
                              <a-avatar :src="player.photo_url" :size="48" />
                            </div>
                            <div class="player-info">
                              <div class="player-name">
                                <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                              </div>
                              <div>
                                <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                                <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                              </div>
                              <div class="player-number-with-edit">
                                <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                                <a-button
                                  v-if="isAdmin"
                                  class="edit-player-btn"
                                  type="text"
                                  size="small"
                                  @click="openEditPlayerModal(player, $event)"
                                >
                                  <EditOutlined />
                                </a-button>
                              </div>
                              <div class="player-username" v-if="player.username">
                                <a 
                                  :href="`https://t.me/${player.username}`" 
                                  target="_blank" 
                                  class="username-link"
                                >
                                  @{{ player.username }}
                                </a>
                              </div>
                              <TeamPlayerResetPasswordButton
                                v-if="isTeamAdmin"
                                :player="player"
                                :loading="resettingWebPasswordUserId === player.user_id"
                                @reset="resetWebPasswordForPlayer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                        <!-- Empty state -->
                        <div v-if="teamPlayersData.totalPlayers === 0" class="empty-players">
                          <TeamOutlined class="empty-icon" />
                          <h3>В команде пока нет игроков</h3>
                          <p>Игроки появятся здесь после одобрения заявок на вступление</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane v-if="isAdmin" key="management" tab="Управление">
                  <div class="team-info-content">
                    <div class="info-section">
                      <h3>Управление составами</h3>
                      <TeamRosterManagement
                        v-if="currentTeamId"
                        :team-id="currentTeamId"
                        :token="token"
                      />
                    </div>
                    
                    <a-divider />
                  </div>
                </a-tab-pane>

                <a-tab-pane v-if="isAdmin" key="invitations" tab="Приглашения">
                  <div class="team-info-content">
                    <div class="info-section">
                      <h3>Управление приглашениями</h3>
                      <div class="invitations-header">
                        <p>Создавайте пригласительные ссылки для привлечения новых игроков в команду.</p>
                        <a-button type="primary" @click="showCreateInvitationModal">
                          <PlusOutlined /> Создать приглашение
                        </a-button>
                      </div>

                      <div v-if="loadingInvitations" class="loading-invitations">
                        <a-spin size="large" />
                        <p>Загрузка приглашений...</p>
                      </div>

                      <div v-else-if="teamInvitations.length === 0" class="empty-invitations">
                        <LinkOutlined class="empty-icon" />
                        <h3>Приглашения не созданы</h3>
                        <p>Создайте первое приглашение, чтобы привлечь новых игроков в команду</p>
                      </div>

                      <div v-else class="invitations-list">
                        <div 
                          v-for="invitation in teamInvitations" 
                          :key="invitation.id"
                          class="invitation-item"
                        >
                          <div class="invitation-info">
                            <div class="invitation-header">
                              <div class="invitation-code">
                                <span class="code-label">Код:</span>
                                <span class="code-value">{{ invitation.invite_code }}</span>
                              </div>
                              <div class="invitation-status">
                                <a-tag 
                                  v-if="invitation.is_expired" 
                                  color="red"
                                >
                                  Истекло
                                </a-tag>
                                <a-tag 
                                  v-else-if="invitation.is_usage_limit_reached" 
                                  color="orange"
                                >
                                  Лимит исчерпан
                                </a-tag>
                                <a-tag 
                                  v-else 
                                  color="green"
                                >
                                  Активно
                                </a-tag>
                              </div>
                            </div>
                            
                            <div class="invitation-details">
                              <div class="invitation-stat">
                                <span class="stat-label">Использований:</span>
                                <span class="stat-value">{{ invitation.used_count }}{{ invitation.max_uses > 0 ? `/${invitation.max_uses}` : '' }}</span>
                              </div>
                              <div class="invitation-stat">
                                <span class="stat-label">Создано:</span>
                                <span class="stat-value">{{ formatDate(invitation.created_at) }}</span>
                              </div>
                              <div v-if="invitation.expires_at" class="invitation-stat">
                                <span class="stat-label">Истекает:</span>
                                <span class="stat-value">{{ formatDate(invitation.expires_at) }}</span>
                              </div>
                            </div>

                            <div class="invitation-link">
                              <a-input 
                                :value="invitation.invite_link" 
                                readonly 
                                class="link-input"
                              >
                                <template #addonAfter>
                                  <a-button 
                                    type="text" 
                                    @click="copyInviteLink(invitation.invite_link)"
                                    class="copy-btn"
                                  >
                                    <CopyOutlined />
                                  </a-button>
                                </template>
                              </a-input>
                            </div>
                          </div>

                          <div class="invitation-actions">
                            <a-button 
                              type="text" 
                              danger
                              @click="deactivateInvitation(invitation.id)"
                              :disabled="!invitation.is_active"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane v-if="isAdmin" key="notifications" tab="Уведомления">
                  <div class="team-info-content">
                    <div class="info-section">
                      <h3>Настройка уведомлений</h3>
                      <p class="section-description">
                        Управляйте автоматическими уведомлениями, которые получают игроки вашей команды в Telegram
                      </p>

                      <!-- Event Notifications Card -->
                      <div class="notification-card">
                        <div class="notification-card-header">
                          <div class="notification-icon event-icon">
                            <CalendarOutlined />
                          </div>
                          <div class="notification-card-title">
                            <h4>Уведомления о событиях</h4>
                            <p>Напоминания о предстоящих играх и тренировках</p>
                          </div>
                          <a-switch 
                            v-model:checked="currentTeamInfo.event_notification_enabled"
                            @change="updateNotificationSettings"
                            :loading="savingNotifications"
                          />
                        </div>

                        <div v-if="currentTeamInfo.event_notification_enabled" class="notification-card-body">
                          <div class="notification-details">
                            <div class="detail-item">
                              <span class="detail-label">📅 Когда отправляются:</span>
                              <span class="detail-value">За день до события</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">⏰ Время отправки:</span>
                              <div class="time-picker-wrapper">
                                <a-time-picker 
                                  v-model:value="currentTeamInfo.event_notification_time_moment"
                                  format="HH:mm"
                                  placeholder="Выберите время"
                                  :minute-step="60"
                                  @change="updateNotificationSettings"
                                  :disabled="savingNotifications"
                                  style="width: 120px"
                                />
                              </div>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">📝 Содержание:</span>
                              <span class="detail-value">Тип события, дата, время, место, оппонент (для игр)</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">👥 Кому отправляются:</span>
                              <span class="detail-value">Игрокам со статусом "неизвестно" (не подтвердили участие)</span>
                            </div>
                          </div>

                          <div class="notification-example">
                            <div class="example-header">
                              <InfoCircleOutlined />
                              <span>Пример уведомления:</span>
                            </div>
                            <div class="example-content">
                              Привет, Иван! 👋<br/><br/>
                              Напоминаем, что завтра у Вас игра в 19:00.<br/><br/>
                              Пожалуйста, отметьте свой статус участия:
                            </div>
                          </div>
                        </div>

                        <div v-else class="notification-disabled-message">
                          <InfoCircleOutlined />
                          <span>Уведомления о событиях выключены. Игроки не будут получать автоматические напоминания.</span>
                        </div>
                      </div>

                      <!-- Payment Notifications Card -->
                      <div class="notification-card">
                        <div class="notification-card-header">
                          <div class="notification-icon payment-icon">
                            <DollarOutlined />
                          </div>
                          <div class="notification-card-title">
                            <h4>Уведомления об оплатах</h4>
                            <p>Напоминания о сроках оплаты и абонементах</p>
                          </div>
                          <a-switch 
                            v-model:checked="currentTeamInfo.payment_notification_enabled"
                            @change="updateNotificationSettings"
                            :loading="savingNotifications"
                          />
                        </div>

                        <div v-if="currentTeamInfo.payment_notification_enabled" class="notification-card-body">
                          <div class="notification-details">
                            <div class="detail-item">
                              <span class="detail-label">📅 Когда отправляются:</span>
                              <span class="detail-value">За день до срока оплаты и в день срока</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">⏰ Время отправки:</span>
                              <div class="time-picker-wrapper">
                                <a-time-picker 
                                  v-model:value="currentTeamInfo.payment_notification_time_moment"
                                  format="HH:mm"
                                  placeholder="Выберите время"
                                  :minute-step="60"
                                  @change="updateNotificationSettings"
                                  :disabled="savingNotifications"
                                  style="width: 120px"
                                />
                              </div>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">📝 Содержание:</span>
                              <span class="detail-value">Название платежа, сумма, срок оплаты, инструкции</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">👥 Кому отправляются:</span>
                              <span class="detail-value">Игрокам с активными типами оплаты, у которых нет текущего платежа</span>
                            </div>
                          </div>

                          <div class="notification-example">
                            <div class="example-header">
                              <InfoCircleOutlined />
                              <span>Пример уведомления:</span>
                            </div>
                            <div class="example-content">
                              📢 НАПОМИНАНИЕ ОБ ОПЛАТЕ<br/><br/>
                              Завтра истекает срок оплаты "Абонемент" в команде "Спартак"!<br/><br/>
                              💰 Сумма: 1000 руб.<br/>
                              📅 Срок: 15 число каждого месяца
                            </div>
                          </div>
                        </div>

                        <div v-else class="notification-disabled-message">
                          <InfoCircleOutlined />
                          <span>Уведомления об оплатах выключены. Игроки не будут получать напоминания о сроках оплаты.</span>
                        </div>
                      </div>

                      <!-- Attendance Statistics Card -->
                      <div class="notification-card">
                        <div class="notification-card-header">
                          <div class="notification-icon statistics-icon">
                            <BarChartOutlined />
                          </div>
                          <div class="notification-card-title">
                            <h4>Статистика посещаемости</h4>
                            <p>Автоматическая отправка статистики посещаемости тренировок и игр в Telegram группу</p>
                          </div>
                        </div>

                        <div class="notification-card-body">
                          <div class="notification-details">
                            <div class="detail-item">
                              <span class="detail-label">📊 Когда отправляется:</span>
                              <span class="detail-value">Автоматически 1-го числа каждого месяца в 10:00</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">📅 Период статистики:</span>
                              <span class="detail-value">За предыдущий месяц</span>
                            </div>
                            <div class="detail-item">
                              <span class="detail-label">📝 Содержание:</span>
                              <span class="detail-value">Список игроков с процентом посещаемости и количеством посещенных тренировок/игр</span>
                            </div>
                          </div>

                          <div class="attendance-statistics-settings">
                            <h4 style="margin-top: 20px; margin-bottom: 12px;">Настройки отправки</h4>
                            
                            <div class="detail-item" style="margin-bottom: 16px;">
                              <span class="detail-label">💬 ID группы Telegram:</span>
                              <a-input
                                v-model:value="attendanceStatisticsSettings.group_id"
                                placeholder="Например: -1001234567890"
                                @blur="saveAttendanceStatisticsSettings"
                                :loading="savingAttendanceStatistics"
                                style="max-width: 300px;"
                              />
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
                                ID группы, куда будут отправляться статистики. Можно получить через @userinfobot
                              </div>
                            </div>

                            <div class="detail-item" style="margin-bottom: 16px;">
                              <span class="detail-label">📌 ID топика (для форумов):</span>
                              <a-input-number
                                v-model:value="attendanceStatisticsSettings.topic_id"
                                placeholder="Оставьте пустым, если группа не форум"
                                @blur="saveAttendanceStatisticsSettings"
                                :loading="savingAttendanceStatistics"
                                style="max-width: 300px;"
                                :min="1"
                              />
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
                                ID топика в форуме (если группа является форумом). Оставьте пустым для обычных групп.
                              </div>
                            </div>

                            <div class="detail-item" style="margin-bottom: 16px;">
                              <span class="detail-label">📋 Учитывать события:</span>
                              <a-checkbox-group
                                v-model:value="attendanceStatisticsSettings.event_types"
                                @change="saveAttendanceStatisticsSettings"
                                :disabled="savingAttendanceStatistics"
                              >
                                <a-checkbox value="training">Тренировки</a-checkbox>
                                <a-checkbox value="game">Игры</a-checkbox>
                              </a-checkbox-group>
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
                                Выберите типы событий, которые будут учитываться в статистике посещаемости
                              </div>
                            </div>

                            <div class="detail-item" style="margin-bottom: 16px;">
                              <span class="detail-label">🤖 Отправлять через:</span>
                              <a-radio-group
                                v-model:value="attendanceStatisticsSettings.bot_type"
                                @change="saveAttendanceStatisticsSettings"
                                :disabled="savingAttendanceStatistics"
                                style="display: flex; flex-direction: column; gap: 8px; margin-top: 8px;"
                              >
                                <a-radio value="both">
                                  <span v-if="hasCustomBotForStats">Оба бота (кастомный и по умолчанию)</span>
                                  <span v-else>Оба бота</span>
                                </a-radio>
                                <a-radio value="custom" v-if="hasCustomBotForStats">Только кастомный бот</a-radio>
                                <a-radio value="default">Только бот по умолчанию</a-radio>
                              </a-radio-group>
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
                                <span v-if="hasCustomBotForStats">Выберите, от имени какого бота отправлять статистику посещаемости</span>
                                <span v-else>Выберите, от имени какого бота отправлять статистику посещаемости (кастомный бот не подключен)</span>
                              </div>
                            </div>

                            <div class="detail-item" style="margin-bottom: 16px;">
                              <span class="detail-label">🟢 Зелёный уровень от %:</span>
                              <a-input-number
                                v-model:value="attendanceStatisticsSettings.green_from"
                                :min="0"
                                :max="100"
                                placeholder="67"
                                @blur="saveAttendanceStatisticsSettings"
                                style="max-width: 100px; margin-right: 16px;"
                              />
                              <span class="detail-label" style="margin-left: 0;">🟡 Жёлтый уровень от %:</span>
                              <a-input-number
                                v-model:value="attendanceStatisticsSettings.yellow_from"
                                :min="0"
                                :max="100"
                                placeholder="34"
                                @blur="saveAttendanceStatisticsSettings"
                                style="max-width: 100px;"
                              />
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 4px;">
                                Порог в процентах: от этого значения и выше — зелёный/жёлтый уровень. Красный — ниже жёлтого. Рекомендуется: зелёный ≥ жёлтый.
                              </div>
                            </div>

                            <div class="detail-item" style="margin-top: 20px;">
                              <a-button
                                type="primary"
                                @click="sendAttendanceStatisticsManually"
                                :loading="sendingAttendanceStatistics"
                                :disabled="!attendanceStatisticsSettings.group_id"
                              >
                                <template #icon>
                                  <SendOutlined />
                                </template>
                                Отправить статистику за период
                              </a-button>
                              <div style="font-size: 12px; color: #8c8c8c; margin-top: 8px;">
                                Отправить статистику за произвольный период вручную
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Best Player Voting Card -->
                      <div class="notification-card">
                        <div class="notification-card-header">
                          <div class="notification-icon statistics-icon">
                            <TrophyOutlined />
                          </div>
                          <div class="notification-card-title">
                            <h4>Голосование за лучшего игрока</h4>
                            <p>Модуль позволяет создавать голосования за лучшего игрока после игр или тренировок. Можно исключать участников из списка кандидатов и назначить администратора, который видит результаты.</p>
                          </div>
                        </div>
                        <div class="notification-card-body">
                          <div class="detail-item" style="margin-bottom: 16px;">
                            <a-switch
                              v-model:checked="bestPlayerVotingEnabled"
                              :loading="bestPlayerVotingSaving"
                              @change="saveBestPlayerVotingSettings"
                            />
                            <span style="margin-left: 12px;">Включить модуль голосования</span>
                          </div>
                          <div v-if="bestPlayerVotingEnabled" class="detail-item">
                            <a-button type="primary" @click="showBestPlayerVoting">
                              Перейти к голосованиям
                            </a-button>
                          </div>
                        </div>
                      </div>

                      <!-- Important Notes -->
                      <div class="notification-notes">
                        <a-alert 
                          type="info" 
                          show-icon
                        >
                          <template #message>
                            <strong>Важная информация</strong>
                          </template>
                          <template #description>
                            <ul>
                              <li>Уведомления отправляются <strong>в начале каждого часа</strong> (XX:00). Используйте только полные часы.</li>
                              <li>Изменения применяются <strong>немедленно</strong> и будут использованы при следующей проверке.</li>
                              <li>Все уведомления отправляются по времени <strong>Europe/Moscow (GMT+3)</strong>.</li>
                              <li>Каждая команда имеет <strong>свои независимые</strong> настройки уведомлений.</li>
                            </ul>
                          </template>
                        </a-alert>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>
          <!-- Best Player Voting Screen -->
          <div v-if="currentScreen === 'bestPlayerVoting'" class="screen-content">
            <BestPlayerVoting
              :team-id="currentTeamId"
              :is-admin="isAdmin"
              :team-players="teamPlayersData"
              :team-events="events"
            />
          </div>
          <!-- Profile Screen -->
          <div v-if="currentScreen === 'profile'">
            <a-tabs v-model:activeKey="activeUserProfileTab" class="profile-tabs">
              <a-tab-pane key="info" tab="Основная информация">
            <div class="user-info">      
              <!-- Basic Information -->
              <div class="profile-section">
                <div class="profile-settings card">
                <a-form layout="vertical">
                  <div class="avatar-container">
                <a-avatar 
                  :src="userData.photo_url" 
                  :size="96"
                  class="profile-avatar"
                  @click="showFullAvatar = true"
                />
                <div class="avatar-upload">
                  <input
                    type="file"
                    ref="fileInput"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileUpload"
                  />
                  <Button 
                    type="primary"
                    @click="$refs.fileInput.click()"
                    :loading="uploadingPhoto"
                  >
                    {{ uploadingPhoto ? 'Загрузка...' : 'Изменить фото' }}
                  </Button>
                </div>
              </div>
                  <a-form-item label="Имя">
                    <a-input v-model:value="userData.first_name" placeholder="Имя" />
                  </a-form-item>
                  <a-form-item label="Фамилия">
                    <a-input v-model:value="userData.last_name" placeholder="Фамилия" />
                  </a-form-item>
                </a-form>
                </div>
              </div>

              <!-- Personal Details -->
              <div class="profile-section">
                <div class="profile-settings card">
                <h3>Личные данные</h3>
                <a-form layout="vertical">
                  <a-form-item label="Дата рождения">
                    <a-date-picker 
                      v-model:value="safeDateOfBirth" 
                      format="YYYY-MM-DD"
                      style="width: 100%"
                      placeholder="Выберите дату рождения"
                    />
                  </a-form-item>
                  <a-form-item label="Возраст" v-if="userData.age">
                    <a-input :value="userData.age + ' лет'" disabled />
                  </a-form-item>
                  <a-form-item label="Рост (см)">
                    <a-input-number 
                      v-model:value="userData.height" 
                      :min="100" 
                      :max="250"
                      style="width: 100%"
                      placeholder="Рост в сантиметрах"
                    />
                  </a-form-item>
                  <a-form-item label="Вес (кг)">
                    <a-input-number 
                      v-model:value="userData.weight" 
                      :min="30" 
                      :max="200"
                      style="width: 100%"
                      placeholder="Вес в килограммах"
                    />
                  </a-form-item>
                </a-form>
                </div>
              </div>

              <!-- Hockey Information -->
              <div class="profile-section">
                <div class="profile-settings card">
                <h3>Хоккейная информация</h3>
                <p class="section-description" style="color: #666; font-size: 14px; margin-bottom: 16px;">
                  Данные актуальны для команды
                  <span v-if="currentTeamName">«{{ currentTeamName }}»</span>
                  <span v-else>—</span>
                </p>
                <a-form layout="vertical">
                  <a-form-item label="Игровой номер">
                    <a-input-number 
                      v-model:value="userData.number" 
                      :min="1" 
                      :max="99"
                      style="width: 100%"
                      placeholder="Номер от 1 до 99"
                    />
                  </a-form-item>
                  <a-form-item label="Хват клюшки">
                    <a-select v-model:value="userData.stick_hand" placeholder="Выберите хват клюшки">
                      <a-select-option value="left">Левый</a-select-option>
                      <a-select-option value="right">Правый</a-select-option>
                    </a-select>
                  </a-form-item>
                  <a-form-item label="Позиция">
                    <a-select v-model:value="userData.position" placeholder="Выберите позицию">
                      <a-select-option value="forward">{{ $t("positions.forward") }}</a-select-option>
                      <a-select-option value="defender">{{ $t("positions.defender") }}</a-select-option>
                      <a-select-option value="goalkeeper">{{ $t("positions.goalkeeper") }}</a-select-option>
                      <a-select-option value="coach">{{ $t("positions.coach") }}</a-select-option>
                      <a-select-option value="service">{{ $t("positions.service") }}</a-select-option>
                    </a-select>
                  </a-form-item>
                  <a-form-item label="Уровень игры">
                    <a-select v-model:value="userData.skill_level" placeholder="Выберите уровень">
                      <a-select-option value="beginner">{{ $t("skillLevels.beginner") }}</a-select-option>
                      <a-select-option value="intermediate">{{ $t("skillLevels.intermediate") }}</a-select-option>
                      <a-select-option value="experienced">{{ $t("skillLevels.experienced") }}</a-select-option>
                    </a-select>
                  </a-form-item>
                </a-form>
                </div>
              </div>

              <!-- Contact Information -->
              <div class="profile-section">
                <div class="profile-settings card">
                <h3>Контактная информация</h3>
                <a-form layout="vertical">
                  <a-form-item label="Экстренный телефон">
                    <a-input 
                      v-model:value="userData.emergency_phone" 
                      placeholder="+375291234567"
                      :maxlength="20"
                    />
                  </a-form-item>
                </a-form>
                </div>
              </div>

              <!-- Web Login Credentials -->
              <div class="profile-section">
                <div class="profile-settings card">
                <h3>Вход в приложение вне Telegram</h3>
                <p class="section-description" style="color: #666; font-size: 14px; margin-bottom: 16px;">
                  Установите логин и пароль, чтобы открывать приложение в обычном браузере без Telegram.
                </p>
                <a-form layout="vertical" v-if="!userData.login || webCredentialsForm.login">
                  <a-form-item label="Логин" :help="userData.login ? 'Текущий логин: ' + userData.login : null">
                    <a-input
                      v-model:value="webCredentialsForm.login"
                      :placeholder="userData.login || 'Минимум 3 символа'"
                      :maxlength="50"
                    />
                  </a-form-item>
                  <a-form-item label="Пароль" :help="userData.login ? 'Оставьте пустым, чтобы не менять' : 'Минимум 6 символов'">
                    <a-input-password
                      v-model:value="webCredentialsForm.password"
                      :placeholder="userData.login ? 'Новый пароль' : 'Введите пароль'"
                    />
                  </a-form-item>
                  <a-button
                    type="primary"
                    @click="saveWebCredentials"
                    :loading="savingWebCredentials"
                    :disabled="!webCredentialsForm.login || !webCredentialsForm.password"
                  >
                    {{ userData.login ? 'Изменить' : 'Сохранить логин и пароль' }}
                  </a-button>
                </a-form>
                <div v-else>
                  <p style="color: var(--color-text-secondary); margin-bottom: 12px;">Логин установлен. Вы можете войти в приложение через браузер.</p>
                  <a-button type="link" @click="webCredentialsForm = { login: userData.login || '', password: '' }">Изменить логин или пароль</a-button>
                </div>
                </div>
              </div>

              <a-button type="primary" @click="updateProfile" :loading="updatingProfile" block>
                {{ updatingProfile ? 'Сохранение...' : 'Сохранить профиль' }}
              </a-button>
            </div>
              </a-tab-pane>


            </a-tabs>

            <!-- Full Screen Avatar Modal -->
            <div 
              v-if="showFullAvatar" 
              class="full-avatar-overlay"
              @click="showFullAvatar = false"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <img 
                :src="userData.photo_url" 
                class="full-avatar"
                @click.stop
              />
            </div>

            <div v-if="!isProduction" class="app-settings">
              <h3>Настройки приложения</h3>
              <div class="setting-item">
                <span>Язык:</span>
                <select v-model="$i18n.locale">
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                </select>
              </div>
              <ThemeSwitcher />
            </div>
            
            <div v-if="!isProduction && isAdmin" class="admin-settings">
              <h3>Панель администратора</h3>
              <a-button @click="testReminders" type="dashed">
                Тест напоминаний
              </a-button>
            </div>
          </div>

          <!-- Participation Settings Screen -->
          <div v-if="currentScreen === 'participationSettings'" class="participation-settings-screen">
            <div class="participation-settings-content">
              <div class="profile-settings card">  
                <a-form layout="vertical">
                  <a-form-item label="Статус по умолчанию">
                    <a-select v-model:value="participationSettings.default_participation">
                      <a-select-option :value="0">Без статуса</a-select-option>
                      <a-select-option :value="1">Не участвую</a-select-option>
                      <a-select-option :value="2">Участвую</a-select-option>
                    </a-select>
                  </a-form-item>
                  
                  <a-form-item label="С даты">
                    <a-date-picker
                      v-model:value="participationSettings.participation_from"
                      :disabledDate="disableFutureDates"
                      style="width: 100%"
                      placeholder="Выберите дату (по умолчанию сегодня)"
                    />
                  </a-form-item>
                  
                  <a-form-item label="До даты">
                    <a-date-picker
                      v-model:value="participationSettings.participation_until"
                      :disabledDate="disableUntilDates"
                      style="width: 100%"
                      placeholder="Выберите дату"
                    />
                  </a-form-item>
                  
                  <a-form-item label="Комментарий по умолчанию">
                    <a-textarea
                      v-model:value="participationSettings.default_comment"
                      :rows="1"
                    />
                  </a-form-item>
                  
                  <a-button 
                    type="primary" 
                    @click="saveParticipationSettings" 
                    :loading="savingParticipationSettings"
                    block
                  >
                    {{ savingParticipationSettings ? 'Сохранение...' : 'Сохранить настройки' }}
                  </a-button>
                </a-form>
              </div>

              <div class="settings-info card">
                <h3>Как это работает</h3>
                <div class="info-item">
                  <div class="info-icon">🎯</div>
                  <div class="info-content">
                    <h4>Статус по умолчанию</h4>
                    <p>Автоматически устанавливает ваш статус участия для новых событий</p>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-icon">📅</div>
                  <div class="info-content">
                    <h4>Период действия</h4>
                    <p>Настройки применяются к событиям в указанном периоде. "С даты" не может быть в прошлом, "До даты" не может быть ранее "С даты"</p>
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-icon">💬</div>
                  <div class="info-content">
                    <h4>Комментарий</h4>
                    <p>Автоматически добавляется к вашим участиям в событиях</p>
                  </div>
                </div>
              </div>
            </div>
          </div>




          <!-- Payments Screen -->
          <div v-if="currentScreen === 'payments'" class="payments-screen">
            <div v-if="loadingSubscriptionStatus" class="loading-container">
              <a-spin size="large" />
              <p>Загрузка платежей...</p>
            </div>

            <div v-else-if="subscriptionStatus.length === 0" class="no-payments">
              <div class="no-payments-message">
                <DollarOutlined class="no-payments-icon" />
                <h3>Нет активных команд</h3>
                <p>У вас пока нет команд для просмотра платежей</p>
              </div>
            </div>

            <div v-else >
              <a-tabs v-model:activeKey="activePaymentsTab" class="payments-tabs">
                <a-tab-pane key="registration" tab="Регистрация оплаты">
                  <UserPaymentRegistration 
                    :team-id="currentTeamId"
                    @payment-registered="onPlayerPaymentRegistered"
                  />
                </a-tab-pane>
                <a-tab-pane key="my-payments" tab="История">
                  <UserPaymentHistory 
                    :team-id="currentTeamId"
                    @payment-registered="onPlayerPaymentRegistered"
                  />
                </a-tab-pane>
                <a-tab-pane v-if="canManagePayments" key="admin-payments" tab="Оплаты">
                  <AdminPaymentManagement 
                    :team-id="currentTeamId"
                    @payment-registered="onPlayerPaymentRegistered"
                  />
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>

          <!-- Payment Types Management Screen -->
          <div v-if="currentScreen === 'paymentTypesManagement'" class="payment-types-management-screen">
            <PaymentTypesManagement 
              ref="paymentTypesManagement"
              :team-id="currentTeamId"
              @payment-type-updated="onPaymentTypeUpdated"
            />
          </div>

          <!-- Admin Payment Management Screen -->
          <div v-if="currentScreen === 'adminPaymentManagement'" class="admin-payment-management-screen">
            <div class="screen-header">
              <a-button @click="currentScreen = 'payments'" class="back-button">
                <ArrowLeftOutlined />
                Назад к платежам
              </a-button>
              <h2>Управление оплатами</h2>
            </div>
            <AdminPaymentManagement 
              :team-id="currentTeamId"
              @payment-registered="onPlayerPaymentRegistered"
            />
          </div>
          <div v-if="currentScreen === 'playerProfile'" class="player-profile-screen">
            <div class="profile-header">
              <a-avatar :src="selectedPlayerProfile?.photo_url" :size="80" class="profile-avatar" @click="showFullAvatar = true" />
              <div class="profile-header-info">
                <h2 class="player-name">{{ selectedPlayerProfile?.last_name }} {{ selectedPlayerProfile?.first_name }}</h2>
                <div class="player-meta">
                  <span class="player-number">#{{ selectedPlayerProfile?.number }}</span>
                  <span class="player-position">{{ $t(`positions.${selectedPlayerProfile?.position}`) }}</span>
                </div>
              </div>
            </div>

            <a-tabs v-model:activeKey="activePlayerProfileTab" class="profile-tabs">
              <a-tab-pane key="info" tab="Общая информация">
                <div class="profile-details card">
                  <!-- Basic Information -->
                  <div class="profile-section">
                    <h3>Основная информация</h3>
                    <div class="detail-item" v-if="isAdmin">
                      <span class="label">Роль:</span>
                      <div class="value-container">
                        <template v-if="!isEditingRole">
                          <span class="value">
                            {{ selectedPlayerProfile?.role === 'admin' ? 'Администратор' : 'Игрок' }}
                          </span>
                          <a-button 
                            type="text" 
                            @click="startEditRole"
                            class="edit-icon-button"
                          >
                            <EditOutlined />
                          </a-button>
                        </template>
                        <div v-else class="edit-role-container">
                          <a-select 
                            v-model:value="editingPlayerRole" 
                            placeholder="Выберите роль"
                            class="edit-role-input"
                          >
                            <a-select-option value="player">Игрок</a-select-option>
                            <a-select-option value="admin">Администратор</a-select-option>
                          </a-select>
                          <div class="edit-role-actions">
                            <a-button @click="cancelEditRole">
                              Отмена
                            </a-button>
                            <a-button type="primary" @click="savePlayerRole" :loading="savingRole">
                              Сохранить
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="detail-item" v-if="isAdmin">
                      <span class="label">Статус в команде:</span>
                      <div class="value-container">
                        <template v-if="!isEditingCaptainStatus">
                          <span class="value">
                            {{ getCaptainStatusText(selectedPlayerProfile?.captain_status) }}
                          </span>
                          <a-button 
                            type="text" 
                            @click="startEditCaptainStatus"
                            class="edit-icon-button"
                          >
                            <EditOutlined />
                          </a-button>
                        </template>
                        <div v-else class="edit-captain-status-container">
                          <a-select 
                            v-model:value="editingPlayerCaptainStatus" 
                            placeholder="Выберите статус"
                            class="edit-captain-status-input"
                          >
                            <a-select-option :value="null">Обычный игрок</a-select-option>
                            <a-select-option value="captain">Капитан</a-select-option>
                            <a-select-option value="assistant">Ассистент</a-select-option>
                          </a-select>
                          <div class="edit-captain-status-actions">
                            <a-button @click="cancelEditCaptainStatus">
                              Отмена
                            </a-button>
                            <a-button type="primary" @click="savePlayerCaptainStatus" :loading="savingCaptainStatus">
                              Сохранить
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="detail-item">
                      <span class="label">Номер:</span>
                      <div class="value-container">
                        <template v-if="!isEditingNumber">
                          <span class="value">
                            {{ selectedPlayerProfile?.number ? `#${selectedPlayerProfile.number}` : 'Не установлен' }}
                          </span>
                          <a-button 
                            v-if="isAdmin" 
                            type="text" 
                            @click="startEditNumber"
                            class="edit-icon-button"
                          >
                            <EditOutlined />
                          </a-button>
                        </template>
                        <div v-else class="edit-number-container">
                          <a-input-number 
                            v-model:value="editingPlayerNumber" 
                            :min="1" 
                            :max="99"
                            placeholder="1-99"
                            class="edit-number-input"
                          />
                          <div class="edit-number-actions">
                            <a-button @click="cancelEditNumber">
                              Отмена
                            </a-button>
                            <a-button type="primary" @click="savePlayerNumber" :loading="savingNumber">
                              Сохранить
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="detail-item">
                      <span class="label">Позиция:</span>
                      <div class="value-container">
                        <template v-if="!isEditingPosition">
                          <span class="value">
                            {{ selectedPlayerProfile?.position ? getPositionLabel(selectedPlayerProfile.position) : 'Не установлена' }}
                          </span>
                          <a-button 
                            v-if="isAdmin" 
                            type="text" 
                            @click="startEditPosition"
                            class="edit-icon-button"
                          >
                            <EditOutlined />
                          </a-button>
                        </template>
                        <div v-else class="edit-position-container">
                          <a-select 
                            v-model:value="editingPlayerPosition" 
                            placeholder="Выберите позицию"
                            class="edit-position-input"
                          >
                            <a-select-option value="forward">{{ $t("positions.forward") }}</a-select-option>
                            <a-select-option value="defender">{{ $t("positions.defender") }}</a-select-option>
                            <a-select-option value="goalkeeper">{{ $t("positions.goalkeeper") }}</a-select-option>
                            <a-select-option value="coach">{{ $t("positions.coach") }}</a-select-option>
                            <a-select-option value="service">{{ $t("positions.service") }}</a-select-option>
                          </a-select>
                          <div class="edit-position-actions">
                            <a-button @click="cancelEditPosition">
                              Отмена
                            </a-button>
                            <a-button type="primary" @click="savePlayerPosition" :loading="savingPosition">
                              Сохранить
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Personal Details -->
                  <div class="profile-section">
                    <h3>Личные данные</h3>
                    <div class="detail-row">
                      <div class="detail-cell" v-if="selectedPlayerProfile?.date_of_birth">
                        <span class="label">Дата рождения</span>
                        <span class="value">{{ formatDateOnly(selectedPlayerProfile.date_of_birth) }}</span>                      </div>
                      <div class="detail-cell" v-if="selectedPlayerProfile?.date_of_birth">
                        <span class="label">Возраст</span>
                        <span class="value">{{ calculateAge(selectedPlayerProfile.date_of_birth) }} лет</span>
                      </div>
                    </div>
                    <div class="detail-row">
                      <div class="detail-cell" v-if="selectedPlayerProfile?.height">
                        <span class="label">Рост</span>
                        <span class="value">{{ selectedPlayerProfile.height }} см</span>
                      </div>
                      <div class="detail-cell" v-if="selectedPlayerProfile?.weight">
                        <span class="label">Вес</span>
                        <span class="value">{{ selectedPlayerProfile.weight }} кг</span>
                      </div>
                    </div>
                    <div class="detail-item" v-if="!selectedPlayerProfile?.date_of_birth && !selectedPlayerProfile?.height && !selectedPlayerProfile?.weight">
                      <span class="value" style="color: #999; font-style: italic;">Личные данные не заполнены</span>
                    </div>
                  </div>
                  <!-- Hockey Information -->
                  <div class="profile-section">
                    <h3>Хоккейная информация</h3>
                    <div class="detail-item" v-if="selectedPlayerProfile?.stick_hand">
                      <span class="label">Хват клюшки:</span>
                      <span class="value">{{ selectedPlayerProfile.stick_hand === 'left' ? 'Левый' : 'Правый' }}</span>
                    </div>
                    <div class="detail-item" v-if="selectedPlayerProfile?.skill_level">
                      <span class="label">Уровень игры:</span>
                      <span class="value">{{ getSkillLevelLabel(selectedPlayerProfile.skill_level) }}</span>
                    </div>
                    <div class="detail-item" v-if="!selectedPlayerProfile?.stick_hand && !selectedPlayerProfile?.skill_level">
                      <span class="value" style="color: #999; font-style: italic;">Хоккейная информация не заполнена</span>
                    </div>
                  </div>

                  <!-- Contact Information -->
                  <div class="profile-section">
                    <h3>Контактная информация</h3>
                    <div class="detail-item" v-if="selectedPlayerProfile?.emergency_phone">
                      <span class="label">Экстренный телефон:</span>
                      <span class="value">{{ selectedPlayerProfile.emergency_phone }}</span>
                    </div>
                    <div class="detail-item" v-if="!selectedPlayerProfile?.emergency_phone">
                      <span class="value" style="color: #999; font-style: italic;">Контактная информация не заполнена</span>
                    </div>
                  </div>
                </div>
              </a-tab-pane>

              <a-tab-pane key="stats" tab="Статистика">
                <div class="statistics-container">
                  <!-- Monthly Statistics Section -->
                  <div class="stats-section">
                    <h3>Статистика по месяцам</h3>
                    <div class="month-selector">
                      <a-select 
                        v-model:value="selectedStatsYear" 
                        style="width: 100px; margin-right: 8px;"
                        @change="loadMonthlyStats"
                      >
                        <a-select-option v-for="year in availableYears" :key="year" :value="year">
                          {{ year }}
                        </a-select-option>
                      </a-select>
                      <a-select 
                        v-model:value="selectedStatsMonth" 
                        style="width: 120px; margin-right: 8px;"
                        @change="loadMonthlyStats"
                      >
                        <a-select-option v-for="month in 12" :key="month" :value="month">
                          {{ getMonthName(month) }}
                        </a-select-option>
                      </a-select>
                    </div>
                    
                    <div v-if="monthlyStats" class="monthly-stats">
                      <div class="stats-summary">
                        <div class="stat-card">
                          <div class="stat-number">{{ monthlyStats.totalEvents }}</div>
                          <div class="stat-label">Всего событий</div>
                        </div>
                        <div class="stat-card">
                          <div class="stat-number">{{ monthlyStats.participatingEvents }}</div>
                          <div class="stat-label">Участвовал</div>
                        </div>
                        <div class="stat-card">
                          <div class="stat-number">{{ monthlyStats.participationRate }}%</div>
                          <div class="stat-label">Процент участия</div>
                        </div>
                      </div>
                      
                      <div v-if="monthlyStats.events.length > 0" class="events-list">
                        <h4>События за {{ getMonthName(selectedStatsMonth) }} {{ selectedStatsYear }}</h4>
                        <div class="event-item" v-for="event in monthlyStats.events" :key="event.id">
                          <div class="event-info">
                            <span class="event-type">{{ event.type === 'game' ? 'Игра' : 'Тренировка' }}</span>
                            <span class="event-date">{{ formatDate(new Date(event.date)) }}</span>
                          </div>
                          <div class="event-status">
                            <ParticipationIcon :status="event.is_participating" />
                            <span class="status-text">{{ getParticipationStatusText(event.is_participating) }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div v-else class="no-events">
                        <p>В выбранном месяце нет завершенных событий</p>
                      </div>
                    </div>
                  </div>

                  <!-- Team Ranking Section -->
                  <div class="stats-section">
                    <h3>Рейтинг в команде</h3>
                    <div v-if="teamRanking" class="ranking-info">
                      <div class="current-player-rank">
                        <div class="rank-badge">
                          <span class="rank-number">{{ teamRanking.currentPlayer.rank }}</span>
                          <span class="rank-label">место</span>
                        </div>
                        <div class="rank-details">
                          <div class="player-name">{{ teamRanking.currentPlayer.firstName }} {{ teamRanking.currentPlayer.lastName }}</div>
                          <div class="rank-stats">
                            {{ teamRanking.currentPlayer.participatingEvents }} из {{ teamRanking.currentPlayer.totalEvents }} событий
                            ({{ teamRanking.currentPlayer.participationRate }}%)
                          </div>
                        </div>
                      </div>
                      
                      <div class="team-ranking-list">
                        <h4>Рейтинг команды ({{ teamRanking.totalPlayers }} игроков)</h4>
                        <div class="ranking-item" v-for="player in teamRanking.players" :key="player.rank">
                          <div class="ranking-position">
                            <span class="position-number">{{ player.rank }}</span>
                          </div>
                          <div class="player-info">
                            <div class="player-name">
                              {{ player.firstName }} {{ player.lastName }}
                              <span class="player-number">#{{ player.number }}</span>
                            </div>
                            <div class="player-stats">
                              {{ player.participatingEvents }} из {{ player.totalEvents }} событий
                              ({{ player.participationRate }}%)
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div v-else class="loading-stats">
                      <a-spin />
                      <p>Загрузка статистики...</p>
                    </div>
                  </div>
                </div>
              </a-tab-pane>

            </a-tabs>

            <!-- Full Screen Avatar Modal -->
            <div 
              v-if="showFullAvatar" 
              class="full-avatar-overlay"
              @click="showFullAvatar = false"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <img 
                :src="selectedPlayerProfile?.photo_url" 
                class="full-avatar"
                @click.stop
              />
            </div>
          </div>
          <!-- Event Screen -->
          <div v-if="currentScreen === 'event'" class="screen-content event-screen">
            <!-- Event Details Card -->
            <a-card class="event-card" :bordered="false">
              <!-- Re-using event list card header -->
              <div class="event-card-header">
                
          
                  <h3>{{ selectedEvent.type === 'game' ? 'Игра' : 'Тренировка' }}</h3>
                
                <div v-if="isAdmin" class="event-actions">
                  <a-button type="text" @click.stop="editEvent(selectedEvent)" class="edit-icon-button">
                    <EditOutlined />
                  </a-button>
                </div>
              </div>

              <!-- Re-using event list card content structure -->
              <div class="event-card-content">
                <!-- Event Details -->
                <div class="event-details">
                  <div class="event-info-row">
                    <ClockCircleOutlined class="event-icon" />
                    <span class="event-text event-date-highlight">{{ formatEventDate(new Date(selectedEvent.date)) }}</span>
                  </div>
                  <div v-if="selectedEvent.location && selectedEvent.location !== ''" class="event-info-row">
                    <EnvironmentOutlined class="event-icon" />
                    <span class="event-text">{{ selectedEvent.location }}</span>
                  </div>
                  <div v-if="selectedEvent.type === 'game' && selectedEvent.opponent" class="event-info-row">
                    <TeamOutlined class="event-icon" />
                    <span class="event-text opponent-text">vs {{ selectedEvent.opponent }}</span>
                  </div>
                  <div v-if="selectedEvent.comment" class="event-info-row">
                    <CommentOutlined class="event-icon" />
                    <span class="event-text comment-text">{{ selectedEvent.comment }}</span>
                  </div>
                  <div v-if="selectedEvent.roster_name" class="event-info-row">
                    <TeamOutlined class="event-icon" />
                    <span class="event-text roster-text">
                      Состав: {{ selectedEvent.roster_name }}
                      <span v-if="selectedEvent.roster_is_default" class="roster-default-badge">(основной)</span>
                    </span>
                  </div>
                </div>

                <!-- User's participation status -->
                <div class="event-stats" @click="openEventParticipationDrawer(selectedEvent)">
                   <div 
                    class="stat-item"
                    :class="{
                      'participating': selectedEvent.user_participation_status === 2,
                      'not-participating': selectedEvent.user_participation_status === 1,
                      'unknown': selectedEvent.user_participation_status === 0,
                      'clickable': true
                    }"
                  >
                    <ParticipationIcon :status="selectedEvent.user_participation_status" />
                    <span class="stat-text-full" style="font-size: 14px;">{{ getParticipationStatusText(selectedEvent?.user_participation_status) }}</span>
                  </div>
                </div>
              </div>
            </a-card>
            
            <!-- Admin notification button -->
            <div v-if="isAdmin && selectedEvent.stats?.unknown > 0" class="notification-section">
              <a-card class="notification-card" :bordered="false">
                <div class="notification-content">
                  <div class="notification-info">
                    <BellOutlined class="notification-icon" />
                    <div class="notification-text">
                      <div class="notification-title">Участники без статуса</div>
                      <div class="notification-subtitle">{{ selectedEvent.stats.unknown }} игроков не отметили участие</div>
                    </div>
                  </div>
                  <a-button 
                    type="primary" 
                    @click="sendNotificationsToUnknown"
                    :loading="sendingNotifications"
                    class="notification-button"
                  >
                    <BellOutlined />
                    Отправить уведомления
                  </a-button>
                </div>
              </a-card>
            </div>
            
            <!-- Вкладки -->
            <a-card class="event-card" :bordered="false">
            <a-tabs v-model:activeKey="activeTab" @change="onTabChange" class="event-tabs" >
              <!-- Вкладка 1: Список игроков -->
              <a-tab-pane key="players" tab="Игроки">
               
                <a-list :data-source="sortedPlayers" item-layout="horizontal" class="event-players-list">
                  <template #renderItem="{ item }">
                    <a-list-item :key="item.id" @click="openPlayerProfile(item)">
                      <template #extra>
                        <div class="player-card">
                          <div class="avatar-container">
                            <div class="avatar-wrapper" :style="item.is_participating === 2 && (item.color === 'white' || item.color === 'yellow') ? { border: '1px solid #ccc' } : {}">
                              <a-avatar
                                :key="item.id"
                                :src="item.photo_url"
                                shape="circle"
                                class="player-avatar"
                                :style="item.is_participating === 2 && item.color ? { border: `2px solid ${item.color}` } : ''"
                              >{{ (item.last_name || item.first_name || '?').charAt(0).toUpperCase() }}</a-avatar>
                            </div>
                            <!-- Индикатор участия на аватарке -->
                            <div class="avatar-indicator">
                              <ParticipationIcon :status=item.is_participating />
                            </div>
                            <!-- Индикатор позиции -->
                            <div class="position-wrapper" >
                              <div
                                class="position-indicator"
                                :style="item.is_participating === 2 && item.color ? { backgroundColor: item.color, color: (item.color === 'white' || item.color === 'yellow')  ? '#000' : '#fff', border: (item.color === 'white' || item.color === 'yellow') ? '1px solid #ccc' : 'none', width: (item.color === 'white' || item.color === 'yellow')  ? '18px' : '20px', height: (item.color === 'white' || item.color === 'yellow')  ? '18px' : '20px' } : {}"
                              >
                                {{ $t(`positions.${item.position}`).charAt(0) }}
                              </div>
                            </div>
                          </div>

                          <div class="player-info">
                            <div class="player-name">
                              <span class="player-name-text">{{ item.last_name }}</span>
                              <span class="player-name-text">{{ item.first_name }}</span>
                            </div>
                            <div class="player-comment">
                              <span v-if="isBirthdayToday(item.date_of_birth)" class="birthday-indicator" title="День рождения!">🎂</span>
                              <i>{{ item.comment }}</i>
                            </div>
                          </div>
                          <div class="player-status-number">
                            <div class="player-status">
                              <span v-if="item.captain_status === 'assistant'" class="assistant-status">А</span>
                              <span v-if="item.captain_status === 'captain'" class="captain-status">К</span>
                            </div>
                            <div class="player-number">
                              {{ item.number }}
                            </div>
                          </div>

                        <!-- Dropdown menu for admin to change player status -->
                        <div v-if="isAdmin" class="admin-actions" @click.stop>
                            <a-dropdown trigger="['click']">
                                <a-button type="text">
                                    <MoreOutlined />
                                </a-button>
                                <template #overlay>
                                    <a-menu @click="({ key }) => setPlayerParticipation(item, Number(key))">
                                        <a-menu-item :key="2">
                                            <CheckOutlined /> Участвую
                                        </a-menu-item>
                                        <a-menu-item :key="1">
                                            <CloseOutlined /> Не участвую
                                        </a-menu-item>
                                        <a-menu-item :key="0">
                                            <QuestionOutlined /> Без статуса
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </div>

                         
                        </div>
                      </template>
                    </a-list-item>
                  </template>
                </a-list>
              
              </a-tab-pane>

              <!-- Вкладка 2: Хоккейное поле -->
              <a-tab-pane key="field" tab="Поле">
                <div class="event-card">
                <!-- Общий выбор цвета -->
                <div v-if="isAdmin" class="color-selection-global">
                  <span>Цвет команды: </span>
                  <a-dropdown trigger="click">
                    <div class="color-option">
                      <div class="color-preview" :style="{ backgroundColor: defaultPlayerColor || '#CCCCCC' }"></div>
                    </div>
                    <template #overlay>
                      <a-menu @click="selectDefaultColor" >
                        <a-menu-item key="red">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: red;"></div>
                            Красный
                          </div>
                        </a-menu-item>
                        <a-menu-item key="blue">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: blue;"></div>
                            Синий
                          </div>
                        </a-menu-item>
                        <a-menu-item key="green">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: green;"></div>
                            Зеленый
                          </div>
                        </a-menu-item>
                        <a-menu-item key="yellow">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: yellow;"></div>
                            Желтый
                          </div>
                        </a-menu-item>
                        <a-menu-item key="white">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: white; border: 1px solid #ccc;"></div>
                            Белый
                          </div>
                        </a-menu-item>
                        <a-menu-item key="black">
                          <div class="color-option">
                            <div class="color-preview" style="background-color: black;"></div>
                            Черный
                          </div>
                        </a-menu-item>
                      </a-menu>
                    </template>
                  </a-dropdown>
                </div>

                <div  style="padding: 0px 24px;">
                <div class="hockey-fields-container" >
                  <div v-for="field in fields" :key="field.id">
                    <!-- Выбор цвета для конкретного поля -->
                    <div class="field-color-selection">
                      <h3>Звено {{ field.id }}</h3>
                      <a-dropdown v-if="isAdmin" trigger="click">
                        <a-button>
                          <div class="color-preview" :style="{ backgroundColor: field.selectedColor || '#CCCCCC' }"></div>
                        </a-button>
                        <template #overlay>
                          <a-menu @click="(e) => selectFieldColor(field.id, e.key)">
                            <a-menu-item key="red">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: red;"></div>
                                Красный
                              </div>
                            </a-menu-item>
                            <a-menu-item key="blue">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: blue;"></div>
                                Синий
                              </div>
                            </a-menu-item>
                            <a-menu-item key="green">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: green;"></div>
                                Зеленый
                              </div>
                            </a-menu-item>
                            <a-menu-item key="yellow">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: yellow;"></div>
                                Желтый
                              </div>
                            </a-menu-item>
                            <a-menu-item key="white">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: white; border: 1px solid #ccc;"></div>
                                Белый
                              </div>
                            </a-menu-item>
                            <a-menu-item key="black">
                              <div class="color-option">
                                <div class="color-preview" style="background-color: black;"></div>
                                Черный
                              </div>
                            </a-menu-item>
                            <a-menu-item key="">
                              <div class="color-option">
                                Использовать общий цвет
                              </div>
                            </a-menu-item>
                          </a-menu>
                        </template>
                      </a-dropdown>
                    </div>

                    <div class="hockey-field">
                    <div class="center-line"></div>
                    <div class="center-circle"></div>
                    <div class="blue-line"></div>
                    <div class="faceoff-circle left">
                      <div class="center-dot"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                    </div>
                    <div class="faceoff-circle right">
                      <div class="center-dot"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                      <div class="faceoff-whisker"></div>
                    </div>
                    <div class="goal-crease"></div>
                    <div class="goal"></div>
                    <div class="goal-line"></div>
                    

                    <div class="position-row top">
                      <div
                        v-for="(place, index) in field.topPlaces"
                        :key="index"
                        class="position-circle"
                        :class="`top-position-${index + 1}`"
                        :style="{ borderColor: place.color }"
                      >
                          <!-- ADMIN VIEW -->
    <template v-if="isAdmin">
      <!-- Dropdown to add player (only if position is empty and there are players to add) -->
      <a-dropdown
        v-if="!place.player && availablePlayers.length > 0"
        v-model:visible="field.menuVisibility[place.id]"
        :trigger="['click']"
        :placement="field.menuPlacement"
        @click="openPlayerMenu(field, place)"
      >
        <div class="position-circle-content interactive">
          <span>+</span>
        </div>
        <template #overlay>
          <ScrollableDropdown 
            :available-players="availablePlayers"
            :field-id="field.id"
            :on-player-select="(player) => handlePlayerSelect(field, place, { key: player.id })"
          />
        </template>
      </a-dropdown>
      
      <!-- Empty position, but no players available to add (show message) -->
<a-dropdown
  v-else-if="!place.player"
  :trigger="['click']"
  :placement="field.menuPlacement"
>
  <div class="position-circle-content interactive">
    <span>+</span>
  </div>
  <template #overlay>
    <a-menu>
      <a-menu-item disabled>
        <ExclamationCircleOutlined style="color: #999;" />
        Нет доступных игроков
      </a-menu-item>
    </a-menu>
  </template>
</a-dropdown>

      <!-- Show assigned player with info popup and unassign button -->
      <a-dropdown
        v-else
        v-model:visible="field.menuVisibility[place.id]"
        :trigger="['click']"
        :placement="field.menuPlacement"
        @click="openPlayerMenu(field, place)"
      >
        <div class="position-circle-content interactive">
          <div class="player-avatar-container">
            <div class="avatar-wrapper" :style="(place.color === 'white' || place.color === 'yellow') ? { border: '1px solid #ccc' } : {}">
              <a-avatar :src="place.player.photo_url" shape="circle" :size="56" />
            </div>
            <div v-if="place.player.number" class="player-number-on-field"
              :style="{
                backgroundColor: place.color || '#fff',
                color: (place.color === 'white' || place.color === 'yellow') ? '#000' : '#fff',
                border: (place.color === 'white' || place.color === 'yellow') ? '1px solid #ccc' : ''
              }"
            >
              {{ place.player.number }}
            </div>
          </div>
        </div>
        <template #overlay>
          <div class="player-info-popup">
            <div class="player-info-header">
              <a-avatar :src="place.player.photo_url" shape="circle" :size="48" />
              <div class="player-info-details">
                <div class="player-name">{{ place.player.last_name }} {{ place.player.first_name }}</div>
                <div v-if="place.player.number" class="player-number">№{{ place.player.number }}  <span v-if="place.player.captain_status === 'captain'" class="captain-status">К</span>
                  <span v-if="place.player.captain_status === 'assistant'" class="assistant-status">А</span></div>
               
              </div>
            </div>
            <div class="player-info-content">
              <div class="info-row">
                <span class="info-label">Позиция:</span>
                <span class="info-value">{{ getPositionLabel(place.player.position) || '—' }}</span>
              </div>
            </div>
            <div class="player-info-actions">
              <a-button type="primary" danger block @click="unassignPlayer(field, place)">
                Открепить
              </a-button>
            </div>
          </div>
        </template>
      </a-dropdown>
    </template>
    <!-- USER VIEW -->
    <template v-else>
      <!-- Show assigned player with info popup -->
      <a-dropdown
        v-if="place.player"
        :trigger="['click']"
        :placement="field.menuPlacement"
        @click="openPlayerMenu(field, place)"
      >
        <div class="position-circle-content interactive">
          <div class="player-avatar-container">
            <div class="avatar-wrapper" :style="(place.color === 'white' || place.color === 'yellow') ? { border: '1px solid #ccc' } : {}">
              <a-avatar :src="place.player.photo_url" shape="circle" :size="56" />
            </div>
            <div v-if="place.player.number" class="player-number-on-field"
              :style="{
                backgroundColor: place.color || '#fff',
                color: (place.color === 'white' || place.color === 'yellow') ? '#000' : '#fff',
                border: (place.color === 'white' || place.color === 'yellow') ? '1px solid #ccc' : ''
              }"
            >
              {{ place.player.number }}
            </div>
          </div>
        </div>
        <template #overlay>
          <div class="player-info-popup">
            <div class="player-info-header">
              <a-avatar :src="place.player.photo_url" shape="circle" :size="48" />
              <div class="player-info-details">
                <div class="player-name">{{ place.player.last_name }} {{ place.player.first_name }}</div>
                <div v-if="place.player.number" class="player-number">№{{ place.player.number }}  <span v-if="place.player.captain_status === 'captain'" class="captain-status">К</span>
                  <span v-if="place.player.captain_status === 'assistant'" class="assistant-status">А</span></div>
               
              </div>
            </div>
            <div class="player-info-content">
              <div class="info-row">
                <span class="info-label">Позиция:</span>
                <span class="info-value">{{ getPositionLabel(place.player.position) || '—' }}</span>
              </div>
            </div>
          </div>
        </template>
      </a-dropdown>
      
      <!-- Show empty position, no interaction -->
      <div v-else class="position-circle-content">
        <span>?</span>
      </div>
    </template>
                      </div>
                    </div>

                    <div class="position-row bottom">
                      <div
                        v-for="(place, index) in field.bottomPlaces"
                        :key="index"
                        class="position-circle"
                        :style="{ borderColor: place.color }"
                      >
                        <!-- ADMIN VIEW -->
                        <template v-if="isAdmin">
                          <!-- Dropdown to add player (only if position is empty and there are players to add) -->
                          <a-dropdown
                            v-if="!place.player && availablePlayers.length > 0"
                            v-model:visible="field.menuVisibility[place.id]"
                            :trigger="['click']"
                            :placement="field.menuPlacement"
                            @click="openPlayerMenu(field, place)"
                          >
                            <div class="position-circle-content interactive">
                              <span>+</span>
                            </div>
                            <template #overlay>
                              <ScrollableDropdown 
                                :available-players="availablePlayers"
                                :field-id="field.id"
                                :on-player-select="(player) => handlePlayerSelect(field, place, { key: player.id })"
                              />
                            </template>
                          </a-dropdown>
                          
                          <!-- Empty position, but no players available to add (show message) -->
                          <a-dropdown
                            v-else-if="!place.player"
                            :trigger="['click']"
                            :placement="field.menuPlacement"
                          >
                            <div class="position-circle-content interactive">
                              <span>+</span>
                            </div>
                            <template #overlay>
                              <a-menu>
                                <a-menu-item disabled>
                                  <ExclamationCircleOutlined style="color: #999;" />
                                  Нет доступных игроков
                                </a-menu-item>
                              </a-menu>
                            </template>
                          </a-dropdown>

                          <!-- Show assigned player with info popup and unassign button -->
                          <a-dropdown
                            v-else
                            v-model:visible="field.menuVisibility[place.id]"
                            :trigger="['click']"
                            :placement="field.menuPlacement"
                            @click="openPlayerMenu(field, place)"
                          >
                            <div class="position-circle-content interactive">
                              <div class="player-avatar-container">
                                <div class="avatar-wrapper" :style="(place.color === 'white' || place.color === 'yellow') ? { border: '1px solid #ccc' } : {}">
                                  <a-avatar :src="place.player.photo_url" shape="circle" :size="56" />
                                </div>
                                <div v-if="place.player.number" class="player-number-on-field"
                                  :style="{
                                    backgroundColor: place.color || '#fff',
                                    color: (place.color === 'white' || place.color === 'yellow') ? '#000' : '#fff',
                                    border: (place.color === 'white' || place.color === 'yellow') ? '1px solid #ccc' : ''
                                  }"
                                >
                                  {{ place.player.number }}
                                </div>
                              </div>
                            </div>
                            <template #overlay>
                              <div class="player-info-popup">
                                <div class="player-info-header">
                                  <a-avatar :src="place.player.photo_url" shape="circle" :size="48" />
                                  <div class="player-info-details">
                                    <div class="player-name">{{ place.player.last_name }} {{ place.player.first_name }}</div>
                                    <div v-if="place.player.number" class="player-number">№{{ place.player.number }}  <span v-if="place.player.captain_status === 'captain'" class="captain-status">К</span>
                                      <span v-if="place.player.captain_status === 'assistant'" class="assistant-status">А</span></div>
                                   
                                  </div>
                                </div>
                                <div class="player-info-content">
                                  <div class="info-row">
                                    <span class="info-label">Позиция:</span>
                                    <span class="info-value">{{ getPositionLabel(place.player.position) || '—' }}</span>
                                  </div>
                                </div>
                                <div class="player-info-actions">
                                  <a-button type="primary" danger block @click="unassignPlayer(field, place)">
                                    Открепить
                                  </a-button>
                                </div>
                              </div>
                            </template>
                          </a-dropdown>
                        </template>
                        <!-- USER VIEW -->
                        <template v-else>
                          <!-- Show assigned player with info popup -->
                          <a-dropdown
                            v-if="place.player"
                            :trigger="['click']"
                            :placement="field.menuPlacement"
                            @click="openPlayerMenu(field, place)"
                          >
                            <div class="position-circle-content interactive">
                              <div class="player-avatar-container">
                                <div class="avatar-wrapper" :style="(place.color === 'white' || place.color === 'yellow') ? { border: '1px solid #ccc' } : {}">
                                  <a-avatar :src="place.player.photo_url" shape="circle" :size="56" />
                                </div>
                                <div v-if="place.player.number" class="player-number-on-field"
                                  :style="{
                                    backgroundColor: place.color || '#fff',
                                    color: (place.color === 'white' || place.color === 'yellow') ? '#000' : '#fff',
                                    border: (place.color === 'white' || place.color === 'yellow') ? '1px solid #ccc' : ''
                                  }"
                                >
                                  {{ place.player.number }}
                                </div>
                              </div>
                            </div>
                            <template #overlay>
                              <div class="player-info-popup">
                                <div class="player-info-header">
                                  <a-avatar :src="place.player.photo_url" shape="circle" :size="48" />
                                  <div class="player-info-details">
                                    <div class="player-name">{{ place.player.last_name }} {{ place.player.first_name }}</div>
                                    <div v-if="place.player.number" class="player-number">№{{ place.player.number }}  <span v-if="place.player.captain_status === 'captain'" class="captain-status">К</span>
                                      <span v-if="place.player.captain_status === 'assistant'" class="assistant-status">А</span></div>
                                   
                                  </div>
                                </div>
                                <div class="player-info-content">
                                  <div class="info-row">
                                    <span class="info-label">Позиция:</span>
                                    <span class="info-value">{{ getPositionLabel(place.player.position) || '—' }}</span>
                                  </div>
                                </div>
                              </div>
                            </template>
                          </a-dropdown>
                          
                          <!-- Show empty position, no interaction -->
                          <div v-else class="position-circle-content">
                            <span>?</span>
                          </div>
                        </template>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              </a-tab-pane>
            </a-tabs>
          </a-card>
          </div>
        </div>

             <!-- Calendar Screen -->
    <div v-if="currentScreen === 'calendar'" class="calendar-screen">
      <div class="calendar-nav">
        <a-button type="text" @click="previousMonth" class="calendar-nav-button back-icon-button">
          <LeftOutlined />
        </a-button>
        <span class="month-name">{{ monthName }}</span>
        <a-button type="text" @click="nextMonth" class="calendar-nav-button back-icon-button">
          <LeftOutlined style="transform: rotate(180deg)" />
        </a-button>
      </div>
      <div class="calendar-grid">
        <div class="weekday-header" v-for="day in ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']" :key="day">
          {{ day }}
        </div>
        <div 
          v-for="(day, index) in calendarDays" 
          :key="index"
          class="calendar-day"
          :class="{
            'has-events': day && hasEventsOnDate(day),
            'has-birthdays': day && hasBirthdaysOnDate(day),
            'empty': !day,
            'today': day && isToday(day),
            'status-participating': day && getUserStatusForDate(day) === 2,
            'status-unknown': day && getUserStatusForDate(day) === 0,
            'status-not-participating': day && getUserStatusForDate(day) === 1
          }"
          @click="day && getEventsForDate(day).length > 0 && showScreenEvetPlayers(getEventsForDate(day)[0])"
        >
          <template v-if="day">
            <span class="day-number" :class="{ 'today': isToday(day) }">{{ day.getDate() }}</span>
            <div 
              class="calendar-indicators"
              :class="{ 'events-only': hasEventsOnDate(day) && !hasBirthdaysOnDate(day) }"
            >
              <div v-if="hasEventsOnDate(day)" class="event-indicator">
                <div class="event-dot"></div>
              </div>
              <div v-if="hasBirthdaysOnDate(day)" class="birthday-players-calendar">
                <div 
                  v-for="player in getBirthdaysForDate(day).slice(0, 2)" 
                  :key="player.id"
                  class="birthday-player-avatar"
                  :title="`${player.last_name} ${player.first_name}${player.number ? ' №' + player.number : ''}`"
                >
                  <a-avatar :src="player.photo_url" :size="30">
                    <template #icon>
                      <UserOutlined />
                    </template>
                  </a-avatar>
                  <span v-if="player.number" class="player-number-badge">{{ player.number }}</span>
                </div>
                <div v-if="getBirthdaysForDate(day).length > 2" class="more-birthdays" :title="getBirthdaysForDate(day).slice(2).map(p => `${p.first_name} ${p.last_name}`).join(', ')">
                  +{{ getBirthdaysForDate(day).length - 2 }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Create Event Screen -->
    <div v-if="currentScreen === 'createEvent' && isAdmin" class="form-screen">
      <a-form
        :model="newEvent"
        layout="vertical"
        @submit.prevent="createEvent"
        class="event-form"
      >
        <a-form-item
          label="Тип события"
          name="type"
          :rules="[{ required: true, message: 'Выберите тип события' }]"
        >
        <a-select v-model:value="newEvent.type">
              <a-select-option value="training">Тренировка</a-select-option>
              <a-select-option value="game">Игра</a-select-option>
            </a-select>
        </a-form-item>

        <a-form-item>
          <a-checkbox v-model:checked="isRepeatingEvent">
            Повторяющееся событие
          </a-checkbox>
        </a-form-item>

        <a-form-item
          v-if="!isRepeatingEvent"
          label="Дата"
          name="date"
          :rules="[{ required: true, message: 'Выберите дату' }]"
        >
          <a-date-picker v-model:value="newEvent.date" format="YYYY-MM-DD" style="width: 100%" />
        </a-form-item>

        <a-form-item label="Время" name="time" :rules="[{ required: true, message: 'Выберите время' }]">
          <a-time-picker v-model:value="newEvent.time" format="HH:mm" style="width: 100%" />
        </a-form-item>

        <a-form-item v-if="isRepeatingEvent" label="Период" :rules="[{ required: true, message: 'Выберите период' }]">
          <a-range-picker v-model:value="dateRange" format="DD.MM.YYYY" :locale="ruRU" style="width: 100%" />
        </a-form-item>

        <a-form-item v-if="isRepeatingEvent" label="Дни недели" :rules="[{ required: true, message: 'Выберите дни недели' }]">
          <a-checkbox-group v-model:value="repeatingEventData.days_of_week" :options="weekDays" style="width: 100%" />
        </a-form-item>

        <a-form-item
          v-if="newEvent.type === 'game'"
          label="Соперник"
          name="opponent"
          :rules="[{ required: true, message: 'Введите название соперника' }]"
        >
          <a-input v-model:value="newEvent.opponent" />
        </a-form-item>

        <a-form-item
          label="Место"
          name="location"
        >
          <a-input v-model:value="newEvent.location" placeholder="Место проведения (необязательно)" />
        </a-form-item>

        <a-form-item
          label="Состав"
          name="roster_id"
        >
          <a-select 
            v-model:value="newEvent.roster_id" 
            placeholder="Выберите состав (необязательно)"
            allow-clear
          >
            <a-select-option :value="null">Без состава</a-select-option>
            <a-select-option 
              v-for="roster in teamRosters" 
              :key="roster.id" 
              :value="roster.id"
            >
              {{ roster.name }} 
              <span v-if="roster.is_default" style="color: #1890ff;">(основной)</span>
            </a-select-option>
          </a-select>
          <div style="margin-top: 8px; font-size: 12px; color: #666;">
            Игроки вне выбранного состава автоматически получат статус "не участвую"
          </div>
        </a-form-item>

        <a-form-item
          label="Комментарий"
          name="comment"
        >
          <a-textarea
            v-model:value="newEvent.comment"
            :rows="4"
            placeholder="Дополнительная информация о событии"
          />
        </a-form-item>

        <a-form-item>
          <a-button @click="handleBackNavigation">
            Отмена
          </a-button>
          <a-button type="primary" html-type="submit">
            Создать
          </a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- Edit Event Screen -->
    <div v-if="currentScreen === 'editEvent' && isAdmin" class="form-screen">
      <template v-if="editingEvent">
        <div v-if="editingEvent.repeating_event_id" class="repeating-event-actions">
          <a-radio-group v-model:value="editModeValue" button-style="solid">
            <a-radio-button value="single">Только это событие</a-radio-button>
            <a-radio-button value="future">Все будущие события</a-radio-button>
            <a-radio-button value="all">Все события</a-radio-button>
          </a-radio-group>
        </div>

        <a-form
          :model="editingEvent"
          layout="vertical"
          @submit.prevent="saveEventEdit"
          class="event-form"
        >
          <a-form-item 
            label="Тип события" 
            name="type"
            :rules="[{ required: true, message: 'Выберите тип события' }]"
          >
            <a-select v-model:value="editingEvent.type">
              <a-select-option value="training">Тренировка</a-select-option>
              <a-select-option value="game">Игра</a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item 
            label="Дата" 
            name="date"
            :rules="[{ required: true, message: 'Выберите дату' }]"
          >
            <a-date-picker
              v-model:value="editingEvent.date"
              format="YYYY-MM-DD"
              style="width: 100%"
            />
          </a-form-item>

          <a-form-item label="Время" name="time" :rules="[{ required: true, message: 'Выберите время' }]">
            <a-time-picker v-model:value="editingEvent.time" format="HH:mm" style="width: 100%" />
          </a-form-item>
          
          <a-form-item 
            v-if="editingEvent.type === 'game'"
            label="Соперник" 
            name="opponent"
          >
            <a-input 
              v-model:value="editingEvent.opponent" 
              placeholder="Название команды соперника"
            />
          </a-form-item>
          
          <a-form-item 
            label="Место" 
            name="location"
          >
            <a-input 
              v-model:value="editingEvent.location" 
              placeholder="Место проведения (необязательно)"
            />
          </a-form-item>

          <a-form-item
            label="Состав"
            name="roster_id"
          >
            <a-select 
              v-model:value="editingEvent.roster_id" 
              placeholder="Выберите состав (необязательно)"
              allow-clear
            >
              <a-select-option :value="null">Без состава</a-select-option>
              <a-select-option 
                v-for="roster in teamRosters" 
                :key="roster.id" 
                :value="roster.id"
              >
                {{ roster.name }} 
                <span v-if="roster.is_default" style="color: #1890ff;">(основной)</span>
              </a-select-option>
            </a-select>
            <div style="margin-top: 8px; font-size: 12px; color: #666;">
              Игроки вне выбранного состава автоматически получат статус "не участвую"
            </div>
          </a-form-item>
          
          <a-form-item 
            label="Комментарий" 
            name="comment"
          >
            <a-textarea 
              v-model:value="editingEvent.comment" 
              placeholder="Комментарий к событию"
              :rows="4"
            />
          </a-form-item>
          
          <div class="drawer-footer">
            <a-button @click="closeEventEdit" style="margin-right: 8px">
              Отмена
            </a-button>
            <a-button type="primary" @click="saveEventEdit">
              Сохранить
            </a-button>
          </div>
        </a-form>
      </template>
    </div>

          <!-- Team Players Screen -->
          <div v-if="currentScreen === 'teamPlayers'" class="team-players-screen">
            <div class="team-players-header">
              <h2>Состав команды</h2>
              <p class="team-players-subtitle">{{ currentTeamName }}</p>
        </div>

            <div v-if="loadingTeamPlayers" class="loading-players">
              <a-spin size="large" />
              <p>Загрузка состава команды...</p>
            </div>

            <div v-else-if="teamPlayersData" class="team-players-content">
              <div class="players-summary">
                <div class="total-players">
                  <TeamOutlined />
                  <span>Всего игроков: {{ teamPlayersData.totalPlayers }}</span>
                </div>
              </div>

              <!-- Создание логинов для входа вне Telegram (только для админов) -->
              <div v-if="isTeamAdmin" class="web-credentials-section">
                <Card size="small" class="web-credentials-card">
                  <div class="web-credentials-content">
                    <div class="web-credentials-info">
                      <span class="web-credentials-icon">🔐</span>
                      <div>
                        <div class="web-credentials-title">Вход в приложение вне Telegram</div>
                        <div class="web-credentials-desc">
                          {{ playersWithoutWebLoginCount }} игроков без логина для входа в браузере.
                          После создания откроется окно с логинами и паролями — передайте их игрокам, если сообщение в Telegram не дошло (в т.ч. из‑за блокировок).
                        </div>
                        <div
                          v-if="playersWithWebLoginCount > 0"
                          class="web-credentials-desc web-credentials-resend-hint"
                        >
                          Уже с веб-логином: {{ playersWithWebLoginCount }} — у каждого в карточке есть «Выдать новый пароль», если пароль утерян; или «Отправить напоминание в Telegram» (логин и ссылка, пароль не меняется).
                        </div>
                      </div>
                    </div>
                    <div class="web-credentials-actions">
                    <a-button
                      type="primary"
                      :loading="generatingWebCredentials"
                      :disabled="playersWithoutWebLoginCount === 0"
                      @click="generateWebCredentialsForTeam"
                    >
                      Создать логины
                    </a-button>
                    <a-button
                      :loading="resendingWebCredentials"
                      :disabled="playersWithWebLoginCount === 0"
                      @click="resendWebCredentialsToTelegram"
                    >
                      Отправить напоминание в Telegram
                    </a-button>
                    </div>
                  </div>
                </Card>
              </div>

              <div class="positions-container">
                <!-- Forwards -->
                <div v-if="teamPlayersData.players.forward.length > 0" class="position-section">
                  <h3 class="position-title">
                    <span class="position-icon">⚽</span>
                    {{ $t("positions.forward") }} ({{ teamPlayersData.players.forward.length }})
                  </h3>
                  <div class="players-grid">
                    <div 
                      v-for="player in teamPlayersData.players.forward" 
                      :key="player.id"
                      class="player-card"
                      @click="openPlayerProfile(player)"
                    >
                      <div class="player-avatar">
                        <a-avatar :src="player.photo_url" :size="48" />
                      </div>
                      <div class="player-info">
                        <div class="player-name">
                          <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                        </div>
                        <div>
                          <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                          <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                        </div>
                        <div class="player-number-with-edit">
                          <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                          <a-button
                            v-if="isTeamAdmin"
                            class="edit-player-btn"
                            type="text"
                            size="small"
                            @click="openEditPlayerModal(player, $event)"
                          >
                            <EditOutlined />
                          </a-button>
                        </div>
                        <div class="player-username" v-if="player.username">
                          <a 
                            :href="`https://t.me/${player.username}`" 
                            target="_blank" 
                            class="username-link"
                            @click.stop
                          >
                            @{{ player.username }}
                          </a>
                        </div>
                        <TeamPlayerResetPasswordButton
                          v-if="isTeamAdmin"
                          :player="player"
                          :loading="resettingWebPasswordUserId === player.user_id"
                          @reset="resetWebPasswordForPlayer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Defenders -->
                <div v-if="teamPlayersData.players.defender.length > 0" class="position-section">
                  <h3 class="position-title">
                    <span class="position-icon">🛡️</span>
                    {{ $t("positions.defender") }} ({{ teamPlayersData.players.defender.length }})
                  </h3>
                  <div class="players-grid">
                    <div 
                      v-for="player in teamPlayersData.players.defender" 
                      :key="player.id"
                      class="player-card"
                      @click="openPlayerProfile(player)"
                    >
                      <div class="player-avatar">
                        <a-avatar :src="player.photo_url" :size="48" />
                      </div>
                      <div class="player-info">
                        <div class="player-name">
                          <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                        </div>
                        <div>
                          <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                          <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                        </div>
                        <div class="player-number-with-edit">
                          <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                          <a-button
                            v-if="isTeamAdmin"
                            class="edit-player-btn"
                            type="text"
                            size="small"
                            @click="openEditPlayerModal(player, $event)"
                          >
                            <EditOutlined />
                          </a-button>
                        </div>
                        <div class="player-username" v-if="player.username">
                          <a 
                            :href="`https://t.me/${player.username}`" 
                            target="_blank" 
                            class="username-link"
                            @click.stop
                          >
                            @{{ player.username }}
                          </a>
                        </div>
                        <TeamPlayerResetPasswordButton
                          v-if="isTeamAdmin"
                          :player="player"
                          :loading="resettingWebPasswordUserId === player.user_id"
                          @reset="resetWebPasswordForPlayer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Goalkeepers -->
                <div v-if="teamPlayersData.players.goalkeeper.length > 0" class="position-section">
                  <h3 class="position-title">
                    <span class="position-icon">🥅</span>
                    {{ $t("positions.goalkeeper") }} ({{ teamPlayersData.players.goalkeeper.length }})
                  </h3>
                  <div class="players-grid">
                    <div 
                      v-for="player in teamPlayersData.players.goalkeeper" 
                      :key="player.id"
                      class="player-card"
                      @click="openPlayerProfile(player)"
                    >
                      <div class="player-avatar">
                        <a-avatar :src="player.photo_url" :size="48" />
                      </div>
                      <div class="player-info">
                        <div class="player-name">
                          <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                        </div>
                        <div>
                          <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                          <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                        </div>
                        <div class="player-number-with-edit">
                          <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                          <a-button
                            v-if="isTeamAdmin"
                            class="edit-player-btn"
                            type="text"
                            size="small"
                            @click="openEditPlayerModal(player, $event)"
                          >
                            <EditOutlined />
                          </a-button>
                        </div>
                        <div class="player-username" v-if="player.username">
                          <a 
                            :href="`https://t.me/${player.username}`" 
                            target="_blank" 
                            class="username-link"
                            @click.stop
                          >
                            @{{ player.username }}
                          </a>
                        </div>
                        <TeamPlayerResetPasswordButton
                          v-if="isTeamAdmin"
                          :player="player"
                          :loading="resettingWebPasswordUserId === player.user_id"
                          @reset="resetWebPasswordForPlayer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Coaches -->
                <div v-if="teamPlayersData.players.coach.length > 0" class="position-section">
                  <h3 class="position-title">
                    <span class="position-icon">📋</span>
                    {{ $t("positions.coach") }} ({{ teamPlayersData.players.coach.length }})
                  </h3>
                  <div class="players-grid">
                    <div 
                      v-for="player in teamPlayersData.players.coach" 
                      :key="player.id"
                      class="player-card"
                      @click="openPlayerProfile(player)"
                    >
                      <div class="player-avatar">
                        <a-avatar :src="player.photo_url" :size="48" />
                      </div>
                      <div class="player-info">
                        <div class="player-name">
                          <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                        </div>
                        <div>
                          <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                          <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                        </div>
                        <div class="player-number-with-edit">
                          <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                          <a-button
                            v-if="isTeamAdmin"
                            class="edit-player-btn"
                            type="text"
                            size="small"
                            @click="openEditPlayerModal(player, $event)"
                          >
                            <EditOutlined />
                          </a-button>
                        </div>
                        <div class="player-username" v-if="player.username">
                          <a 
                            :href="`https://t.me/${player.username}`" 
                            target="_blank" 
                            class="username-link"
                            @click.stop
                          >
                            @{{ player.username }}
                          </a>
                        </div>
                        <TeamPlayerResetPasswordButton
                          v-if="isTeamAdmin"
                          :player="player"
                          :loading="resettingWebPasswordUserId === player.user_id"
                          @reset="resetWebPasswordForPlayer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Service -->
                <div v-if="teamPlayersData.players.service.length > 0" class="position-section">
                  <h3 class="position-title">
                    <span class="position-icon">🔧</span>
                    {{ $t("positions.service") }} ({{ teamPlayersData.players.service.length }})
                  </h3>
                  <div class="players-grid">
                    <div 
                      v-for="player in teamPlayersData.players.service" 
                      :key="player.id"
                      class="player-card"
                      @click="openPlayerProfile(player)"
                    >
                      <div class="player-avatar">
                        <a-avatar :src="player.photo_url" :size="48" />
                      </div>
                      <div class="player-info">
                        <div class="player-name">
                          <span class="player-name-text">{{ player.last_name }} {{ player.first_name }}</span>
                        </div>
                        <div>
                          <span v-if="player.captain_status === 'captain'" class="captain-status">К</span>
                          <span v-if="player.captain_status === 'assistant'" class="assistant-status">А</span>
                        </div>
                        <div class="player-number-with-edit">
                          <span v-if="player.number" class="player-number">#{{ player.number }}</span>
                          <a-button
                            v-if="isTeamAdmin"
                            class="edit-player-btn"
                            type="text"
                            size="small"
                            @click="openEditPlayerModal(player, $event)"
                          >
                            <EditOutlined />
                          </a-button>
                        </div>
                        <div class="player-username" v-if="player.username">
                          <a 
                            :href="`https://t.me/${player.username}`" 
                            target="_blank" 
                            class="username-link"
                            @click.stop
                          >
                            @{{ player.username }}
                          </a>
                        </div>
                        <TeamPlayerResetPasswordButton
                          v-if="isTeamAdmin"
                          :player="player"
                          :loading="resettingWebPasswordUserId === player.user_id"
                          @reset="resetWebPasswordForPlayer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-if="teamPlayersData.totalPlayers === 0" class="empty-players">
                  <TeamOutlined class="empty-icon" />
                  <h3>В команде пока нет игроков</h3>
                  <p>Игроки появятся здесь после одобрения заявок на вступление</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Search Teams Screen -->
          <div v-if="currentScreen === 'searchTeams'" class="screen-content">
            <div class="search-container">
              <h3>Поиск команды</h3>
              <p class="search-description">Найдите команду по названию и подайте заявку на вступление</p>
              
              <div class="search-input-section">
                <a-input-search 
                  v-model:value="searchQuery" 
                  placeholder="Введите название команды" 
                  size="large"
                  @input="searchTeams"
                  @search="searchTeams"
                  style="margin-bottom: 24px;"
                />
              </div>

              <div v-if="searchResults.length > 0" class="search-results">
                <h3>Найденные команды ({{ searchResults.length }})</h3>
                <a-list :data-source="searchResults" item-layout="horizontal">
                  <template #renderItem="{ item }">
                    <a-list-item class="team-search-item">
                      <a-list-item-meta @click="viewTeamInfo(item.id)">
                        <template #avatar>
                          <a-avatar :src="item.logo_url" shape="circle" size="large" />
                        </template>
                        <template #title>
                          <span class="team-name">{{ item.name }}</span>
                        </template>
                        <template #description>
                          <div class="team-details">
                            <span v-if="item.city" class="team-city">{{ item.city }}</span>
                            <span v-if="item.user_status === 'pending'" class="status-badge pending">
                              Заявка на рассмотрении
                            </span>
                            <span v-if="item.user_status === 'rejected'" class="status-badge rejected">
                              Заявка отклонена
                            </span>
                          </div>
                        </template>
                      </a-list-item-meta>
                      <template #actions>
                        <a-button 
                          v-if="!item.user_status" 
                          type="primary" 
                          @click="joinTeam(item.id)"
                        >
                          +
                        </a-button>
                        <a-button 
                          v-else-if="item.user_status === 'rejected'" 
                          type="primary" 
                          @click="joinTeam(item.id)"
                        >
                          Подать заявку снова
                        </a-button>
                        <a-button 
                          v-else-if="item.user_status === 'pending'" 
                          disabled
                        >
                          Заявка отправлена
                        </a-button>
                      </template>
                    </a-list-item>
                  </template>
                </a-list>
              </div>

              <div v-else-if="searchQuery.length >= 2 && searchResults.length === 0" class="no-results">
                <div class="no-results-content">
                  <SearchOutlined class="no-results-icon" />
                  <h3>Команды не найдены</h3>
                  <p>Попробуйте изменить поисковый запрос или создать новую команду</p>
                  <a-button type="primary" @click="showScreen('createTeam')">
                    <PlusOutlined /> Создать команду
                  </a-button>
                </div>
              </div>

              <div v-else-if="searchQuery.length === 0" class="search-placeholder">
                <div class="search-placeholder-content">
                  <SearchOutlined class="search-placeholder-icon" />
                  <h3>Начните поиск</h3>
                  <p>Введите название команды, чтобы найти её и подать заявку на вступление</p>
                </div>
              </div>
            </div>
          </div>

          <!-- View Team Info Screen -->
          <div v-if="currentScreen === 'viewTeamInfo' && selectedTeamInfo" class="team-info-screen">
            <div class="team-info-header">
              <a-avatar :src="selectedTeamInfo.logo_url" :size="120"  shape="square" class="team-logo" />
              <div class="team-basic-info">
                <h2 class="team-name">{{ selectedTeamInfo.name }}</h2>
                <p v-if="selectedTeamInfo.city" class="team-city">
                  <EnvironmentOutlined /> {{ selectedTeamInfo.city }}
                </p>
              </div>
            </div>
            <div class="team-info-content">
              <div v-if="selectedTeamInfo.description" class="info-section">
                <h3>О команде</h3>
                <p class="team-description">{{ selectedTeamInfo.description }}</p>
              </div>
              <div v-if="selectedTeamInfo.social_networks" class="info-section">
                <h3>Социальные сети</h3>
                <div class="social-networks">
                  <a v-for="(link, index) in selectedTeamInfo.social_networks.split('\n').filter(l => l.trim())" :key="index" :href="link" target="_blank" rel="noopener noreferrer" class="social-link">
                    {{ link }}
                  </a>
                </div>
              </div>
              <a-button 
                v-if="!selectedTeamInfo.user_status" 
                type="primary" 
                size="large" 
                block 
                style="margin-top: 32px; font-size: 20px; height: 56px;" 
                @click="joinTeam(selectedTeamInfo.id)"
              >
                <PlusOutlined /> Присоединиться к команде
              </a-button>
              <a-button 
                v-else-if="selectedTeamInfo.user_status === 'rejected'" 
                type="primary" 
                size="large" 
                block 
                style="margin-top: 32px; font-size: 20px; height: 56px;" 
                @click="joinTeam(selectedTeamInfo.id)"
              >
                <PlusOutlined /> Подать заявку снова
              </a-button>
              <a-button 
                v-else-if="selectedTeamInfo.user_status === 'pending'" 
                disabled
                size="large" 
                block 
                style="margin-top: 32px; font-size: 20px; height: 56px;"
              >
                <ClockCircleOutlined /> Заявка на рассмотрении
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drawer Menu -->
    <a-drawer v-model:open="openDrawer" placement="left" :closable="false" :width="320" @close="onClose">
      <div class="telegram-sidebar">
        <!-- User Profile Section -->
        <div class="sidebar-header">
          <div class="user-profile" >
            <a-avatar 
              :src="userData?.photo_url" 
              :size="64" 
              shape="circle"
              class="profile-avatar"
              @click="showProfile"
            />
            <div class="user-info">
              <h3 class="user-name">{{ userData?.last_name }} {{ userData?.first_name }}</h3>
              <!-- Test User Indicator (only in development) -->
              <div v-if="!isProduction" class="test-user-indicator">
                <a-tag :color="'blue'" size="small">
                  {{ testUsers[currentTestUser]?.name }}
                </a-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- Menu Items -->
        <div class="sidebar-menu">
          <a-menu
            mode="inline"
            class="sidebar-menu-component"
            :selected-keys="selectedMenuKeys"
            @click="handleMenuClick"
          >
            <!-- Teams Section -->
            <a-menu-item-group key="teams" title="Мои команды">
              <a-menu-item 
                v-for="team in userTeams" 
                :key="`team-${team.id}`"
                class="team-menu-item"
              >
                <template #icon>
                  <a-avatar 
                    v-if="team.logo_url" 
                    :src="team.logo_url" 
                    :size="24" 
                    shape="square"
                    class="team-logo-icon"
                  />
                  <TeamOutlined v-else />
                </template>
                <span>{{ team.name }}</span>
               
              </a-menu-item>
              <a-menu-item v-if="userTeams.length === 0" key="no-teams" disabled>
                <template #icon>
                  <TeamOutlined />
                </template>
                <span>Нет доступных команд</span>
              </a-menu-item>

              <a-menu-item key="search-teams">
                <template #icon>
                  <SearchOutlined />
                </template>
                <span>Поиск команды</span>
              </a-menu-item>

             
              <a-menu-item key="create-team">
                <template #icon>
                  <PlusOutlined />
                </template>
                <span>Создать команду</span>
              </a-menu-item>


             

              <a-menu-item v-if="isAdmin" key="pending-requests">
                <template #icon>
                  <TeamOutlined />
                </template>
                <span>Заявки на вступление</span>
                <a-badge 
                  v-if="totalPendingRequests > 0" 
                  :count="totalPendingRequests" 
                  class="pending-badge"
                />
              </a-menu-item>

            </a-menu-item-group>

            <!-- Actions Section -->
            <a-menu-item-group key="actions" title="Действия">

              <a-menu-item key="participation-settings">
                <template #icon>
                  <ClockCircleOutlined />
                </template>
                <span>Настройки участия</span>
              </a-menu-item>
              
              <a-menu-item key="payments">
                <template #icon>
                  <DollarOutlined />
                </template>
                <span>Платежи</span>
              </a-menu-item>

              <a-menu-item
                v-if="currentTeamId && currentTeamInfo?.best_player_voting_enabled"
                key="best-player-voting"
              >
                <template #icon>
                  <TrophyOutlined />
                </template>
                <span>Голосования</span>
              </a-menu-item>

            </a-menu-item-group>

            <!-- Settings Section -->
            <a-menu-item-group key="settings" title="Настройки">
              
             




              <a-menu-item key="add-to-homescreen">
                <template #icon>
                  <HomeOutlined />
                </template>
                <span>Создать ярлык</span>
              </a-menu-item>

              <a-menu-item key="profile">
                <template #icon>
                  <UserOutlined />
                </template>
                <span>Профиль</span>
              </a-menu-item>

               

              <a-menu-item v-if="!isProduction" key="theme">
                <template #icon>
                  <ThemeSwitcher />
                </template>
                <span>Тема</span>
              </a-menu-item>
              
              <!-- Test Admin Toggle (only in development) -->
              <a-menu-item v-if="!isProduction" key="toggle-admin">
                <template #icon>
                  <UserOutlined />
                </template>
                <span>{{ isAdmin ? 'Отключить админ' : 'Включить админ' }}</span>
              </a-menu-item>

              <!-- Test User Switcher (only in development) -->
              <a-menu-item v-if="!isProduction" key="test-user-switcher">
                <template #icon>
                  <UserOutlined />
                </template>
                <div class="test-user-switcher">
                  <span>Тестовый пользователь</span>
                  <a-select
                    v-model:value="currentTestUser"
                    size="small"
                    style="width: 140px; margin-left: 8px;"
                    @change="switchTestUser"
                    :options="Object.keys(testUsers).map(key => ({
                      value: key,
                      label: testUsers[key].name
                    }))"
                    placeholder="Выберите пользователя"
                  />
                </div>
              </a-menu-item>
            </a-menu-item-group>
          </a-menu>
        </div>

        <!-- Footer -->
        <div class="sidebar-footer">
          <div class="app-info">
            <p>Версия 25.10</p>
          </div>
        </div>
      </div>
    </a-drawer>

    <!-- Add drawer for event participation -->
    <a-drawer
      :visible="drawerVisible"
      :closable="true"
      @close="closeDrawerPlayer"
      placement="bottom"
      :height="230"
      :title="selectedEvent ? `${selectedEvent.type === 'game' ? 'Игра' : 'Тренировка'}   ·   ${formatDate(new Date(selectedEvent.date))}` : ''"
    >
      

      <!-- Поле для ввода комментария -->
      <a-input
        v-model:value="comment"
        placeholder="Введите комментарий"
        class="comment-input"
      />

      <!-- Кнопки "Не участвую" и "Участвую" -->
      <div class="drawer-buttons">
        <a-button class="participation-button" @click="() => setEventParticipation(PARTICIPATION_STATUS.NOT_PARTICIPATING)">
          <ParticipationIcon :status="PARTICIPATION_STATUS.NOT_PARTICIPATING" />
          <span class="button-text">Не участвую</span>
        </a-button>
        <a-button type="primary" class="participation-button" @click="() => setEventParticipation(PARTICIPATION_STATUS.PARTICIPATING)">
          <ParticipationIcon :status="PARTICIPATION_STATUS.PARTICIPATING" />
          <span class="button-text">Участвую</span>
        </a-button>
      </div>
    </a-drawer>

    <!-- Create Invitation Modal -->
    <a-modal
      v-model:open="createInvitationModalVisible"
      title="Создать приглашение"
      @ok="createInvitation"
      @cancel="cancelCreateInvitation"
      :confirmLoading="creatingInvitation"
      okText="Создать"
      cancelText="Отмена"
    >
      <a-form layout="vertical">
        <a-form-item label="Максимальное количество использований">
          <a-input-number
            v-model:value="newInvitation.max_uses"
            :min="1"
            :max="1000"
            style="width: 100%"
            placeholder="Оставьте пустым для неограниченного количества"
          />
          <div class="form-help-text">
            Оставьте пустым, чтобы приглашение можно было использовать неограниченное количество раз
          </div>
        </a-form-item>
        
        <a-form-item label="Срок действия (дни)">
          <a-input-number
            v-model:value="newInvitation.expires_in_days"
            :min="1"
            :max="365"
            style="width: 100%"
            placeholder="Оставьте пустым для бессрочного приглашения"
          />
          <div class="form-help-text">
            Оставьте пустым, чтобы приглашение не имело срока действия
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Выданные логины/пароли (если Telegram не доставил — админ передаёт вручную) -->
    <a-modal
      v-model:open="issuedCredentialsModalVisible"
      title="Данные для входа игроков"
      width="640px"
      :footer="null"
      destroyOnClose
    >
      <a-alert
        type="warning"
        show-icon
        style="margin-bottom: 16px"
        message="Передайте каждому игроку только его строку. Не публикуйте в общих чатах."
      />
      <p style="margin-bottom: 12px; color: var(--text-secondary, #666); font-size: 13px">
        Отправка в Telegram с сервера при этом тоже выполняется, но при блокировках сообщение может не дойти — используйте этот список как основной способ.
      </p>
      <div
        v-for="row in issuedCredentialsRows"
        :key="row.userId"
        class="issued-credentials-row"
      >
        <div class="issued-credentials-name">{{ row.displayName || 'Игрок' }}</div>
        <div class="issued-credentials-field">
          <span class="issued-credentials-label">Логин</span>
          <code class="issued-credentials-value">{{ row.login }}</code>
          <a-button type="link" size="small" @click="copyIssuedCredential(row.login)">Копировать</a-button>
        </div>
        <div class="issued-credentials-field">
          <span class="issued-credentials-label">Пароль</span>
          <code class="issued-credentials-value">{{ row.password }}</code>
          <a-button type="link" size="small" @click="copyIssuedCredential(row.password)">Копировать</a-button>
        </div>
      </div>
    </a-modal>

    <!-- Edit Player Modal -->
    <a-modal
      v-model:open="editPlayerModalVisible"
      title="Редактировать данные игрока"
      @ok="savePlayerData"
      @cancel="closeEditPlayerModal"
      :confirmLoading="savingPlayerData"
      okText="Сохранить"
      cancelText="Отмена"
      width="600px"
    >
      <a-form layout="vertical">
        <a-form-item label="Имя">
          <a-input
            v-model:value="editPlayerForm.first_name"
            placeholder="Введите имя"
          />
        </a-form-item>
        
        <a-form-item label="Фамилия">
          <a-input
            v-model:value="editPlayerForm.last_name"
            placeholder="Введите фамилию"
          />
        </a-form-item>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <a-form-item label="Номер">
            <a-input-number
              v-model:value="editPlayerForm.number"
              :min="1"
              :max="99"
              style="width: 100%"
              placeholder="Номер"
            />
          </a-form-item>
          
          <a-form-item label="Позиция">
            <a-select
              v-model:value="editPlayerForm.position"
              placeholder="Выберите позицию"
              style="width: 100%"
            >
              <a-select-option value="forward">Нападающий</a-select-option>
              <a-select-option value="defender">Защитник</a-select-option>
              <a-select-option value="goalkeeper">Вратарь</a-select-option>
              <a-select-option value="coach">Тренер</a-select-option>
              <a-select-option value="service">Обслуживающий персонал</a-select-option>
            </a-select>
          </a-form-item>
        </div>

        <a-form-item label="Дата рождения">
          <a-date-picker
            v-model:value="editPlayerForm.date_of_birth"
            style="width: 100%"
            placeholder="Выберите дату"
            format="YYYY-MM-DD"
          />
        </a-form-item>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <a-form-item label="Рост (см)">
            <a-input-number
              v-model:value="editPlayerForm.height"
              :min="0"
              :max="250"
              style="width: 100%"
              placeholder="Рост в см"
            />
          </a-form-item>
          
          <a-form-item label="Вес (кг)">
            <a-input-number
              v-model:value="editPlayerForm.weight"
              :min="0"
              :max="200"
              style="width: 100%"
              placeholder="Вес в кг"
            />
          </a-form-item>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <a-form-item label="Хват клюшки">
            <a-select
              v-model:value="editPlayerForm.stick_hand"
              placeholder="Хват"
              style="width: 100%"
            >
              <a-select-option value="left">Левый</a-select-option>
              <a-select-option value="right">Правый</a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="Уровень навыков">
            <a-select
              v-model:value="editPlayerForm.skill_level"
              placeholder="Уровень"
              style="width: 100%"
            >
              <a-select-option value="beginner">Новичок</a-select-option>
              <a-select-option value="intermediate">Средний</a-select-option>
              <a-select-option value="experienced">Опытный</a-select-option>
            </a-select>
          </a-form-item>
        </div>

        <a-form-item label="Телефон для экстренной связи">
          <a-input
            v-model:value="editPlayerForm.emergency_phone"
            placeholder="Номер телефона"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Attendance Statistics Date Range Modal -->
    <a-modal
      v-model:open="showAttendanceStatisticsDateRange"
      title="Отправить статистику посещаемости"
      @ok="confirmSendAttendanceStatistics"
      @cancel="showAttendanceStatisticsDateRange = false"
      :confirmLoading="sendingAttendanceStatistics"
      okText="Отправить"
      cancelText="Отмена"
      width="500px"
    >
      <div style="margin-bottom: 16px;">
        <p>Выберите период для расчета статистики посещаемости:</p>
        <a-range-picker
          v-model:value="attendanceStatisticsDateRange"
          format="DD.MM.YYYY"
          style="width: 100%"
          :placeholder="['Начало периода', 'Конец периода']"
        />
      </div>
      <a-alert
        type="info"
        show-icon
        style="margin-top: 16px;"
      >
        <template #message>
          Статистика будет отправлена в настроенную Telegram группу
        </template>
      </a-alert>
    </a-modal>

  </a-config-provider>
</template>


<script>
import { MenuOutlined, MoreOutlined, ClockCircleOutlined, EnvironmentOutlined, LeftOutlined, CalendarOutlined, EditOutlined, CommentOutlined, PlusOutlined, SyncOutlined, DeleteOutlined, UploadOutlined, SearchOutlined, TeamOutlined, TrophyOutlined, RiseOutlined,
  CheckOutlined,
  CloseOutlined,
  QuestionOutlined,
  CrownOutlined,
  HomeOutlined,
  LinkOutlined,
  CopyOutlined,
  DashboardOutlined,
  BellOutlined,
  DollarOutlined,
  ReloadOutlined,
  SettingOutlined,
  BarChartOutlined,
  SendOutlined
  } from '@ant-design/icons-vue';
import { UserOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import ruRU from 'ant-design-vue/es/locale/ru_RU';

// Configure dayjs locale
dayjs.locale('ru');

import { Button, Card, Input, Select, DatePicker, Drawer, Tabs, Dropdown, Menu, List, Avatar, Popconfirm, message, TimePicker, Form, Checkbox, Radio, InputNumber, Badge } from 'ant-design-vue';

import { api } from './services/api';
import { PARTICIPATION_STATUS, PARTICIPATION_OPTIONS } from './constants/participation';
import { ref, computed, watch } from 'vue';
import { API_URL } from './config';
import { WS_URL } from './config';
import ParticipationIcon from './components/ParticipationIcon.vue';
import HockeyField from './components/HockeyField.vue';
import ScrollableDropdown from './components/ScrollableDropdown.vue';
import ThemeSwitcher from './components/ThemeSwitcher.vue';
import AdminPanel from './components/AdminPanel.vue';
import SubscriptionWarning from './components/SubscriptionWarning.vue';
import TeamBotManager from './components/TeamBotManager.vue';

import UserSubscriptionHistory from './components/UserSubscriptionHistory.vue';
import UserPaymentRegistration from './components/UserPaymentRegistration.vue';
import UserPaymentHistory from './components/UserPaymentHistory.vue';
import AdminPaymentManagement from './components/AdminPaymentManagement.vue';
import PaymentTypesManagement from './components/PaymentTypesManagement.vue';
import TeamRosterManagement from './components/TeamRosterManagement.vue';
import AppLogin from './components/AppLogin.vue';
import TeamPlayerResetPasswordButton from './components/TeamPlayerResetPasswordButton.vue';
import BestPlayerVoting from './components/BestPlayerVoting.vue';

import { dynamicIconsManager } from './utils/dynamicIcons';
import { preferredManifestTeamId, applyManifestLinkHref } from './utils/earlyPwaManifest';
import { botManager } from './services/botManager';

export default {
  name: 'App',
  components: {
    Button,
    Card,
    Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    DatePicker,
    'a-date-picker': DatePicker,
    'a-drawer': Drawer,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    'a-dropdown': Dropdown,
    'a-menu': Menu,
    'a-menu-item': Menu.Item,
    'a-popconfirm': Popconfirm,
    'a-time-picker': TimePicker,
    'a-form': Form,
    'a-form-item': Form.Item,
    'a-checkbox': Checkbox,
    'a-checkbox-group': Checkbox.Group,
    'a-range-picker': DatePicker.RangePicker,
    'a-radio-button': Radio.Button,
    'a-radio-group': Radio.Group,
    'a-input-number': InputNumber,
    'a-input': Input,
    'a-button': Button,
    'a-avatar': Avatar,
    'a-list': List,
    'a-list-item': List.Item,
    'a-list-item-meta': List.Item.Meta,
    'a-input-search': Input.Search,
    'a-badge': Badge,
    List,
    Avatar,
    MenuOutlined,
    UserOutlined,
    MoreOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    LeftOutlined,
    CalendarOutlined,
    EditOutlined,
    CommentOutlined,
    PlusOutlined,
    SyncOutlined,
    DeleteOutlined,
    UploadOutlined,
    SettingOutlined,
    ParticipationIcon,
    HockeyField,
    ScrollableDropdown,
    ThemeSwitcher,
    AdminPanel,
    SubscriptionWarning,
    TeamBotManager,
    TeamRosterManagement,
    AppLogin,
    TeamPlayerResetPasswordButton,
    
    UserSubscriptionHistory,
    UserPaymentRegistration,
    UserPaymentHistory,
    AdminPaymentManagement,

    Badge,
    SearchOutlined,
    TeamOutlined,
    TrophyOutlined,
    RiseOutlined,
    CheckOutlined,
    CloseOutlined,
    QuestionOutlined,
    CrownOutlined,
    HomeOutlined,
    LinkOutlined,
    CopyOutlined,
    DashboardOutlined,
    BellOutlined,
    DollarOutlined,
    ReloadOutlined,
    SettingOutlined,
    ArrowLeftOutlined,
    AdminPaymentManagement,
    PaymentTypesManagement,
    BestPlayerVoting
  },
  setup() {
    const showFullAvatar = ref(false);
    const fileInput = ref(null);
    const userData = ref({
      photo_url: '',
      first_name: '',
      last_name: '',
      username: '',
      date_of_birth: null,
      age: null,
      height: null,
      weight: null,
      stick_hand: null,
      position: null,
      skill_level: null,
      emergency_phone: ''
    });
    
    return {
      showFullAvatar,
      fileInput,
      userData,
    };
  },
  data() {
    return {
      ruRU,
      ws: null,
      PARTICIPATION_STATUS,
      API_URL,
      isAdmin: false,
      token: null,
      currentTeamId: null,
      currentScreen: 'main',
      previousScreen: null, // Add this line to track previous screen
      activeTab: 'players',
      activePlayerProfileTab: 'info',
      activeUserProfileTab: 'info',
              activePaymentsTab: 'registration',
      isPaymentAdmin: false, // Флаг, является ли пользователь администратором какого-либо платежа
      checkingPaymentAdminRights: false, // Флаг, выполняется ли проверка прав payment admin
      checkPaymentAdminRightsTimer: null, // Таймер для дебаунсинга проверки прав

      defaultPlayerColor: 'red',
      fields: Array.from({ length: 6 }, (_, index) => ({
        id: index + 1,
        selectedColor: '',
        topPlaces: [
          { id: `top-${index + 1}-1`, player: null },
          { id: `top-${index + 1}-2`, player: null },
          { id: `top-${index + 1}-3`, player: null },
        ],
        bottomPlaces: [
          { id: `bottom-${index + 1}-4`, player: null },
          { id: `bottom-${index + 1}-5`, player: null },
        ],
        menuVisibility: {},
        menuPlacement: 'bottomLeft', // По умолчанию
      })),

      userTeams: [],
      pendingTeams: [],
      pendingRequests: [],
      allPendingRequests: [], // Add this to track pending requests for all teams
      events: [],
      selectedEvent: null,
      searchQuery: '',
      searchResults: [],
      newTeam: {
        name: '',
        logo_url: '',
        city: '',
        description: '',
        social_networks: '',
      },
      selectedPosition: null,
      openDrawer: false,
      menuPlacement: 'bottom',
      newEventSelect: {
        type: 'training',
      },
      typeOptions: [
        {
          value: 'training',
          label: 'Тренировка',
        },
        {
          value: 'game',
          label: 'Игра',
        },
      ],
      participationSettings: {
        participation_until: null,
        default_participation: 0,
        default_comment: '',
      },
      participationOptions: PARTICIPATION_OPTIONS,

      drawerVisible: false,
      selectedPlayer: null,
      comment: '',
      showPlayerProfile: false,
      selectedPlayerProfile: null,
      showAdminPanel: false,
      calendarDate: new Date(),
      editingEvent: null,
      dateFormat: 'DD.MM.YYYY HH:mm',
      ruRU,
      newEvent: {
        type: 'training',
        date: dayjs(),
        time: dayjs(),
        opponent: '',
        location: '',
        comment: '',
        team_id: null,
        roster_id: null
      },
      isRepeatingEvent: false,
      teamRosters: [],
      repeatingEventData: {
        start_date: dayjs(),
        end_date: dayjs().add(1, 'month'),
        days_of_week: []
      },
      weekDays: [
       
        { label: 'Понедельник', value: 1 },
        { label: 'Вторник', value: 2 },
        { label: 'Среда', value: 3 },
        { label: 'Четверг', value: 4 },
        { label: 'Пятница', value: 5 },
        { label: 'Суббота', value: 6 },
        { label: 'Воскресенье', value: 0 }
      ],
      editMode: 'single', // Add this line for repeating event edit mode
      accessDenied: false,
      /** В production основной режим — браузер: сразу форма входа, без экрана ожидания. */
      showWebLoginForm: process.env.NODE_ENV === 'production',
      // Touch state for swipe-to-close
      touchStartY: null,
      touchStartTime: null,
      updatingProfile: false,
      webCredentialsForm: { login: '', password: '' },
      savingWebCredentials: false,
      generatingWebCredentials: false,
      resendingWebCredentials: false,
      resettingWebPasswordUserId: null,
      issuedCredentialsModalVisible: false,
      issuedCredentialsRows: [],
      uploadingPhoto: false,
      savingParticipationSettings: false,
      // Test user selector properties
      isProduction: process.env.NODE_ENV === 'production',
      currentTestUser: 'admin',
      testUsers: {
        admin: { token: 'dev-token-1', name: 'Администратор' },
        user1: { token: 'dev-token-2', name: 'Обычный пользователь 1' },
        user2: { token: 'dev-token-3', name: 'Обычный пользователь 2' },
        user3: { token: 'dev-token-4', name: 'Обычный пользователь 3' },
        coach: { token: 'dev-token-5', name: 'Тренер' },
        captain: { token: 'dev-token-6', name: 'Капитан команды' }
      },
      isEditingNumber: false,
      editingPlayerNumber: null,
      savingNumber: false,
      isEditingPosition: false,
      editingPlayerPosition: null,
      savingPosition: false,
      isEditingRole: false,
      editingPlayerRole: null,
      savingRole: false,
      editingTeam: null,
      creatingTeam: false,
      savingTeam: false,
      uploadingTeamLogo: false,
      savingNotifications: false,
      currentTeamInfo: null,
      attendanceStatisticsSettings: {
        group_id: '',
        topic_id: null,
        event_types: ['training'],
        bot_type: 'both',
        green_from: 67,
        yellow_from: 34
      },
      hasCustomBotForStats: false,
      savingAttendanceStatistics: false,
      sendingAttendanceStatistics: false,
      showAttendanceStatisticsDateRange: false,
      attendanceStatisticsDateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
      bestPlayerVotingEnabled: false,
      bestPlayerVotingSaving: false,
      // Statistics data
      selectedStatsYear: new Date().getFullYear(),
      selectedStatsMonth: new Date().getMonth() + 1,
      monthlyStats: null,
      teamRanking: null,
      loadingStats: false,
      // Team players data
      teamPlayersData: null,
      loadingTeamPlayers: false,
      activeTeamTab: 'info',
      selectedTeamInfo: null,
      selectedMenuKeys: [],
      eventsKey: 0, // Add this line for events key
      // Team invitations data
      teamInvitations: [],
      loadingInvitations: false,
      createInvitationModalVisible: false,
      newInvitation: {
        max_uses: null,
        expires_in_days: null
      },
      creatingInvitation: false,
      sendingNotifications: false,
      lastOwnParticipationUpdate: null, // Track own participation updates to avoid double counting
      isEditingCaptainStatus: false,
      editingPlayerCaptainStatus: null,
      // Edit player modal
      editPlayerModalVisible: false,
      editingPlayer: null,
      editPlayerForm: {
        first_name: '',
        last_name: '',
        number: null,
        position: null,
        date_of_birth: null,
        height: null,
        weight: null,
        stick_hand: null,
        skill_level: null,
        emergency_phone: ''
      },
      savingPlayerData: false,
      savingCaptainStatus: false,
      // Subscription payment system
      subscriptionStatus: [],
      loadingSubscriptionStatus: false,
      showSubscriptionWarning: true
    };
  },
  mounted() {
    const isProduction = process.env.NODE_ENV === 'production';
    let hasTelegramData = !!(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData);
    if (hasTelegramData) {
      try {
        window.Telegram.WebApp.ready();
      } catch (e) {
        console.warn('Telegram WebApp.ready() не выполнен:', e);
        hasTelegramData = false;
      }
    }

    if (hasTelegramData) {
      this.showWebLoginForm = false;
    } else if (!isProduction) {
      this.showWebLoginForm = false;
    }

    // Add token expiration handler
    window.addEventListener('token-expired', this.handleTokenExpired);

    if (!hasTelegramData) {
      const urlToken = this.popWebAuthTokenFromUrl();
      if (urlToken) {
        this.showWebLoginForm = false;
        api.setToken(urlToken);
        localStorage.setItem('webAuthToken', urlToken);
        api.getUserData()
          .then(() => {
            this.token = urlToken;
            this.showWebLoginForm = false;
            this.initApp();
          })
          .catch(() => {
            localStorage.removeItem('webAuthToken');
            api.setToken(null);
            if (isProduction) {
              this.showWebLoginForm = true;
            } else {
              const devToken = this.testUsers[this.currentTestUser].token;
              this.token = devToken;
              api.setToken(devToken);
              this.initApp();
            }
          });
        return;
      }
    }

    if (isProduction && !hasTelegramData) {
      const storedToken = localStorage.getItem('webAuthToken');
      if (storedToken) {
        this.showWebLoginForm = false;
        api.setToken(storedToken);
        api.getUserData()
          .then(() => {
            this.token = storedToken;
            this.showWebLoginForm = false;
            this.initApp();
          })
          .catch(() => {
            localStorage.removeItem('webAuthToken');
            api.setToken(null);
            this.showWebLoginForm = true;
          });
      } else {
        this.showWebLoginForm = true;
      }
      return;
    }

    if (hasTelegramData) {
      // Running inside Telegram (prod or dev)
      const tg = window.Telegram.WebApp;
      // Получаем username бота из initDataUnsafe (если есть)
      let botId = null;
      
      // Проверяем все возможные источники информации о боте
      console.log('🔍 FRONTEND DEBUG - Full Telegram.WebApp state:', {
        initDataUnsafe: window.Telegram?.WebApp?.initDataUnsafe,
        initData: window.Telegram?.WebApp?.initData ? 'present' : 'missing',
        platform: window.Telegram?.WebApp?.platform,
        version: window.Telegram?.WebApp?.version,
        colorScheme: window.Telegram?.WebApp?.colorScheme
      });
      
      if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.bot) {
        botId = window.Telegram.WebApp.initDataUnsafe.bot.username || null;
        console.log('🔍 FRONTEND DEBUG - Bot info found:', {
          bot: window.Telegram.WebApp.initDataUnsafe.bot,
          botId: botId
        });
      } else {
        console.log('🔍 FRONTEND DEBUG - No bot info in initDataUnsafe');
        
        // Попробуем извлечь информацию из initData
        if (window.Telegram?.WebApp?.initData) {
          try {
            const params = new URLSearchParams(window.Telegram.WebApp.initData);
            console.log('🔍 FRONTEND DEBUG - initData params:', Object.fromEntries(params));
          } catch (e) {
            console.log('🔍 FRONTEND DEBUG - Failed to parse initData:', e);
          }
        }
      }

      // Альтернативный способ определения бота - через URL
      if (!botId && window.location.href.includes('startapp=')) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const startapp = urlParams.get('startapp');
          if (startapp) {
            console.log('🔍 FRONTEND DEBUG - Found startapp parameter:', startapp);
            // startapp может содержать информацию о боте
          }
        } catch (e) {
          console.log('🔍 FRONTEND DEBUG - Failed to parse URL params:', e);
        }
      }
      
      // Попробуем определить бота по команде пользователя (синхронно)
      if (!botId && this.currentTeamId) {
        console.log('🔍 FRONTEND DEBUG - Trying to get bot for team:', this.currentTeamId);
        // Этот код будет выполнен позже, когда currentTeamId будет доступен
      }
      
      console.log('🔍 FRONTEND DEBUG - Calling authenticateTelegram with:', { botId, hasInitData: !!tg.initData });
      api.authenticateTelegram(tg.initData, botId)
        .then(data => {
          this.token = data.token;
          // НЕ перезаписываем userData здесь - он будет загружен в loadUserData()
          this.initApp();
        })
        .catch(error => {
          console.error("Telegram authentication failed:", error);
          if (isProduction) {
            this.accessDenied = false;
            this.showWebLoginForm = true;
          } else {
            this.accessDenied = true;
          }
        });
    } else if (!isProduction) {
      // Running in local development environment
      console.log("Running in development mode (no Telegram data).");
      const devToken = this.testUsers[this.currentTestUser].token;
      this.token = devToken;
      api.setToken(devToken);
      this.initApp();
    } else {
      // Unexpected state in production (e.g., Telegram script failed to load)
      if (isProduction) {
        this.showWebLoginForm = true;
      } else {
        this.accessDenied = true;
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('token-expired', this.handleTokenExpired);
    if (this.checkPaymentAdminRightsTimer) {
      clearTimeout(this.checkPaymentAdminRightsTimer);
    }
  },

  computed: {
    hasCustomBot() {
      if (!this.currentTeamId) return false;
      // Force reactivity by accessing teamBots map
      const teamBots = botManager.teamBots;
      const numericTeamId = parseInt(this.currentTeamId);
      return teamBots && teamBots.has(numericTeamId);
    },
    // Safe date_of_birth getter/setter
    safeDateOfBirth: {
      get() {
        console.log('safeDateOfBirth getter called, value:', this.userData.date_of_birth);
        if (this.userData.date_of_birth && dayjs.isDayjs(this.userData.date_of_birth)) {
          return this.userData.date_of_birth;
        }
        return null;
      },
      set(value) {
        console.log('safeDateOfBirth setter called, value:', value);
        if (value && dayjs.isDayjs(value)) {
          this.userData.date_of_birth = value;
        } else {
          this.userData.date_of_birth = null;
        }
      }
    },
    
    sortedPlayers() {
      if (!this.selectedEvent?.players) {
        return [];
      }
      return [...this.selectedEvent.players].sort((a, b) => {
        if (a.is_participating === 2 && b.is_participating !== 2) {
          return -1;
        }
        if (a.is_participating !== 2 && b.is_participating === 2) {
          return 1;
        }
        if (a.is_participating === 0 && b.is_participating === 1) {
          return -1;
        }
        if (a.is_participating === 1 && b.is_participating === 0) {
          return 1;
        }
        return 0;
      });
    },
    getScreenTitle() {
      switch (this.currentScreen) {
        case 'createTeam':
          return 'Создание команды';
        case 'editTeam':
          return 'Редактирование команды';
        case 'teamInfo':
          return 'Информация о команде';
        case 'createEvent':
          return 'Создание события';
        case 'editEvent':
          return 'Редактирование события';
        case 'profile':
          return 'Профиль';
        case 'playerProfile':
          return 'Профиль игрока';
        case 'event':
          return this.selectedEvent?.type === 'game' ? 'Игра' : 'Тренировка';
        case 'calendar':
          return 'Календарь';
        case 'pendingRequests':
          return 'Заявки на вступление';
        case 'searchTeams':
          return 'Поиск команды';
        case 'participationSettings':
          return 'Настройки участия';
        case 'subscription':
          return 'Абонемент';
        case 'subscriptionSettings':
          return 'Настройки абонемента';
        case 'payments':
          return 'Платежи';
        case 'bestPlayerVoting':
          return 'Голосования';
        case 'adminPaymentManagement':
          return 'Управление оплатами';
        case 'paymentTypesManagement':
          return 'Управление типами оплат';
        default:
          return '';
      }
    },
    calendarDays() {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const days = [];
      
      // Adjust for Monday as first day of week (0 = Sunday, 1 = Monday, etc.)
      let firstDayOfWeek = firstDay.getDay();
      firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to Monday-based (0-6)
      
      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDayOfWeek; i++) {
        days.push(null);
      }
      
      // Add days of the month
      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(year, month, i));
      }
      
      // Add empty cells to complete the last week if needed
      const remainingCells = 7 - (days.length % 7);
      if (remainingCells < 7) {
        for (let i = 0; i < remainingCells; i++) {
          days.push(null);
        }
      }
      
      return days;
    },
    monthName() {
      const month = this.calendarDate.toLocaleString("ru", { month: "long" });
      const year = this.calendarDate.getFullYear();
      return `${month} ${year}`;
    },
    editModeValue: {
      get() {
        return this.editMode;
      },
      set(value) {
        this.editMode = value;
      }
    },
    totalPendingRequests() {
      return this.allPendingRequests.length;
    },

    birthdayPlayers() {
      if (!this.teamPlayersData || !this.teamPlayersData.players) return [];
      
      const allPlayers = [];
      Object.values(this.teamPlayersData.players).forEach(positionPlayers => {
        allPlayers.push(...positionPlayers);
      });
      
      return allPlayers.filter(player => this.isBirthdayToday(player.date_of_birth));
    },

    currentTeamName() {
      if (!this.currentTeamId) return null;
      const team = this.userTeams.find(t => t.id === this.currentTeamId);
      return team ? team.name : null;
    },

  
    socialNetworksList() {
      if (!this.currentTeamInfo?.social_networks) return [];
      return this.currentTeamInfo.social_networks.split('\n').filter(link => link.trim());
    },

    gameEventsCount() {
      return this.events.filter(event => event.type === 'game').length;
    },

    trainingEventsCount() {
      return this.events.filter(event => event.type === 'training').length;
    },

    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Generate years from 2020 to current year + 1
      for (let year = 2020; year <= currentYear + 1; year++) {
        years.push(year);
      }
      return years;
    },

    availablePlayers() {
      if (!this.selectedEvent || !this.selectedEvent.players) {
        return [];
      }
      
      // Get all players who are participating
      const participatingPlayers = this.selectedEvent.players.filter(player => player.is_participating === 2);
      
      // Get all assigned player IDs from the fields
      const assignedPlayerIds = new Set();
      this.fields.forEach(field => {
        [...field.topPlaces, ...field.bottomPlaces].forEach(place => {
          if (place.player && place.player.id) {
            assignedPlayerIds.add(place.player.id);
          }
        });
      });
      
      // Filter out already assigned players
      const availablePlayers = participatingPlayers.filter(player => !assignedPlayerIds.has(player.id));
      
      return availablePlayers;
    },
    
    dateRange: {
      get() {
        if (!this.repeatingEventData || !this.repeatingEventData.start_date || !this.repeatingEventData.end_date) {
          return [dayjs(), dayjs().add(1, 'month')];
        }
        return [this.repeatingEventData.start_date, this.repeatingEventData.end_date];
      },
      set(value) {
        if (Array.isArray(value) && value.length === 2) {
          if (!this.repeatingEventData) {
            this.repeatingEventData = {
              start_date: dayjs(),
              end_date: dayjs().add(1, 'month'),
              days_of_week: []
            };
          }
          this.repeatingEventData.start_date = value[0];
          this.repeatingEventData.end_date = value[1];
        }
      }
    },

    // Проверка, является ли пользователь администратором команды
    isTeamAdmin() {
      if (!this.currentTeamId) {
        return false;
      }
      if (!this.userTeams || this.userTeams.length === 0) {
        return false;
      }
      const team = this.userTeams.find(t => t.id === this.currentTeamId);
      return team && team.role === 'admin';
    },

    // Проверка доступа к управлению оплатами (только для администраторов команды и администраторов платежей)
    canManagePayments() {
      if (!this.currentTeamId) {
        return false;
      }
      if (!this.userTeams || this.userTeams.length === 0) {
        return false;
      }
      
      // Доступ для администраторов команды или администраторов платежей
      const hasAccess = this.isTeamAdmin || this.isPaymentAdmin;
      
      return hasAccess;
    },

    playersWithoutWebLoginCount() {
      if (!this.teamPlayersData?.players) return 0;
      const all = [];
      Object.values(this.teamPlayersData.players).forEach((positionPlayers) => {
        all.push(...positionPlayers);
      });
      return all.filter((p) => !p.has_web_login).length;
    },

    playersWithWebLoginCount() {
      if (!this.teamPlayersData?.players) return 0;
      const all = [];
      Object.values(this.teamPlayersData.players).forEach((positionPlayers) => {
        all.push(...positionPlayers);
      });
      return all.filter((p) => p.has_web_login).length;
    }
  },

  watch: {
    'userData.date_of_birth': {
      handler(newValue) {
        if (newValue && !dayjs.isDayjs(newValue)) {
          try {
            this.userData.date_of_birth = dayjs(newValue);
          } catch (error) {
            console.warn('Invalid date format, setting to null:', newValue);
            this.userData.date_of_birth = null;
          }
        }
      },
      immediate: true
    },
    currentTeamId: {
      async handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          await this.checkPaymentAdminRights();
        }
      },
      immediate: false
    },
    userTeams: {
      handler() {},
      immediate: true,
      deep: true
    }
  },

  props: {
    item: Object,
  },

  methods: {
    onWebLoginSuccess(data) {
      this.token = data.token;
      this.showWebLoginForm = false;
      this.initApp();
    },

    popWebAuthTokenFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('token');
        if (!t) return null;
        params.delete('token');
        const rest = params.toString();
        const path = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
        window.history.replaceState({}, '', path);
        return t;
      } catch (e) {
        console.warn('popWebAuthTokenFromUrl', e);
        return null;
      }
    },
    // Navigation methods
    async showScreen(screen) {
      // Проверяем права доступа для экранов создания и редактирования событий
      if ((screen === 'createEvent' || screen === 'editEvent') && !this.isAdmin) {
        message.error('Только администраторы могут создавать и редактировать события');
        return;
      }
      this.previousScreen = this.currentScreen;
      this.currentScreen = screen;
      
      // Load all events when opening calendar
      if (screen === 'calendar' && this.currentTeamId) {
        this.events = await this.loadAllEvents(this.currentTeamId);
      }
    },

    showScreenEvetPlayers(event) {
      this.selectedEvent = event;  //!!! установка текущего события
      this.currentScreen = 'event';
      console.log('About to call loadEventPlayers...');
      this.loadEventPlayers(event.id);
      
      // Загружаем цвета при открытии экрана
       this.loadColors();
    },

    async handleBackNavigation() {
      if (this.previousScreen) {
        this.currentScreen = this.previousScreen;
        this.previousScreen = null;
        
        // Reload limited events when returning to main screen from calendar
        if (this.currentScreen === 'main' && this.currentTeamId) {
          await this.loadEvents(this.currentTeamId);
          // Reload subscription status to update warning message if payment was approved
          await this.loadSubscriptionStatus();
        }
      } else {
        this.currentScreen = 'main';
      }
    },

    showDrawer() {
      this.openDrawer = true;
    },

    onClose() {
      this.openDrawer = false;
    },

    async initApp() {
      try {
        await this.loadUserData();
        await this.loadUserTeams();

        const manifestTeamId = preferredManifestTeamId((this.userTeams || []).map((t) => t.id));
        if (manifestTeamId != null) {
          applyManifestLinkHref(manifestTeamId);
        }

        // Инициализируем менеджер ботов
        await botManager.initialize();

        // Восстанавливаем иконки команды из localStorage
        const savedTeamId = await dynamicIconsManager.restoreTeamIcons();

        // Автоматически выбираем первую команду и загружаем её события
        if (this.userTeams && this.userTeams.length > 0) {
          const firstTeam = this.userTeams[0];
          this.currentTeamId = firstTeam.id;
          // Загружаем события для первой команды
          await this.loadEvents(firstTeam.id);

          // Загружаем составы команды
          await this.loadTeamRosters(firstTeam.id);

          // Загружаем состав команды для отображения дней рождения
          await this.loadTeamPlayers();

          // Загружаем статус подписки для отображения предупреждений о неоплате
          await this.loadSubscriptionStatus();

          // Проверяем права администратора платежей
          await this.checkPaymentAdminRights();
          // Загружаем информацию о команде (включая логотип)
          await this.loadCurrentTeamInfo();

          // Устанавливаем активный ключ меню для первой команды
          this.selectedMenuKeys = [`team-${firstTeam.id}`];

          this.isAdmin = this.getIsAdmin();
        }

        await this.loadPendingTeams();
        if (this.isAdmin) {
          await this.loadAllPendingRequests();
        }
        this.initWebSocket();
      } catch (e) {
        console.error('initApp failed:', e);
        if (this.$message) {
          this.$message.error('Не удалось загрузить приложение. Попробуйте войти снова.');
        }
        if (process.env.NODE_ENV === 'production') {
          localStorage.removeItem('webAuthToken');
          api.setToken(null);
          this.showWebLoginForm = true;
        }
      }
    },

    initWebSocket() {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        console.log('WebSocket connection established');
      };

      this.ws.onmessage = (event) => {
        const messageData = JSON.parse(event.data);
        console.log('WebSocket message received:', messageData);
        this.handleWebSocketMessage(messageData);
      };

      this.ws.onclose = () => {
        console.log('WebSocket connection closed. Reconnecting...');
        setTimeout(() => {
          this.initWebSocket();
        }, 3000);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.ws.close();
      };
    },

    handleWebSocketMessage(message) {
      const { type, payload } = message;

      switch (type) {
        case 'participation_update':
          this.handleParticipationUpdate(message);
          break;
        case 'placement_updated':
          this.handlePlacementUpdate(message);
          break;
        case 'event_created':
          this.handleEventCreated(message);
          break;
        case 'events_updated':
          this.handleEventsUpdate(payload);
          break;
        case 'stats_updated':
          this.handleStatsUpdate(payload);
          break;
        case 'pending_requests_updated':
          this.handlePendingRequestsUpdate(payload);
          break;
        case 'field_placement_updated':
          this.handlePlacementUpdate(payload);
          break;
        default:
          console.warn('Unknown WebSocket message type:', type);
      }
    },

    handleParticipationUpdate(message) {
      console.log('Handling participation update:', message);
      
      const { eventId, playerId, player, event, stats } = message;
      
      // Check if this is our own update to avoid double counting
      const isOwnUpdate = this.lastOwnParticipationUpdate && 
                         this.lastOwnParticipationUpdate.eventId === eventId &&
                         this.lastOwnParticipationUpdate.playerId === playerId &&
                         (Date.now() - this.lastOwnParticipationUpdate.timestamp) < 5000; // Within 5 seconds
      
      // Clear the flag if this is our own update
      if (isOwnUpdate) {
        this.lastOwnParticipationUpdate = null;
        console.log('Processing own participation update from WebSocket (without stats update)');
      }
      
      // Update player in selectedEvent.players if we're viewing this event
      if (this.currentScreen === 'event' && this.selectedEvent && this.selectedEvent.id === eventId) {
        const playerInEvent = this.selectedEvent.players.find(p => p.id === playerId);
        if (playerInEvent) {
          playerInEvent.is_participating = player.isParticipating;
          playerInEvent.comment = player.comment;
          
          // If player status is "not participating" (1) or "unknown" (0), clear their placement
          if (player.isParticipating === 1 || player.isParticipating === 0) {
            playerInEvent.place = null;
            playerInEvent.color = null;
            
            // Clear player from all fields
            this.fields.forEach(field => {
              [...field.topPlaces, ...field.bottomPlaces].forEach(placeItem => {
                if (placeItem.player && placeItem.player.id === playerId) {
                  placeItem.player = null;
                  placeItem.color = null;
                }
              });
            });
          }
          
          // Update stats for the selected event (but not for own updates to avoid double counting)
          if (stats && !isOwnUpdate) {
            this.selectedEvent.stats = {
              participating: stats.participating,
              not_participating: stats.notParticipating,
              unknown: stats.unknown
            };
          }
          
          // Update user participation status if this is the current user
          if (playerInEvent.user_id === this.userData.id) {
            this.selectedEvent.user_participation_status = player.isParticipating;
          }
          
          this.$forceUpdate();
          
          // Don't show notification for own updates
          if (!isOwnUpdate) {
            message.info(`Статус ${player.firstName} ${player.lastName} обновлен`);
          }
        }
      }
      
      // Update player in main events list
      const eventIndex = this.events.findIndex(e => e.id === eventId);
      if (eventIndex !== -1) {
        const eventInList = this.events[eventIndex];
        
        // Update stats in main events list (but not for own updates to avoid double counting)
        if (stats && !isOwnUpdate) {
          eventInList.stats = {
            participating: stats.participating,
            not_participating: stats.notParticipating,
            unknown: stats.unknown
          };
        }
        
        // Update player in event.players if it exists
        if (eventInList.players) {
          const playerInEvent = eventInList.players.find(p => p.id === playerId);
          if (playerInEvent) {
            playerInEvent.is_participating = player.isParticipating;
            playerInEvent.comment = player.comment;
          }
        }
        
        // CRITICAL: Update user_participation_status in main events list
        // This is needed for the badge on the main screen
        if (this.userData) {
          // Check if this player belongs to current user
          const isCurrentUser = eventInList.players && 
                               eventInList.players.find(p => p.id === playerId && p.user_id === this.userData.id);
          
          if (isCurrentUser) {
            eventInList.user_participation_status = player.isParticipating;
            console.log('Updated user_participation_status in main list:', eventInList.user_participation_status);
          } else {
            // If we don't have players array or can't find the player,
            // we need to reload the events to get fresh data with updated user_participation_status
            console.log('Reloading events to update user_participation_status');
            this.loadEvents(this.currentTeamId);
          }
        }
      }
    },

    handlePlacementUpdate(message) {
      const { eventId, playerId, place, color, player } = message;
      
      if (this.currentScreen === 'event' && this.selectedEvent && this.selectedEvent.id === eventId) {
        // Clear all placements for this player
        this.fields.forEach(field => {
          [...field.topPlaces, ...field.bottomPlaces].forEach(placeItem => {
            if (placeItem.player && placeItem.player.id === playerId) {
              placeItem.player = null;
              placeItem.color = null;
            }
          });
        });

        // Set new placement if provided
        if (place) {
          for (const field of this.fields) {
            const placeItem = [...field.topPlaces, ...field.bottomPlaces].find(p => p.id === place);
            if (placeItem) {
              placeItem.player = {
                id: player.id,
                number: player.number,
                position: player.position,
                photo_url: player.photoUrl,
                first_name: player.firstName,
                last_name: player.lastName
              };
              placeItem.color = color;
              break;
            }
          }
        }

        // Update player in the list
        const playerInList = this.selectedEvent.players.find(p => p.id === playerId);
        if (playerInList) {
          playerInList.place = place;
          playerInList.color = color;
        }

        this.$forceUpdate();
        message.info(`Размещение ${player.firstName} ${player.lastName} обновлено`);
      }
    },

    handleColorsUpdate(payload) {
      if (this.currentScreen === 'event' && this.selectedEvent && this.selectedEvent.id === payload.eventId) {
        // Reload event details to get updated colors
        this.loadEventPlayers(payload.eventId);
        
        if (payload.fieldId) {
          message.info(`Цвета игроков на поле ${payload.fieldId} обновлены`);
        } else {
          message.info('Цвета всех игроков обновлены');
        }
      }
    },

    handleStatsUpdate(payload) {
      if (this.events.length > 0) {
        const eventIndex = this.events.findIndex(e => e.id === payload.eventId);
        if (eventIndex !== -1) {
          this.events[eventIndex].stats = payload.stats;
        }
      }
    },

    handleEventCreated(message) {
      console.log('Handling event created:', message);
      
      const { event } = message;
      
      if (event.teamId === this.currentTeamId) {
        message.info(`Создано новое событие: ${event.type === 'game' ? 'Игра' : 'Тренировка'}`);
        
        // Reload events list
        this.loadEvents(this.currentTeamId);
        
        // If we're on calendar screen, reload all events
        if (this.currentScreen === 'calendar') {
          this.loadAllEvents(this.currentTeamId).then(events => {
            this.events = events;
          });
        }
      }
    },

    handleEventsUpdate(payload) {
      console.log('Handling events update:', payload);
      
      if (this.currentTeamId === payload.teamId) {
        message.info('Список событий обновлен!');
        
        // Reload events based on current screen
        if (this.currentScreen === 'calendar') {
          this.loadAllEvents(this.currentTeamId).then(events => {
            this.events = events;
          });
        } else {
          this.loadEvents(this.currentTeamId);
        }
        
        // If we're currently viewing an event, reload it to get the latest data
        if (this.currentScreen === 'event' && this.selectedEvent) {
          this.loadEventPlayers(this.selectedEvent.id);
        }
      }
    },

    handlePendingRequestsUpdate(payload) {
      if (this.isAdmin) {
        // Refresh pending requests count for admins
        this.loadAllPendingRequests();
        if (payload.teamId === this.currentTeamId) {
          this.loadPendingRequests();
        }
        message.info('Новые заявки на вступление в команду!');
      }
    },

    async loadUserTeams() {
      console.log('Loading user teams...');
      try {
        this.userTeams = await api.getUserTeams();
        console.log('User teams loaded:', this.userTeams);
        console.log('User teams length:', this.userTeams.length);
        if (this.userTeams.length > 0) {
          console.log('First team role:', this.userTeams[0].role);
        }
        
        // Проверяем права администратора платежей для текущей команды
        if (this.currentTeamId) {
          await this.checkPaymentAdminRights();
        }
      } catch (error) {
        console.error('Error loading user teams:', error);
        this.$message.error('Ошибка при загрузке команд');
      }
    },

    async loadPendingTeams() {
      try {
        this.pendingTeams = await api.getPendingTeams();
      } catch (error) {
        console.error('Error loading pending teams:', error);
      }
    },

    async loadAllPendingRequests() {
      if (!this.isAdmin) return;
      
      try {
        const allRequests = [];
        
        // Load pending requests for each team where user is admin
        for (const team of this.userTeams) {
          try {
            const response = await fetch(`${API_URL}/teams/${team.id}/pending-requests`, {
              headers: { Authorization: `Bearer ${this.token}` },
            });
            
            if (response.ok) {
              const requests = await response.json();
              allRequests.push(...requests);
            }
          } catch (error) {
            console.error(`Error loading pending requests for team ${team.id}:`, error);
          }
        }
        
        this.allPendingRequests = allRequests;
      } catch (error) {
        console.error('Error loading all pending requests:', error);
        this.allPendingRequests = [];
      }
    },

    async approveRequest(requestId) {
      try {
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/requests/${requestId}/accept`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        const data = await response.json();
        
        if (response.ok) {
          this.$message.success(data.message);
          await this.loadPendingRequests();
          if (this.isAdmin) {
            await this.loadAllPendingRequests();
          }
          // Перезагружаем события, чтобы новый игрок появился в списках
          if (this.currentTeamId) {
            await this.loadEvents(this.currentTeamId);
          }
        } else {
          this.$message.error(data.message || 'Ошибка при одобрении заявки');
        }
      } catch (error) {
        console.error('Error approving request:', error);
        this.$message.error('Ошибка при одобрении заявки');
      }
    },

    async rejectRequest(requestId) {
      try {
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/requests/${requestId}/reject`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        const data = await response.json();
        
        if (response.ok) {
          this.$message.success(data.message);
          await this.loadPendingRequests();
          if (this.isAdmin) {
            await this.loadAllPendingRequests();
          }
        } else {
          this.$message.error(data.message || 'Ошибка при отклонении заявки');
        }
      } catch (error) {
        console.error('Error rejecting request:', error);
        this.$message.error('Ошибка при отклонении заявки');
      }
    },

    showPendingRequests() {
      this.showScreen('pendingRequests');
      this.loadPendingRequests();
    },

    switchTestUser(userKey) {
      if (this.isProduction) return;
      
      const selectedUser = this.testUsers[userKey];
      if (selectedUser) {
        this.token = selectedUser.token;
        this.currentTestUser = userKey;
        api.setToken(selectedUser.token);
        
        // Reload data for the new user
        this.loadUserData();
        this.loadUserTeams();
        this.loadPendingTeams();
        
        // Check if user is admin and load pending requests if needed
        this.isAdmin = this.getIsAdmin();
        if (this.isAdmin) {
          this.loadAllPendingRequests();
        } else {
          this.allPendingRequests = [];
        }
        
        // Show success message
        this.$message.success(`Переключились на ${selectedUser.name}`);
        
        // If we're on a team-specific screen, reload team data
        if (this.currentTeamId) {
          this.loadEvents(this.currentTeamId);
        }
      }
    },

    getCurrentUserDisplayName() {
      if (this.isProduction) {
        return `${this.userData?.first_name} ${this.userData?.last_name}`.trim() || 'User';
      }
      return this.testUsers[this.currentTestUser]?.name || 'Test User';
    },

    async handleTokenExpired() {
      console.log('Token expired, attempting to re-authenticate...');
      
      // Show loading message
      this.$message.loading('Обновление сессии...', 0);
      
      try {
        // Try to re-authenticate with Telegram
        if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData) {
          let botId = null;
                  if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.bot) {
          botId = window.Telegram.WebApp.initDataUnsafe.bot.username || null;
          console.log('🔍 FRONTEND DEBUG - Re-auth bot info:', {
            bot: window.Telegram.WebApp.initDataUnsafe.bot,
            botId: botId
          });
        } else {
          console.log('🔍 FRONTEND DEBUG - Re-auth: No bot info in initDataUnsafe');
        }
        console.log('🔍 FRONTEND DEBUG - Re-auth calling authenticateTelegram with:', { botId, hasInitData: !!window.Telegram.WebApp.initData });
        const data = await api.authenticateTelegram(window.Telegram.WebApp.initData, botId);
          this.token = data.token;
          // НЕ перезаписываем userData здесь - он будет загружен в loadUserData()
          
          // Reload current data
          await this.loadUserData(); // Загружаем актуальные данные пользователя
          if (this.currentTeamId) {
            await this.loadUserTeams();
            await this.loadEvents(this.currentTeamId);
          }
          
          this.$message.destroy();
          this.$message.success('Сессия обновлена');
        } else {
          // In development mode, just reload the page
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to re-authenticate:', error);
        this.$message.destroy();
        this.$message.error('Ошибка обновления сессии. Пожалуйста, обновите страницу.');
        
        // Show refresh button after 3 seconds
        setTimeout(() => {
          this.$message.error('Нажмите для обновления страницы', 0, () => {
            window.location.reload();
          });
        }, 3000);
      }
    },

    async loadUserData() {
      try {
        const userData = await api.getUserData();
        
        // Convert date_of_birth string to dayjs object if it exists
        if (userData.date_of_birth) {
          try {
            userData.date_of_birth = dayjs(userData.date_of_birth);
          } catch (error) {
            console.warn('Invalid date format:', userData.date_of_birth);
            userData.date_of_birth = null;
          }
        }
        
        this.userData = userData;
      } catch (error) {
        console.error('Error loading user data:', error);
        this.$message.error('Ошибка при загрузке данных пользователя');
      }
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('Размер файла не должен превышать 10MB');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('Пожалуйста, выберите изображение в формате JPEG, PNG, GIF или HEIC');
        return;
      }
      
      try {
        this.uploadingPhoto = true;
        const formData = new FormData();
        formData.append('photo', file);
        
        const response = await api.uploadPhoto(formData);
        if (response.photo_url) {
          this.userData.photo_url = response.photo_url;
          this.$message.success('Фото профиля обновлено');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        this.$message.error('Ошибка при загрузке фото');
      } finally {
        this.uploadingPhoto = false;
        // Reset file input
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      }
    },

    async updateProfile() {
      try {
    this.loading = true;

    // Создаем новый объект, объединяя данные из userData и editingProfile.
    const combinedProfile = { ...this.userData, ...this.editingProfile };

    // 1. Обновляем общую информацию пользователя
    const profileData = {
      first_name: combinedProfile.first_name,
      last_name: combinedProfile.last_name,
      date_of_birth: combinedProfile.date_of_birth,
      height: combinedProfile.height ? parseInt(combinedProfile.height, 10) : null,
      weight: combinedProfile.weight ? parseInt(combinedProfile.weight, 10) : null,
      stick_hand: combinedProfile.stick_hand,
      skill_level: combinedProfile.skill_level,
      emergency_phone: combinedProfile.emergency_phone,
    };
    const updatedUser = await api.updateProfile(profileData);

    // 2. Обновляем данные игрока, находя player_id в userTeams
    if (this.currentTeamId && this.userTeams) {
      const team = this.userTeams.find(t => t.id === this.currentTeamId);
      if (team && team.player_id) {
        const playerData = {
            number: combinedProfile.number ? parseInt(combinedProfile.number, 10) : null,
            position: combinedProfile.position,
        };
        await api.updatePlayerProfile(this.currentTeamId, team.player_id, playerData);
      }
    }
    
    // 3. Обновляем локальное состояние
    this.userData = { ...this.userData, ...updatedUser };
    this.editingProfile = { ...this.userData, ...combinedProfile }; 
    
    if (this.userTeams && this.currentTeamId) {
      const teamInList = this.userTeams.find(t => t.id === this.currentTeamId);
      if (teamInList) {
        teamInList.number = combinedProfile.number;
        teamInList.position = combinedProfile.position;
      }
    }

    this.$message.success("Профиль успешно обновлен");
    
  } catch (error) {
    console.error("Failed to update profile:", error);
    this.$message.error(error.response?.data?.message || "Ошибка при обновлении профиля");
  } finally {
    this.loading = false;
  }
    },

    async generateWebCredentialsForTeam() {
      if (!this.currentTeamId || this.playersWithoutWebLoginCount === 0) return;
      this.generatingWebCredentials = true;
      try {
        const result = await api.generateWebCredentials(this.currentTeamId, {});
        if (result.created > 0) {
          const rows = (result.details || []).map((d) => ({
            userId: d.userId,
            login: d.login,
            password: d.password,
            displayName: d.displayName || ''
          }));
          this.issuedCredentialsRows = rows;
          this.issuedCredentialsModalVisible = true;
          this.$message.success(
            `Создано учётных записей: ${result.created}. Откройте окно ниже и передайте данные игрокам.`
          );
        }
        if (result.skipped > 0 && result.created === 0) {
          this.$message.info(result.message || 'Все игроки уже имеют логин');
        }
        if (result.errors?.length) {
          this.$message.warning(`Ошибки у ${result.errors.length} игроков`);
        }
        await this.loadTeamPlayers();
      } catch (error) {
        this.$message.error(error.message || 'Ошибка при создании логинов');
      } finally {
        this.generatingWebCredentials = false;
      }
    },

    async resendWebCredentialsToTelegram() {
      if (!this.currentTeamId || this.playersWithWebLoginCount === 0) return;
      this.resendingWebCredentials = true;
      try {
        const result = await api.resendWebCredentialsTelegram(this.currentTeamId, {});
        if (result.sent > 0) {
          this.$message.success(
            `Напоминаний в Telegram: ${result.sent}. Пароли не менялись; в сообщении — логин и ссылка для входа.`
          );
        }
        if (result.sent === 0 && result.skipped > 0 && (!result.errors || result.errors.length === 0)) {
          this.$message.info(result.message || 'Отправить некому: нет игроков с веб-логином и Telegram.');
        } else if (result.skipped > 0 && result.sent > 0) {
          this.$message.warning(`Отправлено ${result.sent}, пропущено ${result.skipped} (нет Telegram и т.п.).`);
        }
        if (result.errors?.length) {
          this.$message.warning(`Ошибки у ${result.errors.length} игроков`);
        }
      } catch (error) {
        this.$message.error(error.message || 'Ошибка при отправке напоминаний');
      } finally {
        this.resendingWebCredentials = false;
      }
    },

    async resetWebPasswordForPlayer(player) {
      if (!this.currentTeamId || !player?.user_id) return;
      this.resettingWebPasswordUserId = player.user_id;
      try {
        const result = await api.resetWebCredentials(this.currentTeamId, player.user_id);
        const row = (result.details && result.details[0]) || {
          userId: player.user_id,
          login: result.login,
          password: result.password,
          displayName: [player.last_name, player.first_name].filter(Boolean).join(' ')
        };
        this.issuedCredentialsRows = [row];
        this.issuedCredentialsModalVisible = true;
        this.$message.success(
          result.reset
            ? `Новый пароль для ${row.displayName || 'игрока'}. Передайте данные игроку.`
            : `Создан логин для ${row.displayName || 'игрока'}.`
        );
        await this.loadTeamPlayers();
      } catch (error) {
        this.$message.error(error.message || 'Не удалось выдать новый пароль');
      } finally {
        this.resettingWebPasswordUserId = null;
      }
    },

    copyIssuedCredential(text) {
      if (text == null || text === '') return;
      const s = String(text);
      navigator.clipboard.writeText(s).then(
        () => this.$message.success('Скопировано'),
        () => this.$message.error('Не удалось скопировать')
      );
    },

    async saveWebCredentials() {
      if (!this.webCredentialsForm.login?.trim() || !this.webCredentialsForm.password) {
        this.$message.warning('Введите логин и пароль');
        return;
      }
      this.savingWebCredentials = true;
      try {
        await api.setWebCredentials(this.webCredentialsForm.login.trim(), this.webCredentialsForm.password);
        this.userData = { ...this.userData, login: this.webCredentialsForm.login.trim().toLowerCase() };
        this.webCredentialsForm = { login: '', password: '' };
        this.$message.success('Данные для входа сохранены');
      } catch (error) {
        this.$message.error(error.message || 'Ошибка при сохранении');
      } finally {
        this.savingWebCredentials = false;
      }
    },

    async loadEvents(teamId) {
      try {
        this.currentTeamId = teamId;
      
        const response = await fetch(`${API_URL}/events/${this.currentTeamId}?limit=10&future_only=true`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Trigger token refresh
            window.dispatchEvent(new CustomEvent('token-expired'));
            return;
          }
          throw new Error(`Failed to load events: ${response.statusText}`);
        }
        
        const events = await response.json();
        console.log('Loaded events:', events);

        // Получаем статистику для каждого события (оптимизированно)
        if (events.length > 0) {
          try {
            // Use bulk stats endpoint for better performance
            const eventIds = events.map(event => event.id).join(',');
            const statsResponse = await fetch(`${API_URL}/events/team/${teamId}/stats?eventIds=${eventIds}`, {
              headers: { Authorization: `Bearer ${this.token}` },
            });
            
            if (statsResponse.ok) {
              const statsMap = await statsResponse.json();
              // Apply stats to events
              events.forEach(event => {
                event.stats = statsMap[event.id] || { participating: 0, not_participating: 0, unknown: 0 };
              });
            } else {
              console.warn('Failed to load bulk stats, using individual requests');
              // Fallback to individual requests
              for (const event of events) {
                try {
                  const statsResponse = await fetch(`${API_URL}/events/${event.id}/stats`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                  });
                  
                  if (statsResponse.ok) {
                    const stats = await statsResponse.json();
                    event.stats = stats;
                  } else {
                    event.stats = { participating: 0, not_participating: 0, unknown: 0 };
                  }
                } catch (statsError) {
                  console.warn(`Error loading stats for event ${event.id}:`, statsError);
                  event.stats = { participating: 0, not_participating: 0, unknown: 0 };
                }
              }
            }
          } catch (bulkStatsError) {
            console.warn('Error loading bulk stats:', bulkStatsError);
            // Fallback to individual requests
            for (const event of events) {
              try {
                const statsResponse = await fetch(`${API_URL}/events/${event.id}/stats`, {
                  headers: { Authorization: `Bearer ${this.token}` },
                });
                
                if (statsResponse.ok) {
                  const stats = await statsResponse.json();
                  event.stats = stats;
                } else {
                  event.stats = { participating: 0, not_participating: 0, unknown: 0 };
                }
              } catch (statsError) {
                console.warn(`Error loading stats for event ${event.id}:`, statsError);
                event.stats = { participating: 0, not_participating: 0, unknown: 0 };
              }
            }
          }
        }

        this.events = events;
        console.log('Events set to:', this.events);
        console.log('Current team name after loading events:', this.currentTeamName);

        // Load subscription status
        await this.loadSubscriptionStatus();
      } catch (error) {
        console.error('Error loading events:', error);
        this.$message.error('Ошибка загрузки событий');
      }
    },

    async loadTeamRosters(teamId) {
      try {
        const response = await fetch(`${API_URL}/teams/${teamId}/rosters`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        if (response.ok) {
          this.teamRosters = await response.json();
        } else {
          console.error('Failed to load team rosters');
          this.teamRosters = [];
        }
      } catch (error) {
        console.error('Error loading team rosters:', error);
        this.teamRosters = [];
      }
    },

    async loadAllEvents(teamId) {
      try {
        // Use optimized bulk stats endpoint
        return await api.getTeamEventsWithBulkStats(teamId);
      } catch (error) {
        console.error('Error loading all events:', error);
        // Fallback to individual requests if bulk fails
        const response = await fetch(`${API_URL}/events/${teamId}`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        const events = await response.json();

        // Получаем статистику для каждого события
        for (const event of events) {
          try {
            const statsResponse = await fetch(`${API_URL}/events/${event.id}/stats`, {
              headers: { Authorization: `Bearer ${this.token}` },
            });
            if (statsResponse.ok) {
              const stats = await statsResponse.json();
              event.stats = stats;
            } else {
              event.stats = { participating: 0, not_participating: 0, unknown: 0 };
            }
          } catch (statsError) {
            console.warn(`Error loading stats for event ${event.id}:`, statsError);
            event.stats = { participating: 0, not_participating: 0, unknown: 0 };
          }
        }

        return events;
      }
    },

    async loadEventPlayers(eventId) {
      try {
        const response = await fetch(`${API_URL}/events/${eventId}/players`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            // Trigger token refresh
            window.dispatchEvent(new CustomEvent('token-expired'));
            return;
          }
          console.error('Failed to load event details:', response.status, response.statusText);
          this.$message.error('Ошибка загрузки участников события');
          return;
        }
        
        const players = await response.json();
        console.log('Loaded players:', players);
        
        // Debug: Check if the updated player is in the list
        const updatedPlayer = players.find(p => p.user_id === 21);
        if (updatedPlayer) {
          console.log('Found player with user_id 21:', updatedPlayer);
          console.log('Player ID (from players table):', updatedPlayer.id);
          console.log('User ID:', updatedPlayer.user_id);
          console.log('Current number:', updatedPlayer.number);
        }

        this.selectedEvent.players = players;
        console.log('selectedEvent.players after assignment:', this.selectedEvent.players);

        // Связываем игроков с местами на поле
        this.fields.forEach((field) => {
          field.topPlaces.forEach((place) => {
            const player = players.find((p) => p.place === place.id);
            if (player) {
              place.player = player;
              place.color = player.color; // Сохраняем цвет рамки
            } else {
              place.player = null;
              place.color = null;
            }
          });

          field.bottomPlaces.forEach((place) => {
            const player = players.find((p) => p.place === place.id);
            if (player) {
              place.player = player;
              place.color = player.color; // Сохраняем цвет рамки
            } else {
              place.player = null;
              place.color = null;
            }
          });
        });
      } catch (error) {
        console.error('Error in loadEventPlayers:', error);
        this.$message.error('Ошибка загрузки участников события');
      }
    },

    async updateParticipation(eventId, playerId, isParticipating, comment = '') {
      const response = await fetch(`${API_URL}/events/${eventId}/players/${playerId}/participation`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          is_participating: isParticipating,
          comment
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Server error, could not parse response' }));
        throw new Error(errorData.message || 'Failed to update participation');
      }

      return await response.json();
    },

    async updatePlacement(eventId, playerId, place = null, color = null) {
      await fetch(`${API_URL}/events/${eventId}/players/${playerId}/placement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          place,
          color,
        }),
      });
      // Перезагрузка данных после обновления
      await this.loadEventPlayers(eventId);
    },

    async searchTeams() {
      if (this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      const response = await fetch(`${API_URL}/teams/search?query=${this.searchQuery}`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      this.searchResults = await response.json();
    },

    async joinTeam(teamId) {
      try {
      const response = await fetch(`${API_URL}/teams/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ teamId }),
      });

        const data = await response.json();

      if (response.ok) {
          this.$message.success(data.message || 'Заявка на вступление в команду отправлена и ожидает одобрения администратора');
        await this.loadUserTeams();
      } else {
          this.$message.error(data.message || 'Ошибка при присоединении к команде');
        }
      } catch (error) {
        console.error('Error joining team:', error);
        this.$message.error('Ошибка при присоединении к команде');
      }
    },

    async createTeam() {
      const { name, logo_url, city, description, social_networks } = this.newTeam;

      if (!name) {
        this.$message.error('Пожалуйста, введите название команды');
        return;
      }

      try {
        this.creatingTeam = true;
      const response = await fetch(`${API_URL}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
          body: JSON.stringify({ name, logo_url, city, description, social_networks }),
      });

      if (response.ok) {
          this.$message.success('Команда создана успешно!');
        this.currentScreen = 'main';
        await this.loadUserTeams();
          // Reset form
          this.newTeam = {
            name: '',
            logo_url: '',
            city: '',
            description: '',
            social_networks: '',
          };
      } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при создании команды');
        }
      } catch (error) {
        console.error('Error creating team:', error);
        this.$message.error('Ошибка при создании команды');
      } finally {
        this.creatingTeam = false;
      }
    },

    async runAutomatedTest() {
      this.$message.info('Running automated test... Check console for results');
      console.log('🧪 Starting automated test...');
      
      try {
        // Simulate the test workflow
        console.log('1. Current user:', this.getCurrentUserDisplayName());
        console.log('2. Available teams:', this.userTeams.length);
        console.log('3. Pending teams:', this.pendingTeams.length);
        console.log('4. Pending requests:', this.pendingRequests.length);
        
        this.$message.success('Test completed! Check console for details.');
      } catch (error) {
        console.error('Test failed:', error);
        this.$message.error('Test failed. Check console for details.');
      }
    },

    async loadPendingRequests() {
      if (!this.currentTeamId) return;
      
      try {
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/pending-requests`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (response.ok) {
          this.pendingRequests = await response.json();
        } else if (response.status === 403) {
          // User is not admin, clear requests
          this.pendingRequests = [];
        }
      } catch (error) {
        console.error('Error loading pending requests:', error);
        this.pendingRequests = [];
      }
    },

    startEditNumber() {
      this.isEditingNumber = true;
      this.editingPlayerNumber = this.selectedPlayerProfile.number;
    },

    savePlayerNumber() {
      // Validate the number
      if (!this.editingPlayerNumber || this.editingPlayerNumber < 1 || this.editingPlayerNumber > 99) {
        this.$message.error('Номер должен быть от 1 до 99');
        return;
      }

      this.savingNumber = true;
      
      // Store the original number for potential rollback
      const originalNumber = this.selectedPlayerProfile.number;
      
      // Update the local state first
      this.selectedPlayerProfile.number = this.editingPlayerNumber;
      this.isEditingNumber = false;
      
      // Debug: Log the player ID being used
      console.log('Updating player number:', {
        playerId: this.selectedPlayerProfile.id, // This should be the player_id from players table
        user_id: this.selectedPlayerProfile.user_id,
        number: this.editingPlayerNumber,
        player: this.selectedPlayerProfile
      });
      
      // Validate that we have the correct player_id
      if (!this.selectedPlayerProfile.id) {
        console.error('ERROR: No player_id found in selectedPlayerProfile');
        this.$message.error('Ошибка: не найден ID игрока');
        return;
      }
      
      // Update the player's number in the database
      this.updatePlayerNumber(this.selectedPlayerProfile.id, this.editingPlayerNumber)
        .then(() => {
          this.$message.success('Номер игрока обновлен');
          // Refresh event details to show updated number in lists
          if (this.selectedEvent && this.selectedEvent.id) {
            this.loadEventPlayers(this.selectedEvent.id);
          }
        })
        .catch((error) => {
          console.error('Error updating player number:', error);
          this.$message.error('Ошибка при обновлении номера игрока');
          // Revert the local change on error
          this.selectedPlayerProfile.number = originalNumber;
          this.isEditingNumber = true; // Keep editing mode open
        })
        .finally(() => {
          this.savingNumber = false;
        });
    },

    cancelEditNumber() {
      this.isEditingNumber = false;
      this.editingPlayerNumber = null;
    },

    async updatePlayerNumber(playerId, number) {
      try {
        console.log('Sending request to update player number:', {
          url: `${API_URL}/players/${playerId}/number`,
          playerId: playerId,
          number: number
        });
        
        const response = await fetch(`${API_URL}/players/${playerId}/number`, {
          method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ number }),
          });

          if (!response.ok) {
            const error = await response.json();
          throw new Error(error.message || 'Ошибка при обновлении номера игрока');
        }

        return await response.json();
      } catch (error) {
        console.error('Error updating player number:', error);
        throw error;
      }
    },

    startEditPosition() {
      this.isEditingPosition = true;
      this.editingPlayerPosition = this.selectedPlayerProfile.position;
    },

    savePlayerPosition() {
      // Validate the position
      if (!this.editingPlayerPosition) {
        this.$message.error('Пожалуйста, выберите позицию');
        return;
      }

      this.savingPosition = true;
      
      // Store the original position for potential rollback
      const originalPosition = this.selectedPlayerProfile.position;
      
      // Update the local state first
      this.selectedPlayerProfile.position = this.editingPlayerPosition;
      this.isEditingPosition = false;
      
      // Update the player's position in the database
      this.updatePlayerPosition(this.selectedPlayerProfile.id, this.editingPlayerPosition)
        .then(() => {
          this.$message.success('Позиция игрока обновлена');
          // Refresh event details to show updated position in lists
          if (this.selectedEvent && this.selectedEvent.id) {
            this.loadEventPlayers(this.selectedEvent.id);
          }
        })
        .catch((error) => {
          console.error('Error updating player position:', error);
          this.$message.error('Ошибка при обновлении позиции игрока');
          // Revert the local change on error
          this.selectedPlayerProfile.position = originalPosition;
          this.isEditingPosition = true; // Keep editing mode open
        })
        .finally(() => {
          this.savingPosition = false;
        });
    },

    cancelEditPosition() {
      this.isEditingPosition = false;
      this.editingPlayerPosition = null;
    },

    async updatePlayerPosition(playerId, position) {
      try {
        console.log('Sending request to update player position:', {
          url: `${API_URL}/players/${playerId}/position`,
          playerId: playerId,
          position: position
        });
        
        // Use the generic player endpoint for consistency
        const response = await fetch(`${API_URL}/players/${playerId}/position`, {
          method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ position }),
          });

          if (!response.ok) {
            const error = await response.json();
          throw new Error(error.message || 'Ошибка при обновлении позиции игрока');
        }

        return await response.json();
      } catch (error) {
        console.error('Error updating player position:', error);
        throw error;
      }
    },

    startEditRole() {
      this.isEditingRole = true;
      this.editingPlayerRole = this.selectedPlayerProfile.role;
    },

    savePlayerRole() {
      // Validate the role
      if (!this.editingPlayerRole || !['admin', 'player'].includes(this.editingPlayerRole)) {
        this.$message.error('Пожалуйста, выберите корректную роль');
        return;
      }

      this.savingRole = true;
      
      // Store the original role for potential rollback
      const originalRole = this.selectedPlayerProfile.role;
      
      // Update the local state first
      this.selectedPlayerProfile.role = this.editingPlayerRole;
      this.isEditingRole = false;
      
      // Debug: Log the player ID being used
      console.log('Updating player role:', {
        playerId: this.selectedPlayerProfile.id,
        user_id: this.selectedPlayerProfile.user_id,
        role: this.editingPlayerRole,
        player: this.selectedPlayerProfile
      });
      
      // Validate that we have the correct player_id
      if (!this.selectedPlayerProfile.id) {
        console.error('ERROR: No player_id found in selectedPlayerProfile');
        this.$message.error('Ошибка: не найден ID игрока');
        return;
      }
      
      // Update the player's role in the database
      this.updatePlayerRole(this.selectedPlayerProfile.id, this.editingPlayerRole)
        .then(() => {
          this.$message.success('Роль игрока обновлена');
          // Refresh team players to show updated role
          this.loadTeamPlayers();
        })
        .catch((error) => {
          console.error('Error updating player role:', error);
          this.$message.error('Ошибка при обновлении роли игрока');
          // Revert the local change on error
          this.selectedPlayerProfile.role = originalRole;
          this.isEditingRole = true; // Keep editing mode open
        })
        .finally(() => {
          this.savingRole = false;
        });
    },

    cancelEditRole() {
      this.isEditingRole = false;
      this.editingPlayerRole = null;
    },

    async updatePlayerRole(playerId, role) {
      try {
        console.log('Sending request to update player role:', {
          url: `${API_URL}/players/${playerId}/role`,
          playerId: playerId,
          role: role
        });
        
        const response = await fetch(`${API_URL}/players/${playerId}/role`, {
          method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ role }),
          });

          if (!response.ok) {
            const error = await response.json();
          throw new Error(error.message || 'Ошибка при обновлении роли игрока');
        }

        return await response.json();
      } catch (error) {
        console.error('Error updating player role:', error);
        throw error;
      }
    },

    startEditCaptainStatus() {
      this.isEditingCaptainStatus = true;
      this.editingPlayerCaptainStatus = this.selectedPlayerProfile.captain_status;
    },

    savePlayerCaptainStatus() {
      this.savingCaptainStatus = true;
      const originalStatus = this.selectedPlayerProfile.captain_status;
      this.selectedPlayerProfile.captain_status = this.editingPlayerCaptainStatus;
      this.isEditingCaptainStatus = false;
      
      // Debug: Log the player ID being used
      console.log('Updating player captain status:', {
        playerId: this.selectedPlayerProfile.id,
        user_id: this.selectedPlayerProfile.user_id,
        captain_status: this.editingPlayerCaptainStatus,
        player: this.selectedPlayerProfile
      });
      
      // Validate that we have the correct player_id
      if (!this.selectedPlayerProfile.id) {
        console.error('ERROR: No player_id found in selectedPlayerProfile');
        this.$message.error('Ошибка: не найден ID игрока');
        return;
      }
      
      this.updatePlayerCaptainStatus(this.selectedPlayerProfile.id, this.editingPlayerCaptainStatus)
        .then(() => {
          this.$message.success('Статус игрока обновлен');
          this.loadTeamPlayers();
        })
        .catch((error) => {
          console.error('Error updating captain status:', error);
          this.$message.error('Ошибка при обновлении статуса игрока');
          this.selectedPlayerProfile.captain_status = originalStatus;
          this.isEditingCaptainStatus = true;
        })
        .finally(() => {
          this.savingCaptainStatus = false;
        });
    },

    cancelEditCaptainStatus() {
      this.isEditingCaptainStatus = false;
      this.editingPlayerCaptainStatus = null;
    },

    async updatePlayerCaptainStatus(playerId, captain_status) {
      try {
        console.log('Sending request to update player captain status:', {
          url: `${API_URL}/players/${playerId}/captain-status`,
          playerId: playerId,
          captain_status: captain_status
        });
        
        const response = await fetch(`${API_URL}/players/${playerId}/captain-status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ captain_status }),
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Ошибка при обновлении статуса игрока');
        }
        return await response.json();
      } catch (error) {
        console.error('Error updating captain status:', error);
        throw error;
      }
    },

    getCaptainStatusText(status) {
      if (status === 'captain') return 'Капитан';
      if (status === 'assistant') return 'Ассистент';
      return 'Обычный игрок';
    },

    // Team logo upload methods
    async handleTeamLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('Размер файла не должен превышать 10MB');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('Пожалуйста, выберите изображение в формате JPEG, PNG, GIF или HEIC');
        return;
      }
      
      try {
        this.uploadingTeamLogo = true;
        const formData = new FormData();
        formData.append('photo', file);
        
        // For new team creation, we'll use a temporary upload endpoint
        // or store the file temporarily and upload it when creating the team
        const response = await fetch(`${API_URL}/user/photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.newTeam.logo_url = data.photo_url;
          this.$message.success('Логотип команды загружен');
        } else {
            const error = await response.json();
          this.$message.error(error.message || 'Ошибка при загрузке логотипа');
        }
      } catch (error) {
        console.error('Error uploading team logo:', error);
        this.$message.error('Ошибка при загрузке логотипа');
      } finally {
        this.uploadingTeamLogo = false;
        // Reset file input
        if (this.$refs.teamLogoInput) {
          this.$refs.teamLogoInput.value = '';
        }
      }
    },

    async handleEditTeamLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('Размер файла не должен превышать 10MB');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('Пожалуйста, выберите изображение в формате JPEG, PNG, GIF или HEIC');
        return;
      }
      
      try {
        this.uploadingTeamLogo = true;
        const formData = new FormData();
        formData.append('logo', file);
        
        const response = await fetch(`${API_URL}/teams/${this.editingTeam.id}/logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.editingTeam.logo_url = data.logo_url;
          this.$message.success('Логотип команды обновлен');
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при загрузке логотипа');
        }
      } catch (error) {
        console.error('Error uploading team logo:', error);
        this.$message.error('Ошибка при загрузке логотипа');
      } finally {
        this.uploadingTeamLogo = false;
        // Reset file input
        if (this.$refs.editTeamLogoInput) {
          this.$refs.editTeamLogoInput.value = '';
        }
      }
    },

    uploadNewTeamLogo() {
      if (this.$refs.editTeamLogoInput) {
        this.$refs.editTeamLogoInput.click();
      }
    },

    // Team editing methods
    async editTeam(teamId) {
      try {
        const response = await fetch(`${API_URL}/teams/${teamId}`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (response.ok) {
          const teamData = await response.json();
          this.editingTeam = teamData;
          
          // Initialize notification settings with defaults
          if (this.editingTeam.event_notification_enabled === undefined) {
            this.editingTeam.event_notification_enabled = true;
          } else {
            this.editingTeam.event_notification_enabled = !!this.editingTeam.event_notification_enabled;
          }
          
          if (this.editingTeam.payment_notification_enabled === undefined) {
            this.editingTeam.payment_notification_enabled = true;
          } else {
            this.editingTeam.payment_notification_enabled = !!this.editingTeam.payment_notification_enabled;
          }
          
          // Convert time strings to dayjs objects for TimePicker
          const dayjs = (await import('dayjs')).default;
          
          const eventTime = this.editingTeam.event_notification_time || '09:00';
          const [eventHour, eventMinute] = eventTime.split(':');
          this.editingTeam.event_notification_time_moment = dayjs().hour(parseInt(eventHour)).minute(parseInt(eventMinute));
          
          const paymentTime = this.editingTeam.payment_notification_time || '13:00';
          const [paymentHour, paymentMinute] = paymentTime.split(':');
          this.editingTeam.payment_notification_time_moment = dayjs().hour(parseInt(paymentHour)).minute(parseInt(paymentMinute));
          
          this.showScreen('editTeam');
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при загрузке данных команды');
        }
      } catch (error) {
        console.error('Error loading team data:', error);
        this.$message.error('Ошибка при загрузке данных команды');
      }
    },

    async saveTeamEdit() {
      if (!this.editingTeam) return;

      try {
        this.savingTeam = true;
        
        // Convert dayjs objects to time strings
        let eventNotificationTime = '09:00';
        let paymentNotificationTime = '13:00';
        
        if (this.editingTeam.event_notification_time_moment) {
          eventNotificationTime = this.editingTeam.event_notification_time_moment.format('HH:mm');
        }
        
        if (this.editingTeam.payment_notification_time_moment) {
          paymentNotificationTime = this.editingTeam.payment_notification_time_moment.format('HH:mm');
        }
        
        const response = await fetch(`${API_URL}/teams/${this.editingTeam.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            name: this.editingTeam.name,
            city: this.editingTeam.city,
            description: this.editingTeam.description,
            social_networks: this.editingTeam.social_networks,
            participating_positions: JSON.stringify(this.editingTeam.participating_positions || ['forward', 'defender', 'goalkeeper']),
            event_notification_enabled: this.editingTeam.event_notification_enabled,
            event_notification_time: eventNotificationTime,
            payment_notification_enabled: this.editingTeam.payment_notification_enabled,
            payment_notification_time: paymentNotificationTime,
          }),
        });

        if (response.ok) {
          this.$message.success('Команда обновлена успешно');
          this.editingTeam = null;
          this.showScreen('main');
          await this.loadUserTeams();
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при обновлении команды');
        }
      } catch (error) {
        console.error('Error updating team:', error);
        this.$message.error('Ошибка при обновлении команды');
      } finally {
        this.savingTeam = false;
      }
    },

    // Initialize notification settings for currentTeamInfo
    async initializeNotificationSettings() {
      if (!this.currentTeamInfo) return;

      // Initialize notification settings with defaults
      if (this.currentTeamInfo.event_notification_enabled === undefined) {
        this.currentTeamInfo.event_notification_enabled = true;
      } else {
        this.currentTeamInfo.event_notification_enabled = !!this.currentTeamInfo.event_notification_enabled;
      }
      
      if (this.currentTeamInfo.payment_notification_enabled === undefined) {
        this.currentTeamInfo.payment_notification_enabled = true;
      } else {
        this.currentTeamInfo.payment_notification_enabled = !!this.currentTeamInfo.payment_notification_enabled;
      }
      
      // Convert time strings to dayjs objects for TimePicker
      const dayjs = (await import('dayjs')).default;
      
      const eventTime = this.currentTeamInfo.event_notification_time || '09:00';
      const [eventHour, eventMinute] = eventTime.split(':');
      this.currentTeamInfo.event_notification_time_moment = dayjs()
        .hour(parseInt(eventHour))
        .minute(parseInt(eventMinute));
      
      const paymentTime = this.currentTeamInfo.payment_notification_time || '13:00';
      const [paymentHour, paymentMinute] = paymentTime.split(':');
      this.currentTeamInfo.payment_notification_time_moment = dayjs()
        .hour(parseInt(paymentHour))
        .minute(parseInt(paymentMinute));

      // Best player voting (from team data)
      this.bestPlayerVotingEnabled = !!this.currentTeamInfo.best_player_voting_enabled;
    },

    // Update notification settings from team info screen
    async updateNotificationSettings() {
      if (!this.currentTeamInfo || !this.currentTeamId) return;

      try {
        this.savingNotifications = true;
        
        // Convert dayjs objects to time strings
        let eventNotificationTime = '09:00';
        let paymentNotificationTime = '13:00';
        
        if (this.currentTeamInfo.event_notification_time_moment) {
          eventNotificationTime = this.currentTeamInfo.event_notification_time_moment.format('HH:mm');
        }
        
        if (this.currentTeamInfo.payment_notification_time_moment) {
          paymentNotificationTime = this.currentTeamInfo.payment_notification_time_moment.format('HH:mm');
        }
        
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            event_notification_enabled: this.currentTeamInfo.event_notification_enabled,
            event_notification_time: eventNotificationTime,
            payment_notification_enabled: this.currentTeamInfo.payment_notification_enabled,
            payment_notification_time: paymentNotificationTime,
          }),
        });

        if (response.ok) {
          this.$message.success('Настройки уведомлений обновлены');
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при обновлении настроек');
        }
      } catch (error) {
        console.error('Error updating notification settings:', error);
        this.$message.error('Ошибка при обновлении настроек');
      } finally {
        this.savingNotifications = false;
      }
    },

    async saveBestPlayerVotingSettings() {
      if (!this.currentTeamId) return;
      try {
        this.bestPlayerVotingSaving = true;
        await api.updateBestPlayerVotingSettings(this.currentTeamId, this.bestPlayerVotingEnabled);
        if (this.currentTeamInfo) {
          this.currentTeamInfo.best_player_voting_enabled = this.bestPlayerVotingEnabled ? 1 : 0;
        }
        this.$message.success('Настройки голосования обновлены');
      } catch (e) {
        this.$message?.error(e.message || 'Ошибка сохранения');
        this.bestPlayerVotingEnabled = !this.bestPlayerVotingEnabled;
      } finally {
        this.bestPlayerVotingSaving = false;
      }
    },

    // Load attendance statistics settings
    async loadAttendanceStatisticsSettings() {
      if (!this.currentTeamId) return;

      try {
        // Ensure bot manager has loaded team bots
        await botManager.loadTeamBots();
        
        // Update reactive property for custom bot check
        this.hasCustomBotForStats = botManager.hasCustomBot(this.currentTeamId);
        
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/attendance-statistics/settings`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        if (response.ok) {
          const settings = await response.json();
          this.attendanceStatisticsSettings = {
            group_id: settings.group_id || '',
            topic_id: settings.topic_id || null,
            event_types: settings.event_types || ['training'],
            bot_type: settings.bot_type || 'both',
            green_from: settings.green_from != null ? settings.green_from : 67,
            yellow_from: settings.yellow_from != null ? settings.yellow_from : 34
          };
        } else if (response.status === 404) {
          // Settings not found, use defaults
          this.attendanceStatisticsSettings = {
            group_id: '',
            topic_id: null,
            event_types: ['training'],
            bot_type: 'both',
            green_from: 67,
            yellow_from: 34
          };
        }
      } catch (error) {
        console.error('Error loading attendance statistics settings:', error);
      }
    },

    // Save attendance statistics settings
    async saveAttendanceStatisticsSettings() {
      if (!this.currentTeamId) return;

      try {
        this.savingAttendanceStatistics = true;

        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/attendance-statistics/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            group_id: this.attendanceStatisticsSettings.group_id || null,
            topic_id: this.attendanceStatisticsSettings.topic_id || null,
            event_types: this.attendanceStatisticsSettings.event_types || ['training'],
            bot_type: this.attendanceStatisticsSettings.bot_type || 'both',
            green_from: this.attendanceStatisticsSettings.green_from,
            yellow_from: this.attendanceStatisticsSettings.yellow_from
          }),
        });

        if (response.ok) {
          this.$message.success('Настройки статистики посещаемости сохранены');
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при сохранении настроек');
        }
      } catch (error) {
        console.error('Error saving attendance statistics settings:', error);
        this.$message.error('Ошибка при сохранении настроек');
      } finally {
        this.savingAttendanceStatistics = false;
      }
    },

    // Send attendance statistics manually
    async sendAttendanceStatisticsManually() {
      if (!this.currentTeamId) return;

      // Show date range picker modal
      this.showAttendanceStatisticsDateRange = true;
    },

    // Confirm and send attendance statistics
    async confirmSendAttendanceStatistics() {
      if (!this.currentTeamId || !this.attendanceStatisticsDateRange || this.attendanceStatisticsDateRange.length !== 2) {
        this.$message.error('Выберите период для статистики');
        return;
      }

      if (!this.attendanceStatisticsSettings.group_id) {
        this.$message.error('Сначала настройте ID группы Telegram');
        return;
      }

      try {
        this.sendingAttendanceStatistics = true;

        const [startDate, endDate] = this.attendanceStatisticsDateRange;
        if (!startDate || !endDate) {
          this.$message.error('Выберите период для статистики');
          return;
        }

        const startDateStr = startDate.format('YYYY-MM-DD');
        const endDateStr = endDate.format('YYYY-MM-DD');

        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/attendance-statistics/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            start_date: startDateStr,
            end_date: endDateStr
          }),
        });

        if (response.ok) {
          const result = await response.json();
          this.$message.success(`Статистика отправлена успешно (${result.players_count} игроков, ${result.total_events} событий)`);
          this.showAttendanceStatisticsDateRange = false;
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при отправке статистики');
        }
      } catch (error) {
        console.error('Error sending attendance statistics:', error);
        this.$message.error('Ошибка при отправке статистики');
      } finally {
        this.sendingAttendanceStatistics = false;
      }
    },

    // Player profile methods
    async openPlayerProfile(player) {
      // Store the player_id (id from players table) before loading full profile
      const playerId = player.id;
      this.selectedPlayerProfile = player;
      this.showScreen("playerProfile");
      await this.loadFullPlayerProfile(player.user_id, playerId);
      // Load statistics when opening player profile
      this.loadPlayerStatistics();
    },

    // Statistics methods
    async loadFullPlayerProfile(userId, playerId) {
      try {
        const response = await fetch(`${API_URL}/user/${userId}/profile`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('FULL PROFILE DATA:', data); // Для отладки
          
          // Извлекаем данные из объекта user
          const fullProfile = data.user || data;
          
          // Merge full profile data with existing player data, preserving the correct player_id
          this.selectedPlayerProfile = { 
            ...this.selectedPlayerProfile, 
            ...fullProfile,
            id: playerId // Ensure we keep the correct player_id from the players table
          };
          
          // Принудительно обновляем компонент
          this.$forceUpdate();
        } else {
          console.error("Failed to load full player profile:", response.status);
        }
      } catch (error) {
        console.error("Error loading full player profile:", error);
      }
    },
    async loadPlayerStatistics() {
      if (!this.selectedPlayerProfile) return;
      
      this.loadingStats = true;
      try {
        // Load both monthly stats and team ranking
        await Promise.all([
          this.loadMonthlyStats(),
          this.loadTeamRanking()
        ]);
      } catch (error) {
        console.error('Error loading player statistics:', error);
        this.$message.error('Ошибка при загрузке статистики');
      } finally {
        this.loadingStats = false;
      }
    },

    async loadMonthlyStats() {
      if (!this.selectedPlayerProfile) return;
      
      try {
        const response = await fetch(
          `${API_URL}/user/${this.selectedPlayerProfile.user_id}/statistics/monthly?year=${this.selectedStatsYear}&month=${this.selectedStatsMonth}`,
          {
            headers: { Authorization: `Bearer ${this.token}` },
          }
        );
        
        if (response.ok) {
          this.monthlyStats = await response.json();
        } else {
          console.error('Failed to load monthly stats:', response.status);
          this.monthlyStats = null;
        }
      } catch (error) {
        console.error('Error loading monthly stats:', error);
        this.monthlyStats = null;
      }
    },

    async loadTeamRanking() {
      if (!this.selectedPlayerProfile) return;
      
      try {
        const response = await fetch(
          `${API_URL}/user/${this.selectedPlayerProfile.user_id}/statistics/ranking`,
          {
            headers: { Authorization: `Bearer ${this.token}` },
          }
        );
        
        if (response.ok) {
          this.teamRanking = await response.json();
      } else {
          console.error('Failed to load team ranking:', response.status);
          this.teamRanking = null;
        }
      } catch (error) {
        console.error('Error loading team ranking:', error);
        this.teamRanking = null;
      }
    },

    async loadTeamPlayers() {
      if (!this.currentTeamId) return;
      
      try {
        this.loadingTeamPlayers = true;
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}/players`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (response.ok) {
          this.teamPlayersData = await response.json();
        } else {
          console.error('Failed to load team players:', response.status);
          this.teamPlayersData = null;
        }
      } catch (error) {
        console.error('Error loading team players:', error);
        this.teamPlayersData = null;
      } finally {
        this.loadingTeamPlayers = false;
      }
    },

    // Edit player methods
    openEditPlayerModal(player, event) {
      if (event) {
        event.stopPropagation(); // Prevent opening player profile
      }
      
      
      if (!player || !player.id) {
        console.error('Invalid player data:', player);
        this.$message.error('Ошибка: некорректные данные игрока');
        return;
      }
      
      this.editingPlayer = player;
      this.editPlayerForm = {
        first_name: player.first_name || '',
        last_name: player.last_name || '',
        number: player.number || null,
        position: player.position || null,
        date_of_birth: player.date_of_birth ? dayjs(player.date_of_birth) : null,
        height: player.height || null,
        weight: player.weight || null,
        stick_hand: player.stick_hand || null,
        skill_level: player.skill_level || null,
        emergency_phone: player.emergency_phone || ''
      };
      
      this.editPlayerModalVisible = true;
    },

    closeEditPlayerModal() {
      this.editPlayerModalVisible = false;
      this.editingPlayer = null;
      this.editPlayerForm = {
        first_name: '',
        last_name: '',
        number: null,
        position: null,
        date_of_birth: null,
        height: null,
        weight: null,
        stick_hand: null,
        skill_level: null,
        emergency_phone: ''
      };
    },

    async savePlayerData() {

      if (!this.editingPlayer || !this.currentTeamId) {
        console.error('Missing required data:', {
          editingPlayer: this.editingPlayer,
          currentTeamId: this.currentTeamId
        });
        this.$message.error('Ошибка: данные игрока не найдены');
        return;
      }

      if (!this.editingPlayer.id) {
        console.error('Missing player ID:', this.editingPlayer);
        this.$message.error('Ошибка: ID игрока не найден');
        return;
      }

      this.savingPlayerData = true;
      try {
        // Prepare data for sending to server
        const playerData = {
          ...this.editPlayerForm,
          // Convert dayjs object to string format for date_of_birth
          date_of_birth: this.editPlayerForm.date_of_birth 
            ? this.editPlayerForm.date_of_birth.format('YYYY-MM-DD') 
            : null
        };


        const response = await fetch(
          `${API_URL}/teams/${this.currentTeamId}/players/${this.editingPlayer.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`
            },
            body: JSON.stringify(playerData)
          }
        );

        if (response.ok) {
          this.$message.success('Данные игрока обновлены');
          this.closeEditPlayerModal();
          // Reload team players to show updated data
          await this.loadTeamPlayers();
          // If player profile is open, reload it
          if (this.selectedPlayerProfile && this.selectedPlayerProfile.id === this.editingPlayer.id) {
            await this.loadFullPlayerProfile(this.editingPlayer.user_id, this.editingPlayer.id);
          }
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при обновлении данных игрока');
        }
      } catch (error) {
        console.error('Error updating player data:', error);
        this.$message.error('Ошибка при обновлении данных игрока');
      } finally {
        this.savingPlayerData = false;
      }
    },

    getMonthName(month) {
      const months = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
      ];
      return months[month - 1] || '';
    },

    getParticipationStatusText(status) {
      switch (status) {
        case 0:
          return 'Без статуса';
        case 1:
          return 'Не участвую';
        case 2:
          return 'Участвую';
        default:
          return 'Неизвестно';
      }
    },

    getPositionLabel(position) {
      const positions = {
        'forward': this.$t('positions.forward'),
        'defender': this.$t('positions.defender'),
        'goalkeeper': this.$t('positions.goalkeeper'),
        'coach': this.$t('positions.coach'),
        'service': this.$t('positions.service')
      };
      return positions[position] || position;
    },

    getSkillLevelLabel(level) {
      const levels = {
        'beginner': this.$t('skillLevels.beginner'),
        'intermediate': this.$t('skillLevels.intermediate'),
        'experienced': this.$t('skillLevels.experienced')
      };
      return levels[level] || level;
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return null;
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },

    isBirthdayToday(dateOfBirth) {
      if (!dateOfBirth) return false;
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      return birthDate.getMonth() === today.getMonth() && birthDate.getDate() === today.getDate();
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateOnly(date) {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      return d.toLocaleDateString(this.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formatEventDate(date) {
      if (!date) return '';
      const eventDate = new Date(date);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Проверяем, является ли событие завершенным (прошло более 3 часов)
      const nowTime = new Date(today.getTime());
      const isPast = eventDate < nowTime;
      
      // Проверяем, является ли дата сегодняшней или завтрашней
      const isToday = eventDate.toDateString() === today.toDateString();
      const isTomorrow = eventDate.toDateString() === tomorrow.toDateString();
      
      const time = eventDate.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      let formattedDate;
      if (isToday) {
        formattedDate = `Сегодня · ${time}`;
      } else if (isTomorrow) {
        formattedDate = `Завтра · ${time}`;
      } else {
        // Для остальных дат используем стандартный формат
        const dayOfWeek = eventDate.toLocaleDateString('ru-RU', { weekday: 'short' });
        const dayOfMonth = eventDate.getDate();
        const month = eventDate.toLocaleDateString('ru-RU', { month: 'short' });
        formattedDate = `${dayOfWeek}, ${dayOfMonth} ${month} · ${time}`;
      }
      
      // Добавляем "(прошло)" для завершенных событий
      return isPast ? `${formattedDate} (прошло)` : formattedDate;
    },

    // Event participation methods
    openEventParticipationDrawer(event) {
      this.selectedEvent = event;
      this.drawerVisible = true;
    },

    openDrawerPlayer(player) {
      this.selectedPlayer = player;
      this.drawerVisible = true;
    },

    closeDrawerPlayer() {
      this.drawerVisible = false;
      this.selectedPlayer = null;
      // this.selectedEvent = null; // Removed to prevent clearing event screen data
      this.comment = '';
    },

    async setEventParticipation(status) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;

      try {
        let playerToUpdate = this.selectedPlayer;
        if (!playerToUpdate) {
          const eventPlayersResponse = await fetch(`${API_URL}/events/${this.selectedEvent.id}/players`, {
            headers: { Authorization: `Bearer ${this.token}` },
          });
          if (!eventPlayersResponse.ok) throw new Error('Could not fetch event players');
          const players = await eventPlayersResponse.json();
          playerToUpdate = players.find(p => p.user_id === this.userData.id);
        }

        if (!playerToUpdate) {
          return this.$message.error('Не удалось определить игрока для обновления статуса.');
        }

        const eventIndex = this.events.findIndex(e => e.id === this.selectedEvent.id);
        if (eventIndex === -1) {
          throw new Error("Could not find the event in the local list.");
        }

        const eventToUpdate = this.events[eventIndex];
        const oldStatus = eventToUpdate.user_participation_status;
        const newStatus = status;

        // We only proceed if the status has actually changed.
        if (oldStatus !== newStatus) {
          // Set a flag to indicate this is our own update
          this.lastOwnParticipationUpdate = {
            eventId: this.selectedEvent.id,
            playerId: playerToUpdate.id,
            timestamp: Date.now()
          };

          await this.updateParticipation(
            this.selectedEvent.id,
            playerToUpdate.id,
            newStatus,
            this.comment
          );

          // Update stats locally for immediate feedback
          const stats = { ...eventToUpdate.stats };
          const statusMap = { 0: 'unknown', 1: 'not_participating', 2: 'participating' };
          if (stats[statusMap[oldStatus]] > 0) {
            stats[statusMap[oldStatus]]--;
          }
          stats[statusMap[newStatus]]++;
          
          // Create the new event object for the main list
          const updatedEvent = {
            ...eventToUpdate,
            user_participation_status: newStatus,
            stats: stats
          };
          this.events.splice(eventIndex, 1, updatedEvent);

          // Update the selectedEvent object if it is currently displayed
          if (this.selectedEvent && this.selectedEvent.id === updatedEvent.id) {
            this.selectedEvent.user_participation_status = newStatus;
            this.selectedEvent.stats = stats;
            
            // Find and update the player in the detailed player list
            if (this.selectedEvent.players) {
              const playerInListIndex = this.selectedEvent.players.findIndex(p => p.id === playerToUpdate.id);
              if (playerInListIndex !== -1) {
                const updatedPlayer = { 
                  ...this.selectedEvent.players[playerInListIndex], 
                  is_participating: newStatus,
                  comment: this.comment
                };
                this.selectedEvent.players.splice(playerInListIndex, 1, updatedPlayer);
              }
            }
          }
        }

        this.closeDrawerPlayer();
        this.$message.success('Статус участия обновлен');

      } catch (error) {
        console.error('Error updating event participation:', error);
        
        // Check if it's a subscription block error
        if (error.message && error.message.includes('Абонемент не оплачен')) {
          this.$message.error('Нельзя отметить участие. Абонемент не оплачен.');
          // Reload subscription status to update the warning
          await this.loadSubscriptionStatus();
        } else {
        this.$message.error(`Ошибка: ${error.message}`);
        }
      }
    },

    async setPlayerParticipation(player, status) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;

      try {
        const oldStatus = player.is_participating;
        const newStatus = status;

        if (oldStatus !== newStatus) {
          await this.updateParticipation(
            this.selectedEvent.id,
            player.id,
            newStatus,
            '' 
          );

          // The server will automatically clear placement if status is "not participating" (1) or "unknown" (0)
          // We just need to update local state for immediate feedback
          
          // 1. Update player in selectedEvent.players list
          const playerInListIndex = this.selectedEvent.players.findIndex(p => p.id === player.id);
          if (playerInListIndex !== -1) {
            const updatedPlayer = { 
              ...this.selectedEvent.players[playerInListIndex], 
              is_participating: newStatus,
              comment: ''
            };
            
            // Clear placement if status is "not participating" or "unknown"
            if (newStatus === 1 || newStatus === 0) {
              updatedPlayer.place = null;
              updatedPlayer.color = null;
              
              // Clear player from all fields for immediate visual feedback
              this.fields.forEach(field => {
                [...field.topPlaces, ...field.bottomPlaces].forEach(placeItem => {
                  if (placeItem.player && placeItem.player.id === player.id) {
                    placeItem.player = null;
                    placeItem.color = null;
                  }
                });
              });
            }
            
            this.selectedEvent.players.splice(playerInListIndex, 1, updatedPlayer);
          }

          // 2. Update the main event object and its stats
          const eventIndex = this.events.findIndex(e => e.id === this.selectedEvent.id);
          if (eventIndex !== -1) {
            const eventToUpdate = this.events[eventIndex];
            const stats = { ...eventToUpdate.stats };
            const statusMap = { 0: 'unknown', 1: 'not_participating', 2: 'participating' };
            
            if (stats[statusMap[oldStatus]] !== undefined && stats[statusMap[oldStatus]] > 0) {
              stats[statusMap[oldStatus]]--;
            }
            if (stats[statusMap[newStatus]] !== undefined) {
              stats[statusMap[newStatus]]++;
            }
            
            if (player.user_id === this.userData.id) {
              eventToUpdate.user_participation_status = newStatus;
              if (this.selectedEvent) {
                this.selectedEvent.user_participation_status = newStatus;
              }
            }

            const updatedEvent = {
              ...eventToUpdate,
              stats: stats,
              players: this.selectedEvent.players 
            };
            this.events.splice(eventIndex, 1, updatedEvent);
            
            this.selectedEvent.stats = stats;
          }
        }
        this.$message.success(`Статус для ${player.first_name} ${player.last_name} обновлен`);
      } catch (error) {
        console.error('Error setting player participation by admin:', error);
        this.$message.error(`Ошибка при обновлении статуса: ${error.message}`);
      }
    },

    // Calendar methods
    previousMonth() {
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1);
    },

    nextMonth() {
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1);
    },

    hasEventsOnDate(date) {
      return this.events.some(event => {
        const eventDate = new Date(event.date);
        return eventDate.toDateString() === date.toDateString();
      });
    },

    getEventsForDate(date) {
      return this.events.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate.toDateString() === date.toDateString();
      });
    },

    isToday(date) {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    },

    getUserStatusForDate(date) {
      const eventsOnDate = this.getEventsForDate(date);
      if (eventsOnDate.length === 0) return null;
      
      // Return the status of the first event (assuming user can only have one status per day)
      const event = eventsOnDate[0];
      return event.user_participation_status;
    },

    hasBirthdaysOnDate(date) {
      if (!this.teamPlayersData || !this.teamPlayersData.players) return false;
      
      const allPlayers = [];
      Object.values(this.teamPlayersData.players).forEach(positionPlayers => {
        allPlayers.push(...positionPlayers);
      });
      
      return allPlayers.some(player => {
        if (!player.date_of_birth) return false;
        const birthDate = new Date(player.date_of_birth);
        return birthDate.getMonth() === date.getMonth() && birthDate.getDate() === date.getDate();
      });
    },

    getBirthdaysForDate(date) {
      if (!this.teamPlayersData || !this.teamPlayersData.players) return [];
      
      const allPlayers = [];
      Object.values(this.teamPlayersData.players).forEach(positionPlayers => {
        allPlayers.push(...positionPlayers);
      });
      
      return allPlayers.filter(player => {
        if (!player.date_of_birth) return false;
        const birthDate = new Date(player.date_of_birth);
        return birthDate.getMonth() === date.getMonth() && birthDate.getDate() === date.getDate();
      });
    },

    // Touch methods for full avatar
    handleTouchStart(event) {
      this.touchStartY = event.touches[0].clientY;
      this.touchStartTime = Date.now();
    },

    handleTouchMove(event) {
      if (!this.touchStartY) return;
      
      const touchY = event.touches[0].clientY;
      const deltaY = touchY - this.touchStartY;
      
      // Prevent default if swiping down
      if (deltaY > 50) {
        event.preventDefault();
      }
    },

    handleTouchEnd(event) {
      if (!this.touchStartY || !this.touchStartTime) return;
      
      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = touchEndY - this.touchStartY;
      const deltaTime = Date.now() - this.touchStartTime;
      
      // Close if swiping down fast enough
      if (deltaY > 100 && deltaTime < 300) {
        this.showFullAvatar = false;
      }
      
      this.touchStartY = null;
      this.touchStartTime = null;
    },

    // Event editing methods
    editEvent(event) {
      // Extract time from the event date
      const eventDate = dayjs(event.date);
      const eventTime = eventDate.format('HH:mm');
      
      this.editingEvent = { 
        ...event,
        date: eventDate,
        time: dayjs(eventTime, 'HH:mm')
      };
      this.showScreen('editEvent');
    },

    closeEventEdit() {
      this.editingEvent = null;
      this.showScreen('main');
    },

    async saveEventEdit() {
      if (!this.editingEvent) return;

      try {
        const response = await fetch(`${API_URL}/events/${this.editingEvent.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            type: this.editingEvent.type,
            date: this.editingEvent.date.format('YYYY-MM-DD'),
            time: this.editingEvent.time.format('HH:mm'),
            opponent: this.editingEvent.opponent,
            location: this.editingEvent.location,
            comment: this.editingEvent.comment,
            editMode: this.editMode,
            roster_id: this.editingEvent.roster_id
          }),
        });

        if (response.ok) {
          this.$message.success('Событие обновлено успешно');
          this.closeEventEdit();
          if (this.currentTeamId) {
            await this.loadEvents(this.currentTeamId);
          }
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при обновлении события');
        }
      } catch (error) {
        console.error('Error updating event:', error);
        this.$message.error('Ошибка при обновлении события');
      }
    },

    async handleDeleteEvent() {
      if (!this.editingEvent) return;

      try {
        const url = new URL(`${API_URL}/events/${this.editingEvent.id}`);
        if (this.editMode) {
          url.searchParams.append('editMode', this.editMode);
        }
        
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`,
            },
        });

        if (response.ok) {
          this.$message.success('Событие удалено успешно');
          this.closeEventEdit();
          if (this.currentTeamId) {
            await this.loadEvents(this.currentTeamId);
          }
        } else {
            const error = await response.json();
          this.$message.error(error.message || 'Ошибка при удалении события');
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        this.$message.error('Ошибка при удалении события');
      }
    },

    // Event creation methods
    async createEvent() {
      try {
        if (this.isRepeatingEvent) {
          // Validate required fields for repeating events
          if (!this.repeatingEventData.start_date || !this.repeatingEventData.end_date) {
            this.$message.error('Пожалуйста, выберите период для повторяющегося события');
            return;
          }
          
          if (!this.repeatingEventData.days_of_week || this.repeatingEventData.days_of_week.length === 0) {
            this.$message.error('Пожалуйста, выберите дни недели для повторяющегося события');
            return;
          }
          
          // Handle repeating event creation
          const repeatingEventData = {
            type: this.newEvent.type,
            start_date: this.repeatingEventData.start_date.format('YYYY-MM-DD'),
            end_date: this.repeatingEventData.end_date.format('YYYY-MM-DD'),
            days_of_week: this.repeatingEventData.days_of_week,
            location: this.newEvent.location,
            comment: this.newEvent.comment,
            team_id: this.currentTeamId,
            time: this.newEvent.time.format('HH:mm')
          };

          if (this.newEvent.type === 'game') {
            repeatingEventData.opponent = this.newEvent.opponent;
          }

          const response = await fetch(`${API_URL}/repeating-events`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`,
            },
            body: JSON.stringify(repeatingEventData),
          });

          if (response.ok) {
            const result = await response.json();
            this.$message.success(`Повторяющееся событие создано успешно. Создано ${result.events_created} событий.`);
            this.showScreen('main');
            if (this.currentTeamId) {
              await this.loadEvents(this.currentTeamId);
            }
            // Reset form
            this.newEvent = {
              type: 'training',
              date: dayjs(),
              time: dayjs(),
              opponent: '',
              location: '',
              comment: '',
              team_id: null,
              roster_id: null
            };
            this.isRepeatingEvent = false;
            this.repeatingEventData = {
              start_date: dayjs(),
              end_date: dayjs().add(1, 'month'),
              days_of_week: []
            };
          } else {
            const error = await response.json();
            this.$message.error(error.message || 'Ошибка при создании повторяющегося события');
          }
        } else {
          // Validate required fields for single events
          if (!this.newEvent.date) {
            this.$message.error('Пожалуйста, выберите дату события');
            return;
          }
          
          if (!this.newEvent.time) {
            this.$message.error('Пожалуйста, выберите время события');
            return;
          }
          
          if (!this.newEvent.location) {
            //this.$message.error('Пожалуйста, укажите место проведения');
            this.newEvent.location = '';
            //return;
          }
          
          // Handle single event creation
          let eventData = {
            type: this.newEvent.type,
            date: this.newEvent.date.format('YYYY-MM-DD'),
            time: this.newEvent.time.format('HH:mm'),
            location: this.newEvent.location,
            comment: this.newEvent.comment,
            team_id: this.currentTeamId,
            roster_id: this.newEvent.roster_id
          };

          if (this.newEvent.type === 'game') {
            eventData.opponent = this.newEvent.opponent;
          }

          const response = await fetch(`${API_URL}/events`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`,
            },
            body: JSON.stringify(eventData),
          });
          
          if (response.ok) {
            this.$message.success('Событие создано успешно');
            this.showScreen('main');
            if (this.currentTeamId) {
              await this.loadEvents(this.currentTeamId);
            }
            // Reset form
            this.newEvent = {
              type: 'training',
              date: dayjs(),
              time: dayjs(),
              opponent: '',
              location: '',
              comment: '',
              team_id: null,
              roster_id: null
            };
            this.isRepeatingEvent = false;
            this.repeatingEventData = {
              start_date: dayjs(),
              end_date: dayjs().add(1, 'month'),
              days_of_week: []
            };
          } else {
            const error = await response.json();
            this.$message.error(error.message || 'Ошибка при создании события');
          }
        }
      } catch (error) {
        console.error('Error creating event:', error);
        this.$message.error('Ошибка при создании события');
      }
    },

    handleDateRangeChange(dates) {
      if (dates && dates.length === 2) {
        this.repeatingEventData.start_date = dates[0];
        this.repeatingEventData.end_date = dates[1];
      }
    },

    disablePastDates(current) {
      return current && current < dayjs().startOf('day');
    },

    disableFutureDates(current) {
      return current && current < dayjs().startOf('day');
    },

    disableUntilDates(current) {
      // Не может быть ранее даты "С даты" или сегодняшнего дня
      const fromDate = this.participationSettings.participation_from || dayjs();
      return current && current < fromDate.startOf('day');
    },

    // Hockey field methods
    openPlayerMenu(field, place) {
      // Toggle menu visibility for this place
      if (!field.menuVisibility) {
        field.menuVisibility = {};
      }
      field.menuVisibility[place.id] = !field.menuVisibility[place.id];
    },

    handlePlayerSelect(field, place, event) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      
      const playerId = event.key;
      
      if (playerId === 'unassign') {
        // Remove player from position
        this.updatePlacement(this.selectedEvent.id, place.player.id, null, null);
        place.player = null;
        place.color = null;
      } else {
        // Assign player to position
        const player = this.selectedEvent.players.find(p => p.id === playerId);
        if (player) {
          
          // Determine the color, prioritizing place color, then field, then default
          const color = place.color || field.selectedColor || this.defaultPlayerColor;
          
          //const color = place.color || this.defaultPlayerColor;
          this.updatePlacement(this.selectedEvent.id, player.id, place.id, color);
          place.player = player;
          place.color = color;
        }
      }
      
      // Close menu
      field.menuVisibility[place.id] = false;
    },
   // Обновляем обработчик выбора общего цвета
   async selectDefaultColor(e) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      
      console.log('selectDefaultColor', e);
      const newColor = e.key;
      const previousColor = this.defaultPlayerColor;
      this.defaultPlayerColor = newColor;
      
      try {
        // Обновляем цвета всех полей на новый цвет
        this.fields.forEach(field => {
          field.selectedColor = newColor;
        });

        // Сохраняем новый общий цвет и цвета полей
        await this.saveColors();
        
        // Обновляем цвета всех игроков на всех полях
        const response = await fetch(`${API_URL}/events/${this.selectedEvent.id}/players/update-colors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            color: newColor
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update player colors');
        }

        const result = await response.json();
        console.log(`Server update result: ${result.message}`);

        if (result.changedRows === 0) {
          console.log('No players were updated (no players on fields)');
        }

        // Обновляем локальное состояние только после успешного сохранения
        this.fields.forEach(field => {
          field.topPlaces.forEach(place => {
            if (place.player) {
              place.color = newColor;
            }
          });
          field.bottomPlaces.forEach(place => {
            if (place.player) {
              place.color = newColor;
            }
          });
        });

        // Перезагружаем данные игроков для синхронизации
        await this.loadEventPlayers(this.selectedEvent.id);
      } catch (error) {
        console.error('Ошибка при обновлении цветов:', error);
        // Восстанавливаем предыдущий цвет в случае ошибки
        this.defaultPlayerColor = previousColor;
        this.fields.forEach(field => {
          field.selectedColor = previousColor;
        });
        // Перезагружаем данные для синхронизации состояния
        await this.loadEventPlayers(this.selectedEvent.id);
      }
    },

    // Обновляем обработчик выбора цвета для поля
    async selectFieldColor(fieldId, color) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      
      const field = this.fields.find(f => f.id === fieldId);
      if (field) {
        const previousColor = field.selectedColor;
        field.selectedColor = color;
        
        try {
          // Сохраняем цвета полей
          await this.saveColors();

          // Определяем какой цвет использовать для игроков
          const playerColor = color || this.defaultPlayerColor;

          // Обновляем цвета всех игроков на этом поле в базе данных
          const response = await fetch(`${API_URL}/events/${this.selectedEvent.id}/players/update-colors`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`,
            },
            body: JSON.stringify({
              color: playerColor,
              fieldId: field.id
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to update player colors on server');
          }

          const result = await response.json();
          console.log(`Server update result: ${result.message}`);

          if (result.changedRows === 0) {
            console.log('No players were updated (no players on this field)');
          }

          // Обновляем локальное состояние только после успешного сохранения
          field.topPlaces.forEach(place => {
            if (place.player) {
              place.color = playerColor;
            }
          });

          field.bottomPlaces.forEach(place => {
            if (place.player) {
              place.color = playerColor;
            }
          });

          // Перезагружаем данные игроков для синхронизации
          await this.loadEventPlayers(this.selectedEvent.id);
        } catch (error) {
          console.error('Ошибка при обновлении цветов:', error);
          // Восстанавливаем предыдущий цвет поля в случае ошибки
          field.selectedColor = previousColor;
          // Перезагружаем данные для синхронизации состояния
          await this.loadEventPlayers(this.selectedEvent.id);
        }
      }
    },

    // Загрузка цветов при открытии вкладки
    async loadColors() {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      
      try {
        const response = await fetch(`${API_URL}/events/${this.selectedEvent.id}/colors`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        const data = await response.json();
        
        // Установка общего цвета
        this.defaultPlayerColor = data.default_color;
        
        // Установка цветов для полей
        const fieldColors = JSON.parse(data.field_colors || '{}');
        this.fields.forEach(field => {
          field.selectedColor = fieldColors[field.id] || '';
        });
      } catch (error) {
        console.error('Ошибка при загрузке цветов:', error);
      }
    },

    // Сохранение цветов на сервере
    async saveColors() {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      
      try {
        // Создаем объект с цветами полей
        const fieldColors = {};
        this.fields.forEach(field => {
          if (field.selectedColor) {
            fieldColors[field.id] = field.selectedColor;
          }
        });

        await fetch(`${API_URL}/events/${this.selectedEvent.id}/colors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            default_color: this.defaultPlayerColor,
            field_colors: JSON.stringify(fieldColors)
          }),
        });
      } catch (error) {
        console.error('Ошибка при сохранении цветов:', error);
      }
    },

    // Participation settings methods
    async updateParticipationSettings() {
      try {
        if (!this.currentTeamId) {
          this.$message.error('Сначала выберите команду');
          return;
        }

        await api.updateParticipationSettings(this.participationSettings, this.currentTeamId);
        this.$message.success('Настройки участия обновлены');
      } catch (error) {
        console.error('Error updating participation settings:', error);
        this.$message.error('Ошибка при обновлении настроек участия');
      }
    },

    async saveParticipationSettings() {
      if (!this.currentTeamId) {
        this.$message.error('Сначала выберите команду');
        return;
      }

      // Если дата "С" не указана, используем сегодняшний день
      const settingsToSave = {
        ...this.participationSettings,
        participation_from: this.participationSettings.participation_from || dayjs().format('YYYY-MM-DD')
      };

      this.savingParticipationSettings = true;
      try {
        const response = await api.updateParticipationSettings(settingsToSave, this.currentTeamId);
        this.$message.success('Настройки участия сохранены');
        
        // Показываем информацию о том, сколько событий обновлено
        if (response && response.message) {
          this.$message.info(response.message);
        }
        
        // Показываем индикатор обновления событий
        this.$message.loading('Обновление статусов участия в событиях...', 0);
        
        // Перезагружаем события для обновления статусов участия
        await this.loadEvents(this.currentTeamId);
        
        // Если открыто конкретное событие, перезагружаем его участников
        if (this.selectedEvent && this.selectedEvent.id) {
          await this.loadEventPlayers(this.selectedEvent.id);
        }
        
        // Закрываем индикатор загрузки
        this.$message.destroy();
        this.$message.success('Статусы участия обновлены!');
        
      } catch (error) {
        console.error('Error saving participation settings:', error);
        this.$message.error('Ошибка при сохранении настроек участия');
      } finally {
        this.savingParticipationSettings = false;
      }
    },

    // Test methods
    async testReminders() {
      try {
        const response = await fetch(`${API_URL}/test-reminders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.ok) {
          this.$message.success('Тестовые напоминания отправлены');
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при отправке тестовых напоминаний');
        }
      } catch (error) {
        console.error('Error testing reminders:', error);
        this.$message.error('Ошибка при отправке тестовых напоминаний');
      }
    },

    // Send notifications to players with unknown status
    async sendNotificationsToUnknown() {
      if (!this.selectedEvent?.id) {
        this.$message.error('Событие не выбрано');
        return;
      }

      this.sendingNotifications = true;
      try {
        const response = await api.notifyUnknownPlayers(this.selectedEvent.id);
        
        if (response.notifiedCount > 0) {
          this.$message.success(`Уведомления отправлены ${response.notifiedCount} игрокам`);
          
          // Reload event players to update stats
          await this.loadEventPlayers(this.selectedEvent.id);
          
          // Reload events list to update stats
          if (this.currentTeamId) {
            await this.loadEvents(this.currentTeamId);
          }
        } else {
          this.$message.info(response.message || 'Нет игроков со статусом "неизвестно" для уведомления');
        }
      } catch (error) {
        console.error('Error sending notifications:', error);
        this.$message.error('Ошибка при отправке уведомлений');
      } finally {
        this.sendingNotifications = false;
      }
    },

    // Tab change handler
    onTabChange(activeKey) {
      this.activeTab = activeKey;
    },

    // Team info methods
    async loadCurrentTeamInfo() {
      if (!this.currentTeamId) return;
      
      try {
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        
        if (response.ok) {
          const teamData = await response.json();
          this.currentTeamInfo = teamData;
          
          // Initialize notification settings
          await this.initializeNotificationSettings();
          
          // Load attendance statistics settings
          await this.loadAttendanceStatisticsSettings();
          
          // Проверяем, есть ли у команды кастомный бот
          try {
            const botIconData = await botManager.getTeamBotIcon(this.currentTeamId);
            if (botIconData && botIconData.iconUrl) {
              // Обновляем иконки на основе кастомного бота
              await dynamicIconsManager.updateAppIconsFromBot(botIconData.iconUrl, botIconData.teamName, this.currentTeamId);
              dynamicIconsManager.saveIconSettings(this.currentTeamId, botIconData.iconUrl, botIconData.teamName);
              console.log('Иконки обновлены на основе кастомного бота');
            } else if (teamData.logo_url) {
              // Если кастомного бота нет, используем логотип команды
              await dynamicIconsManager.updateAppIcons(teamData.logo_url, teamData.name, this.currentTeamId);
              dynamicIconsManager.saveIconSettings(this.currentTeamId, teamData.logo_url, teamData.name);
            } else {
              dynamicIconsManager.resetToDefaultIcons();
            }
          } catch (error) {
            console.error('Ошибка при получении иконки бота:', error);
            // Fallback к логотипу команды
            if (teamData.logo_url) {
              await dynamicIconsManager.updateAppIcons(teamData.logo_url, teamData.name, this.currentTeamId);
              dynamicIconsManager.saveIconSettings(this.currentTeamId, teamData.logo_url, teamData.name);
            } else {
              dynamicIconsManager.resetToDefaultIcons();
            }
          }
        } else {
          console.error('Failed to load team info:', response.status);
        }
      } catch (error) {
        console.error('Error loading team info:', error);
      }
    },

    async showTeamInfo() {
      if (!this.currentTeamId) return;
      
      try {
        const response = await fetch(`${API_URL}/teams/${this.currentTeamId}`, {
                headers: { Authorization: `Bearer ${this.token}` },
              });
        
        if (response.ok) {
          const teamData = await response.json();
          this.currentTeamInfo = teamData;
          
          // Initialize notification settings
          await this.initializeNotificationSettings();
          
          // Load attendance statistics settings
          await this.loadAttendanceStatisticsSettings();
          
          this.showScreen('teamInfo');
          // Загружаем список игроков сразу при открытии экрана команды
          this.loadTeamPlayers();
        } else {
          const error = await response.json();
          this.$message.error(error.message || 'Ошибка при загрузке информации о команде');
        }
      } catch (error) {
        console.error('Error loading team info:', error);
        this.$message.error('Ошибка при загрузке информации о команде');
      }
    },

    editCurrentTeam() {
      if (!this.currentTeamInfo) return;
      
      // Parse participating_positions if it's a string, or use default
      let participatingPositions = ['forward', 'defender', 'goalkeeper']; // Default
      if (this.currentTeamInfo.participating_positions) {
        if (typeof this.currentTeamInfo.participating_positions === 'string') {
          try {
            participatingPositions = JSON.parse(this.currentTeamInfo.participating_positions);
          } catch (e) {
            console.error('Error parsing participating_positions:', e);
          }
        } else if (Array.isArray(this.currentTeamInfo.participating_positions)) {
          participatingPositions = this.currentTeamInfo.participating_positions;
        }
      }
      
      this.editingTeam = { 
        ...this.currentTeamInfo,
        participating_positions: participatingPositions
      };
      this.showScreen('editTeam');
    },

    showTeamPlayers() {
      this.showScreen('teamPlayers');
      this.loadTeamPlayers();
    },

    onTeamTabChange(key) {
      this.activeTeamTab = key;
      if (key === 'players' && !this.teamPlayersData) {
        this.loadTeamPlayers();
      }
    },

    // Add method to load team info and show the screen
    async viewTeamInfo(teamId) {
      try {
        // First try to get team info from search results
        const teamFromSearch = this.searchResults.find(team => team.id === teamId);
        if (teamFromSearch) {
          this.selectedTeamInfo = teamFromSearch;
          this.currentScreen = 'viewTeamInfo';
          return;
        }
        
        // If not found in search results, fetch from server
        const response = await fetch(`${API_URL}/teams/search?query=`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        if (response.ok) {
          const allTeams = await response.json();
          const team = allTeams.find(t => t.id === teamId);
          if (team) {
            this.selectedTeamInfo = team;
            this.currentScreen = 'viewTeamInfo';
          } else {
            this.$message.error('Команда не найдена');
          }
        } else {
          this.$message.error('Ошибка при загрузке информации о команде');
          }
        } catch (error) {
        console.error('Error loading team info:', error);
        this.$message.error('Ошибка при загрузке информации о команде');
      }
    },

    // Load participation settings for the current team
    async loadParticipationSettings() {
      try {
        if (!this.currentTeamId) {
          console.warn('No current team selected for participation settings');
          return;
        }
        
        const settings = await api.getParticipationSettings(this.currentTeamId);
        this.participationSettings = {
          participation_from: settings.participation_from ? dayjs(settings.participation_from) : null,
          participation_until: settings.participation_until ? dayjs(settings.participation_until) : null,
          default_participation: settings.default_participation || 0,
          default_comment: settings.default_comment || ''
        };
      } catch (error) {
        console.error('Error loading participation settings:', error);
        // Set default values if loading fails
        this.participationSettings = {
          participation_from: null,
          participation_until: null,
          default_participation: 0,
          default_comment: ''
        };
      }
    },

    // Show profile screen and load settings
    showProfile() {
      this.currentScreen = 'profile';
      if (this.currentTeamId) {
        this.loadParticipationSettings();
      }
      this.onClose(); // Close the sidebar menu
    },

    // Show participation settings screen
    showParticipationSettings() {
      if (!this.currentTeamId) {
        this.$message.error('Сначала выберите команду');
        return;
      }
      this.currentScreen = 'participationSettings';
      this.loadParticipationSettings();
    },




    // Show create payment modal
    showCreatePaymentModal() {
      if (!this.currentTeamId) {
        this.$message.error('Сначала выберите команду');
        return;
      }
      // Переключаемся на вкладку регистрации оплаты
      this.activePaymentsTab = 'registration';
    },

    // Show payments screen
    showBestPlayerVoting() {
      if (!this.currentTeamId) {
        this.$message.warning('Выберите команду');
        return;
      }
      this.previousScreen = this.currentScreen;
      this.currentScreen = 'bestPlayerVoting';
      this.loadTeamPlayers();
      this.loadEvents(this.currentTeamId);
    },

    showPayments() {
      console.log('Opening payments screen');
      this.currentScreen = 'payments';
      this.activePaymentsTab = 'registration';
      this.loadSubscriptionStatus();
    },

    // Show subscription screen (alias for showPayments)
    showSubscription() {
      console.log('Opening subscription/payment screen from warning');
      this.showPayments();
    },

    // Load subscription status
    async loadSubscriptionStatus() {
      try {
        this.loadingSubscriptionStatus = true;
        const newStatus = await api.getSubscriptionStatus();
        // Force Vue reactivity by creating a new array
        this.subscriptionStatus = JSON.parse(JSON.stringify(newStatus));
        
        console.log('📊 Subscription status loaded:', {
          count: this.subscriptionStatus.length,
          statuses: this.subscriptionStatus.map(s => ({
            team: s.team_name,
            is_blocked: s.is_blocked,
            is_deadline_approaching: s.is_deadline_approaching,
            payment_status: s.payment_status
          }))
        });
      } catch (error) {
        console.error('Error loading subscription status:', error);
      } finally {
        this.loadingSubscriptionStatus = false;
      }
    },

    // Show payment info
    showPaymentInfo() {
      this.$message.info('Информация об оплате абонемента будет доступна в ближайшее время');
    },

    // Handle bot settings update
    async onBotUpdated() {
      this.$message.success('Настройки бота обновлены');
      
      // Проверяем, есть ли у команды кастомный бот
      if (this.currentTeamId) {
        try {
          const botIconData = await botManager.getTeamBotIcon(this.currentTeamId);
          if (botIconData && botIconData.iconUrl) {
            // Обновляем иконки на основе кастомного бота
            await dynamicIconsManager.updateAppIconsFromBot(botIconData.iconUrl, botIconData.teamName, this.currentTeamId);
            console.log('Иконки обновлены на основе кастомного бота');
          } else {
            // Если кастомного бота нет, используем логотип команды
            if (this.currentTeamInfo?.logo_url) {
              await dynamicIconsManager.updateAppIcons(this.currentTeamInfo.logo_url, this.currentTeamInfo.name, this.currentTeamId);
            } else {
              dynamicIconsManager.resetToDefaultIcons();
            }
          }
        } catch (error) {
          console.error('Ошибка при обновлении иконок после изменения бота:', error);
        }
      }
    },

    // Handle player exemption update
    onPlayerExemptionUpdated() {
      this.$message.success('Настройки освобождения игрока обновлены');
      this.loadSubscriptionStatus();
    },

    // Handle player payment registration
    onPlayerPaymentRegistered() {
      this.$message.success('Оплата игрока зарегистрирована');
      this.loadSubscriptionStatus();
    },

    getIsAdmin() {
      if (!this.currentTeamId) return null;
      const team = this.userTeams.find(t => t.id === this.currentTeamId);
      return team.role === 'admin' ? true : false;
    },


    async checkPaymentAdminRights() {
      if (!this.currentTeamId) return;
      
      // Защита от повторных вызовов
      if (this.checkingPaymentAdminRights) {
        console.log('Payment admin rights check already in progress, skipping...');
        return;
      }
      
      // Если уже установлено, что пользователь payment admin, не проверяем повторно
      if (this.isPaymentAdmin) {
        console.log('User is already payment admin, skipping check...');
        return;
      }
      
      // Дебаунсинг - отменяем предыдущий таймер если он есть
      if (this.checkPaymentAdminRightsTimer) {
        clearTimeout(this.checkPaymentAdminRightsTimer);
      }
      
      this.checkPaymentAdminRightsTimer = setTimeout(async () => {
        this.checkingPaymentAdminRights = true;
        
        try {
          // Получаем все активные типы оплат для команды (используем публичный endpoint)
          const paymentTypes = await api.getPublicTeamPaymentTypes(this.currentTeamId);
          
          // Проверяем, является ли пользователь администратором хотя бы одного типа
          for (const paymentType of paymentTypes) {
            try {
              const result = await api.canManagePaymentType(this.currentTeamId, paymentType.id);
              if (result.canManage && result.isPaymentAdmin) {
                this.isPaymentAdmin = true;
                console.log('User is payment admin for type:', paymentType.name);
                return;
              }
            } catch (error) {
              console.error('Error checking payment admin rights for type:', paymentType.id, error);
            }
          }
          
          this.isPaymentAdmin = false;
        } catch (error) {
          console.error('Error checking payment admin rights:', error);
          this.isPaymentAdmin = false;
        } finally {
          this.checkingPaymentAdminRights = false;
        }
      }, 300); // Дебаунсинг 300ms
    },

    unassignPlayer(field, place) {
      if (!this.selectedEvent || !this.selectedEvent.id) return;
      this.updatePlacement(this.selectedEvent.id, place.player.id, null, null);
      place.player = null;
      place.color = null;
      // Close menu after unassigning
      if (field.menuVisibility && field.menuVisibility[place.id] !== undefined) {
        field.menuVisibility[place.id] = false;
      }
    },

    async handleMenuClick({ key }) {
      if (key.startsWith('team-')) {
        const teamId = key.replace('team-', '');

        this.currentTeamId = parseInt(teamId);
        this.currentScreen = 'main';
        await this.loadEvents(this.currentTeamId);
        // Загружаем составы команды
        await this.loadTeamRosters(this.currentTeamId);
        // Загружаем состав команды для отображения дней рождения
        await this.loadTeamPlayers();
        await this.loadCurrentTeamInfo();
        this.selectedMenuKeys = [key];
        this.isAdmin = this.getIsAdmin();
        // Проверяем права администратора платежей для новой команды
        await this.checkPaymentAdminRights();

      } else {
        switch (key) {
          case 'profile':
            this.showProfile();
            break;
          case 'participation-settings':
            this.showParticipationSettings();
            break;

          case 'payments':
            this.showPayments();
            break;
          case 'best-player-voting':
            this.showBestPlayerVoting();
            break;
          case 'create-team':
            this.showScreen('createTeam');
            break;
          case 'search-teams':
            this.showScreen('searchTeams');
            break;
          case 'pending-requests':
            this.showPendingRequests();
            break;

          case 'add-to-homescreen':
          if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.isVersionAtLeast('8.0') && window.Telegram.WebApp.addToHomeScreen) {
                window.Telegram.WebApp.addToHomeScreen();
            } else {
                this.$message.info('Функция не поддерживается в вашей версии Telegram или вы не в приложении Telegram.');
            }
            break;
          case 'toggle-admin':
            this.toggleAdminRole();
            break;
          case 'test-user-switcher':
            // Don't close drawer for user switcher
            return;
          case 'theme':
            // Theme switching is handled by the ThemeSwitcher component
            break;
        }
        this.selectedMenuKeys = [key];
      }
      this.onClose();
    },

    toggleAdminRole() {
      this.isAdmin = !this.isAdmin;
      this.$message.success(`Режим администратора ${this.isAdmin ? 'включен' : 'отключен'}`);
      
      // Если админ включен, загружаем заявки на вступление
      if (this.isAdmin) {
        this.loadAllPendingRequests();
      } else {
        // Если админ отключен, очищаем заявки
        this.allPendingRequests = [];
      }
    },

    // Team Invitation Methods
    async loadTeamInvitations() {
      if (!this.currentTeamId || !this.isAdmin) return;
      
      try {
        this.loadingInvitations = true;
        const invitations = await api.getTeamInvitations(this.currentTeamId);
        this.teamInvitations = invitations;
      } catch (error) {
        console.error('Error loading team invitations:', error);
        this.$message.error('Ошибка при загрузке приглашений');
        this.teamInvitations = [];
      } finally {
        this.loadingInvitations = false;
      }
    },

    showCreateInvitationModal() {
      this.createInvitationModalVisible = true;
      this.newInvitation = {
        max_uses: null,
        expires_in_days: null
      };
    },

    async createInvitation() {
      if (!this.currentTeamId) return;
      
      try {
        this.creatingInvitation = true;
        
        const invitationData = {
          max_uses: this.newInvitation.max_uses || null,
          expires_in_days: this.newInvitation.expires_in_days || null
        };
        
        const result = await api.createTeamInvitation(this.currentTeamId, invitationData);
        
        this.$message.success(result.message);
        this.createInvitationModalVisible = false;
        await this.loadTeamInvitations();
        
      } catch (error) {
        console.error('Error creating invitation:', error);
        this.$message.error('Ошибка при создании приглашения');
      } finally {
        this.creatingInvitation = false;
      }
    },

    cancelCreateInvitation() {
      this.createInvitationModalVisible = false;
      this.newInvitation = {
        max_uses: null,
        expires_in_days: null
      };
    },

    async deactivateInvitation(invitationId) {
      if (!this.currentTeamId) return;
      
      try {
        await api.deactivateTeamInvitation(this.currentTeamId, invitationId);
        this.$message.success('Приглашение деактивировано');
        await this.loadTeamInvitations();
      } catch (error) {
        console.error('Error deactivating invitation:', error);
        this.$message.error('Ошибка при деактивации приглашения');
      }
    },

    async copyInviteLink(link) {
      try {
        await navigator.clipboard.writeText(link);
        this.$message.success('Ссылка скопирована в буфер обмена');
      } catch (error) {
        console.error('Error copying link:', error);
        this.$message.error('Ошибка при копировании ссылки');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Update onTeamTabChange to load invitations when needed
    onTeamTabChange(key) {
      this.activeTeamTab = key;
      if (key === 'players' && !this.teamPlayersData) {
        this.loadTeamPlayers();
      } else if (key === 'invitations' && this.isAdmin) {
        this.loadTeamInvitations();
      } else if (key === 'notifications' && this.isAdmin) {
        // Ensure notification settings are initialized when opening the tab
        this.initializeNotificationSettings();
      }
    },

    // Определение оптимального placement для dropdown меню
    getOptimalPlacement(element) {
      if (!element) return 'bottomLeft';
      
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 300; // Примерная высота меню
      
      // Если элемент в верхней половине экрана, открываем вниз
      if (rect.top < viewportHeight / 2) {
        return 'bottomLeft';
      } else {
        // Если элемент в нижней половине экрана, открываем вверх
        return 'topLeft';
      }
    },

    // Обработчик клика для открытия меню с динамическим placement
    openPlayerMenu(field, place) {
      // Находим элемент, на который кликнули
      const element = event?.target?.closest('.position-circle-content');
      if (element) {
        const placement = this.getOptimalPlacement(element);
        // Обновляем placement для конкретного места
        field.menuPlacement = placement;
      }
    },

    // Payment management methods
    showPaymentTypesManagement() {
      if (this.canManagePayments) {
        this.currentScreen = 'paymentTypesManagement';
      } else {
        this.$message.error('У вас нет прав для управления типами оплат');
      }
    },

    showCreatePaymentTypeModal() {
      // Emit event to PaymentTypesManagement component to show create modal
      this.$refs.paymentTypesManagement?.showCreateModal();
    },

    showAdminPaymentManagement() {
      if (this.canManagePayments) {
        this.currentScreen = 'adminPaymentManagement';
      } else {
        this.$message.error('У вас нет прав для управления оплатами');
      }
    }
  }
};

</script>

<style scoped>
/* ===== RESET & BASE STYLES ===== */
body {
  margin: 0;
  padding: 0;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ===== LAYOUT COMPONENTS ===== */

/* Header */
.app-header {
  background: var(--color-bg-primary);
  padding: 0 var(--spacing-lg);
  box-shadow: var(--shadow-md);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: var(--z-index-fixed);
  height: var(--header-height);
  line-height: var(--header-height);
}

/* Main Content */
.main-content {
  margin-top: var(--header-height);
  flex: 1;
  background: var(--color-bg-secondary);
}

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: var(--spacing-xl) var(--spacing-lg);
}

/* Screen Content */
.screen-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* ===== FORM ELEMENTS ===== */
.ant-input,
.ant-select,
.ant-picker,
.ant-btn {
  width: 100%;
  max-width: 400px;
}

/* ===== LIST COMPONENTS ===== */
.ant-list {
  width: 100%;
  max-width: var(--container-max-width);
}

/* ===== USER INFO ===== */
.user-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  width: 100%;
  max-width: 400px;
}

/* ===== EVENT HEADER ===== */
.event-header {
  width: 100%;
  max-width: var(--container-max-width);
  margin: 0 auto;
}

/* ===== SIDEBAR ===== */
.sidebar {
  width: 280px;
  padding: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.section {
  border-bottom: var(--border-width) solid var(--color-border-primary);
  padding-bottom: var(--spacing-xl);
}

.section.actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.action-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  border: none;
  background: none;
  padding: var(--spacing-sm) 0;
  justify-content: flex-start;
}

.action-button:not(:last-child) {
  border-bottom: var(--border-width) solid var(--color-border-primary);
}

.action-button:hover {
  background-color: var(--color-bg-hover);
}

.empty-message {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.ant-list-item {
  cursor: pointer;
  padding: 0 !important;
  margin: 0 !important;
  border-bottom: none !important;
}

.ant-list-item:hover {
  background-color: transparent !important;
}

/* ===== PLAYER CARD ===== */
.player-card {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  padding: 0px var(--spacing-sm);
  background-color: var(--color-bg-primary);
  width: 100%;
  border-bottom: var(--border-width) solid var(--color-border-secondary);
  max-width: 100%;
}

.player-number-with-edit {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-player-btn {
  padding: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.edit-player-btn:hover {
  opacity: 1;
}

.player-name-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.avatar-container .profile-avatar {
  margin: 0 auto;
}

.player-avatar {
  width: 100%;
  height: 100%;
}

.avatar-wrapper {
  border-radius: var(--border-radius-circle);
}

/* Avatar Indicators */
.avatar-indicator {
  position: absolute;
  bottom: -6px;
  left: -6px;
}

.drawer-avatar {
  position: relative;
  display: inline-block;
}

.drawer-buttons {
  display: flex;
  justify-content: space-around;
}

.ant-drawer-content-wrapper {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.position-indicator {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-circle);
  background-color: gray;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
}

.position-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-circle);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.player-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
}

.player-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.player-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
}

.player-comment {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.player-comment i {
  font-style: italic;
}

.birthday-indicator {
  font-size: 20px;
  animation: birthday-pulse 1.5s ease-in-out infinite;
  font-style: normal;
  flex-shrink: 0;
}

@keyframes birthday-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

/* Номера игроков в списке событий */
.event-players-list .player-number {
  font-size: var(--font-size-lg);
  text-align: right;
}

.player-status-number {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.player-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.captain-status,
.assistant-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  flex-shrink: 0;
}

.assistant-status {
  background-color: #52c41a;
}

.admin-actions {
  margin-left: auto;
  z-index: 2;
}

.header-button {
  width: var(--avatar-size-sm);
  height: var(--avatar-size-sm);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-lg);
}

.profile-button-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  height: 100%;
}

.profile-button-container .header-button {
  width: 48px;
  height: 48px;
}

.profile-button-container .ant-avatar {
  cursor: pointer;
}

/* Hockey Fields Container */
.hockey-fields-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.field-color-selection .ant-btn {
  width: 30px;
  border-radius: var(--border-radius-circle);
  height: 30px;
  padding: var(--spacing-xs);
  display: flex;
}

.field-color-selection .color-preview {
  width: 20px !important;
  height: 20px !important;
  border-radius: var(--border-radius-sm) !important;
  border: var(--border-width) solid var(--color-border-primary) !important;
}

.hockey-field {
  position: relative;
  max-width: 400px;
  aspect-ratio: 1/1;
  background-color: aliceblue;
  border: var(--border-width) solid #000;
  border-radius: 0 0 25% 25%;
  overflow: hidden;
  margin-bottom: var(--spacing-xl);
  align-self: center;
}

/* Center Line */
.center-line {
  position: absolute;
  top: 1%;
  left: 0;
  width: 100%;
  height: var(--border-width-thick);
  background-color: var(--color-hockey-red);
  transform: translateY(-50%);
}

/* Center Circle */
.center-circle {
  position: absolute;
  top: 1%;
  left: 50%;
  width: 30%;
  height: 30%;
  border: var(--border-width-thick) solid var(--color-hockey-blue);
  border-radius: var(--border-radius-circle);
  transform: translate(-50%, -50%);
  clip-path: polygon(0 45%, 100% 45%, 100% 100%, 0 100%);
}

/* Center Dot */
.center-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6%;
  height: 6%;
  background-color: var(--color-hockey-red);
  border-radius: var(--border-radius-circle);
  transform: translate(-50%, -50%);
}

/* Blue Line */
.blue-line {
  position: absolute;
  top: 26%;
  left: 0;
  width: 100%;
  height: var(--border-width-thick);
  background-color: var(--color-hockey-blue);
}

/* Faceoff Circles */
.faceoff-circle {
  position: absolute;
  width: 30%;
  height: 30%;
  border: var(--border-width-thick) solid var(--color-hockey-red);
  border-radius: var(--border-radius-circle);
  background-color: transparent;
}

.faceoff-circle.left {
  top: 67%;
  left: 25%;
  transform: translate(-50%, -50%);
}

.faceoff-circle.right {
  top: 67%;
  left: 75%;
  transform: translate(-50%, -50%);
}

/* Faceoff Whiskers */
.faceoff-whisker {
  position: absolute;
  width: 10%;
  height: var(--border-width-thick);
  background-color: var(--color-hockey-red);
  top: 45%;
  left: -10%;
  transform: translateY(-50%);
}


.faceoff-circle.left .faceoff-whisker:nth-child(2) {
  top: 55%;
  left: -10%;
  transform: translateY(-50%);
}

/* Левый круг, усы справа */
.faceoff-circle.left .faceoff-whisker:nth-child(3) {
  top: 45%;
  left: 100%;
  transform: translateY(-50%);
}

.faceoff-circle.left .faceoff-whisker:nth-child(4) {
  top: 55%;
  left: 100%;
  transform: translateY(-50%);
}

/* Правый круг, усы слева */

.faceoff-circle.right .faceoff-whisker:nth-child(2) {
  top: 55%;
  left: -10%;
  transform: translateY(-50%);
}

/* Правый круг, усы справа */
.faceoff-circle.right .faceoff-whisker:nth-child(3) {
  top: 45%;
  left: 100%;
  transform: translateY(-50%);
}

.faceoff-circle.right .faceoff-whisker:nth-child(4) {
  top: 55%;
  left: 100%;
  transform: translateY(-50%);
}

/* Goal Crease */
.goal-crease {
  position: absolute;
  bottom: 10%;
  left: 50%;
  width: 20%;
  height: 10%;
  background-color: var(--color-hockey-goal);
  transform: translateX(-50%);
  border-radius: 50% 50% 0 0;
  border: var(--border-width-thick) solid var(--color-hockey-red);
}

.goal {
  position: absolute;
  bottom: 5%;
  left: 50%;
  width: 15%;
  height: 5%;
  transform: translateX(-50%);
  border-radius: 0% 0% 40% 40%;
  border: var(--border-width) solid black;
  background: linear-gradient(45deg,rgba(0, 0, 0, 0) 48%,rgba(0, 0, 0, 0.2) 50%,rgba(0, 0, 0, 0) 52%),linear-gradient(-45deg,rgba(0, 0, 0, 0) 48%,rgba(0, 0, 0, 0.2) 50%,rgba(0, 0, 0, 0) 52%);
  background-size: 0.5em 0.5em;
  background-color: var(--color-bg-primary);
}

/* Goal Line */
.goal-line {
  position: absolute;
  bottom: 10%;
  left: 50%;
  width: 100%;
  height: var(--border-width-thick);
  background-color: var(--color-hockey-red);
  transform: translateX(-50%);
}

/* Player Positions */
.position-row {
  display: flex;
  justify-content: space-around;
  position: absolute;
  left: 0;
  right: 0;
}

.position-row.top {
  top: 15%;
}

/* Центральная позиция в верхнем ряду выше боковых */
.position-row.top .top-position-2 {
  transform: translateY(-20px);
}

.position-row.bottom {
  bottom: 22%;
}

.position-circle {
  width: 60px;
  height: 60px;
  border: 3px solid var(--color-text-secondary);
  border-radius: var(--border-radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-primary);
  cursor: pointer;
}

.position-circle:hover {
  background-color: var(--color-bg-hover);
}

/* Пустые позиции с тонкой границей */
.position-circle:not(:has(.player-avatar-container)) {
  border-width: 1px;
}

/* Player Avatar Container on Field */
.player-avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
}

.player-avatar-container .avatar-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-circle);
  padding: 2px;
}

.player-number-on-field {
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-sm);
  padding: 1px 4px;
  min-width: 16px;
  text-align: center;
  line-height: 1;
  border: 1px solid var(--color-border-secondary);
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

/* Comment Input */
.comment-input {
  margin: var(--spacing-lg) 0;
  width: 100%;
}

/* Drawer Buttons */
.drawer-buttons {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);
}

.participation-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-md);
  transition: background-color var(--transition-duration) ease;
}

.button-text {
  margin-left: var(--spacing-sm);
}

/* Participation Button States */
.participation-button:not(.ant-btn-primary) {
  background-color: var(--color-bg-secondary);
  border-color: var(--color-border-primary);
}

.participation-button:not(.ant-btn-primary):hover {
  background-color: var(--color-bg-hover);
}

.participation-button.ant-btn-primary {
  background-color: var(--color-success);
  border-color: var(--color-success);
}

.participation-button.ant-btn-primary:hover {
  background-color: var(--color-success-hover);
}

.color-selection-global {
  padding: var(--spacing-md);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}
.field-color-selection {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-sm);
  display: inline-block;
  vertical-align: middle;
  border: var(--border-width) solid var(--color-border-secondary);
}

.color-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}
.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}



/* Header Styles */
.app-header {
  background: var(--color-bg-primary);
  padding: 0 var(--spacing-lg);
  box-shadow: var(--shadow-sm);
  position: fixed;
  width: 100%;
  z-index: 1000;
  height: 64px;
  line-height: 64px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.screen-title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.back-button {
  width: var(--avatar-size-sm);
  height: var(--avatar-size-sm);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-lg);
}

/* Main Content Styles */
.main-content {
  padding-top: 0px;
  min-height: calc(100vh - 64px);
  background: var(--color-bg-secondary);
}

.section-title {
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.teams-list {
  margin-bottom: var(--spacing-xl);
}

.team-card {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  cursor: pointer;
  transition: all var(--transition-duration) ease;
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
}

.team-logo {
  height: 120px;
  width: 120px;
  border-radius: 0 !important;
  object-fit: contain;
}

/* Override Ant Design avatar styles for team logo */
.team-logo-section .ant-avatar {
  border-radius: 0 !important;
  overflow: visible !important;
}

.team-logo-section .ant-avatar img {
  border-radius: 0 !important;
  object-fit: contain !important;
}

.team-logo-placeholder {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-secondary);
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .team-card {
    max-width: 100%;
  }
}

.profile-settings,
.app-settings {
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.profile-settings h3,
.app-settings h3 {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
}

.setting-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.setting-item select {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: var(--border-width) solid var(--color-border-primary);
  border-radius: var(--border-radius-sm);
}



.user-info .ant-avatar {
  align-self: center;
  margin-bottom: var(--spacing-sm);
}

.profile-avatar {
  cursor: pointer;
  transition: transform var(--transition-duration);
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.full-avatar {
  max-width: 100vw;
  max-height: 100vh;
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* Full screen avatar overlay */
.full-avatar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn var(--transition-duration) ease-out;
  touch-action: pan-y;
}

.full-avatar-overlay .full-avatar {
  animation: zoomIn var(--transition-duration) ease-out;
  max-width: 90vw;
  max-height: 90vh;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Avatar Container */
.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
}

.profile-avatar {
  cursor: pointer;
  transition: transform var(--transition-duration);
  width: 96px !important;
  height: 96px !important;
  font-size: var(--font-size-xxl) !important;
  border-radius: var(--border-radius-circle) !important;
  overflow: hidden !important;
  flex-shrink: 0 !important;
}

.profile-avatar img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: var(--border-radius-circle) !important;
}

/* Override any conflicting Ant Design styles */
:deep(.ant-avatar) {
  border-radius: var(--border-radius-circle) !important;
}

:deep(.ant-avatar img) {
  border-radius: var(--border-radius-circle) !important;
  object-fit: cover !important;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.profile-button-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  height: 100%;
}

.profile-button-container .ant-btn {
  padding: 0;
  width: var(--avatar-size-sm);
  height: var(--avatar-size-sm);
  border-radius: var(--border-radius-circle);
  overflow: hidden;
}

.profile-button-container .ant-avatar {
  width: 100%;
  height: 100%;
}

/* Player Card */
.player-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background-color: var(--color-bg-primary);
  width: 100%;
  border-bottom: var(--border-width) solid var(--color-border-light);
}

.player-card:last-child {
  border-bottom: none;
}

.user-participation-status {
  position: absolute;
  right: var(--spacing-md);
  z-index: 1;
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-circle);
  transition: background-color var(--transition-duration);
}

.user-participation-status:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.user-participation-status .participation-icon {
  width: 24px;
  height: 24px;
  font-size: var(--font-size-md);
}

.ant-card {
  position: relative;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.test-user-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  border: var(--border-width) solid var(--color-border-light);
}

.test-user-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.test-user-selector .ant-select {
  font-size: var(--font-size-sm);
}

.test-user-selector .ant-select-selector {
  background-color: var(--color-bg-primary) !important;
  border-color: var(--color-border-primary) !important;
}

.test-user-selector .ant-select-focused .ant-select-selector {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
}

.current-user-indicator {
  font-size: 11px;
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  padding: var(--spacing-xs) var(--spacing-md);
  background-color: var(--color-primary-light);
  border-radius: var(--border-radius-sm);
  border: var(--border-width) solid var(--color-primary-lighter);
}

.calendar-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-sm);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.calendar-nav-button {
  width: var(--avatar-size-sm);
  height: var(--avatar-size-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.month-name {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-medium);
  min-width: 180px;
  text-align: center;
  text-transform: capitalize;
}


.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--spacing-xs);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  box-shadow: var(--shadow-sm);
}

.weekday-header {
  text-align: center;
  font-weight: var(--font-weight-medium);
  padding: var(--spacing-sm);
  color: var(--color-text-secondary);
}

.calendar-day {
  aspect-ratio: 0.7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: var(--spacing-xs) var(--spacing-xs) 26px;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  position: relative;
  min-height: 60px;
}

.calendar-day.empty {
  cursor: default;
}

.calendar-day.has-events {
  background-color: var(--color-primary-light);
}

.calendar-day.today {
  background-color: var(--color-primary-lighter);
  border: var(--border-width) solid var(--color-primary);
}

.calendar-day.has-birthdays {
  background: linear-gradient(135deg, #fff9e6 0%, #ffe6f0 100%);
  border: 1px solid #ffb6c1;
}

.calendar-day.has-birthdays.has-events {
  background: linear-gradient(135deg, #fff9e6 0%, var(--color-primary-light) 100%);
}

.day-number {
  font-size: var(--font-size-md);
  margin-bottom: var(--spacing-xs);
}

.day-number.today {
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
}

.calendar-indicators {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.calendar-indicators.events-only {
  top: 50%;
  bottom: auto;
  transform: translate(-50%, -50%);
}

.event-indicator {
  display: flex;
  gap: 2px;
}

.event-dot {
  width: 4px;
  height: 4px;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-primary);
}

.birthday-players-calendar {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.birthday-player-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
}

.birthday-player-avatar .ant-avatar {
  border: 2px solid #ffb6c1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.player-number-badge {
  position: absolute;
  bottom: -2px;
  right: -6px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid white;
  line-height: 1;
}

.more-birthdays {
  font-size: 10px;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
  cursor: help;
  white-space: nowrap;
}

.calendar-day.has-events:hover {
  background-color: var(--color-primary-lighter);
}

.calendar-day.has-birthdays:hover {
  background: linear-gradient(135deg, #ffe6cc 0%, #ffd6e0 100%);
}

.event-actions {
  display: flex;
  align-items: center;
}

.event-actions .ant-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px var(--spacing-sm);
}

.drawer-footer {
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  left: 0;
}

.event-comment {
  color: var(--color-text-secondary);
  font-style: italic;
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-sm);
}

.event-comment .anticon {
  margin-right: var(--spacing-sm);
  color: var(--color-primary);
}

.screen-content .header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  padding: 0px var(--spacing-xs);
}

.header-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.header-button .anticon {
  font-size: var(--font-size-lg);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-xl);
  gap: var(--spacing-sm);
}

.repeating-event-actions {
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-sm);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-sm);
}

.repeating-event-actions .ant-radio-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.repeating-event-actions .ant-radio-button-wrapper {
  width: 100%;
  text-align: center;
  margin-right: 0 !important;
}

.event-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.repeating-indicator {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  background: var(--color-primary-lighter);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

.repeating-indicator .anticon {
  font-size: var(--font-size-sm);
}

.form-screen {
  width: 100%;
}

.event-form .ant-input,
.event-form .ant-select,
.event-form .ant-picker,
.event-form .ant-input-search .ant-input {
  max-width: none;
}

.player-profile-screen {
  padding: var(--spacing-lg);
}

.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-xl);
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.profile-header .profile-avatar {
  margin-right: var(--spacing-lg);
}

.profile-header-info .player-name {
  margin: 0;
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-semibold);
}

.player-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  color: var(--color-text-disabled);
}

.profile-tabs {
  background: var(--color-bg-primary);
  padding: 0 var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.profile-details, .profile-settings {
  padding: var(--spacing-lg) 0;
}

.card {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-md) 0;
  border-bottom: var(--border-width) solid var(--color-border-light);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  color: var(--color-text-secondary);
}

.detail-item .value {
  font-weight: var(--font-weight-medium);
}

:deep(.ant-tabs-nav) {
  margin-bottom: 0 !important;
}

:deep(.ant-tabs-content-holder) {
  padding: var(--spacing-md) 0;
}

.repeating-indicator {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  background: var(--color-primary-lighter);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

.repeating-indicator .anticon {
  font-size: var(--font-size-sm);
}

.admin-settings {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.admin-settings h3 {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
}

.access-denied-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
  background-color: var(--color-bg-dark);
  color: var(--color-text-primary);
}
.access-denied-container h1 {
  color: var(--color-error);
  margin-bottom: var(--spacing-lg);
}

.calendar-day.status-participating {
  background-color: #d6f5d6 !important; /* light green */
}
.calendar-day.status-unknown {
  background-color: #e0e0e0 !important; /* light gray */
}
.calendar-day.status-not-participating {
  background-color: #ffe0ef !important; /* light pink */
}

/* Player card avatar container */
.player-card .avatar-container {
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}
/* Profile screen avatar container */
.user-info .avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  width: 100%;
}
.user-info .avatar-container .profile-avatar {
  margin: 0 auto;
}

/* Override any conflicting Ant Design styles */
:deep(.ant-avatar) {
  border-radius: var(--border-radius-circle) !important;
}

:deep(.ant-avatar img) {
  border-radius: var(--border-radius-circle) !important;
  object-fit: cover !important;
}

/* Force profile avatar to maintain aspect ratio */
.user-info .profile-avatar {
  aspect-ratio: 1 !important;
  min-width: 96px !important;
  min-height: 96px !important;
  max-width: 96px !important;
  max-height: 96px !important;
}

.user-info .profile-avatar img {
  aspect-ratio: 1 !important;
  object-fit: cover !important;
  width: 100% !important;
  height: 100% !important;
}

/* Profile sections styling */
.profile-section {
  margin-bottom: var(--spacing-lg);
}

.profile-section h3 {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  border-bottom: var(--border-width) solid var(--color-border-light);
  padding-bottom: var(--spacing-sm);
}

.profile-section .ant-form-item {
  margin-bottom: var(--spacing-lg);
}

.profile-section .ant-form-item-label {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
}

.profile-section .ant-input,
.profile-section .ant-select,
.profile-section .ant-picker,
.profile-section .ant-input-number {
  border-radius: var(--border-radius-md);
}

.profile-section .ant-input:focus,
.profile-section .ant-select:focus,
.profile-section .ant-picker:focus,
.profile-section .ant-input-number:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Disabled age field styling */
.profile-section .ant-input[disabled] {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-secondary);
  cursor: not-allowed;
}

/* Save button styling */
.user-info .ant-btn-primary {
  height: 40px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-md);
}

/* Responsive adjustments for profile screen */
@media (max-width: 768px) {
  .profile-section {
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
  
  .user-info {
    padding: var(--spacing-md);
  }
}

/* Participation Settings Screen Styles */
.participation-settings-screen {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.participation-settings-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.settings-description {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-xl);
  line-height: 1.5;
}

.form-item-help {
  font-size: var(--font-size-sm);
  color: var(--color-text-disabled);
  margin-top: var(--spacing-xs);
}

.settings-info {
  background: var(--color-bg-secondary);
  border: var(--border-width) solid var(--color-border-light);
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-icon {
  font-size: var(--font-size-xxl);
  flex-shrink: 0;
}

.info-content h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.info-content p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.4;
}

/* Player profile screen styling */
.player-profile-screen {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.player-profile-screen .profile-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.player-profile-screen .profile-avatar {
  cursor: pointer;
  transition: transform var(--transition-duration) ease;
}

.player-profile-screen .profile-avatar:hover {
  transform: scale(1.05);
}

.player-profile-screen .profile-header-info {
  flex: 1;
}

.player-profile-screen .player-name {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.player-profile-screen .player-meta {
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
}

.player-profile-screen .player-number {
  background: var(--color-primary);
  color: var(--color-text-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-md);
}

.player-profile-screen .player-position {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.player-profile-screen .profile-details {
  background: var(--color-bg-primary);
  padding: var(--spacing-xl);
}

.player-profile-screen .profile-section {
  margin-bottom: var(--spacing-xl);
  max-width: none;
}

.player-profile-screen .profile-section:last-child {
  margin-bottom: 0;
}

.player-profile-screen .profile-section h3 {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  border-bottom: var(--border-width) solid var(--color-border-light);
  padding-bottom: var(--spacing-sm);
}

.player-profile-screen .detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) 0;
  border-bottom: var(--border-width) solid var(--color-border-light);
}

.player-profile-screen .detail-item:last-child {
  border-bottom: none;
}

.player-profile-screen .detail-item .label {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.player-profile-screen .detail-item .value {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  text-align: right;
}

.player-profile-screen .detail-item .separator {
  margin: 0 var(--spacing-sm);
  color: var(--color-border-secondary);
  font-weight: var(--font-weight-normal);
}

/* Profile tabs styling */
.player-profile-screen .profile-tabs {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.player-profile-screen .card {
  background: var(--color-bg-primary);
  padding: var(--spacing-xl);
}

.player-profile-screen .detail-row {
  display: flex;
  gap: var(--spacing-lg);
}

.player-profile-screen .detail-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md) 0;
  border-bottom: var(--border-width) solid var(--color-border-light);
}

/* Responsive adjustments for player profile */
@media (max-width: 768px) {
  .player-profile-screen {
    padding: 0 var(--spacing-md);
  }
  
  .player-profile-screen .profile-header {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }
  
  .player-profile-screen .player-meta {
    justify-content: center;
  }
  
  .player-profile-screen .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xs);
  }
  
  .player-profile-screen .detail-item .value {
    text-align: left;
  }
}

.subscription-tab-content {
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.subscription-sub-tabs {
  background: transparent;
}

.subscription-sub-tabs .ant-tabs-nav {
  margin-bottom: var(--spacing-lg);
}

/* Payments Screen Styles */
.payments-screen {
  padding: var(--spacing-xl);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}



.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xxl);
  gap: var(--spacing-lg);
  min-height: 200px;
}

.loading-container p {
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  text-align: center;
  margin: 0;
}

.no-payments {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xxl);
  min-height: 200px;
}

.no-payments-message {
  text-align: center;
  max-width: 400px;
}

.no-payments-icon {
  font-size: 64px;
  color: var(--color-text-disabled);
  margin-bottom: var(--spacing-lg);
}

.no-payments-message h3 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
}

.no-payments-message p {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.payments-tabs {
  background: transparent;
}

.payments-tabs .ant-tabs-nav {
  margin-bottom: var(--spacing-lg);
}

.payments-tabs .ant-tabs-content-holder {
  padding: var(--spacing-sm) 0;
}

/* Responsive adjustments for payments screen */
@media (max-width: 768px) {
  .payments-screen {
    padding: var(--spacing-md);
    margin: var(--spacing-sm);
  }
  
  .no-payments {
    padding: var(--spacing-lg);
    min-height: 150px;
  }
  
  .no-payments-icon {
    font-size: 48px;
  }
  
  .no-payments-message h3 {
    font-size: var(--font-size-lg);
  }
  
  .no-payments-message p {
    font-size: var(--font-size-md);
  }
  
  .payments-tabs .ant-tabs-nav {
    margin-bottom: var(--spacing-md);
  }
  
  .payments-tabs .ant-tabs-tab {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
  }
  
  .payments-tabs .ant-tabs-content-holder {
    padding: var(--spacing-xs) 0;
  }
  
  .loading-container {
    padding: var(--spacing-lg);
    min-height: 150px;
  }
  
  .loading-container p {
    font-size: var(--font-size-md);
  }
}

@media (max-width: 480px) {
  .payments-screen {
    padding: var(--spacing-sm);
    margin: var(--spacing-xs);
  }
  
  .no-payments-icon {
    font-size: 40px;
  }
  
  .no-payments-message h3 {
    font-size: var(--font-size-md);
  }
  
  .no-payments {
    padding: var(--spacing-md);
    min-height: 120px;
  }
  
  .no-payments-message p {
    font-size: var(--font-size-sm);
  }
  
  .loading-container {
    padding: var(--spacing-md);
    min-height: 120px;
  }
  
  .loading-container p {
    font-size: var(--font-size-sm);
  }
  
  .payments-tabs .ant-tabs-content-holder {
    padding: 0;
  }
}

/* Payment Management Screens Styles */
.payment-types-management-screen,
.admin-payment-management-screen {
  padding: var(--spacing-xl);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.screen-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
}

.screen-header h2 {
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.2;
}

.back-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-sm);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  transition: all 0.2s ease;
}

.back-button:hover {
  background: var(--color-bg-hover);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.back-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

/* Responsive adjustments for payment management screens */
@media (max-width: 768px) {
  .payment-types-management-screen,
  .admin-payment-management-screen {
    padding: var(--spacing-md);
    margin: var(--spacing-sm);
  }
  
  .screen-header {
    flex-direction: column;
    gap: var(--spacing-md);
    text-align: center;
    padding: var(--spacing-md);
  }
  
  .screen-header h2 {
    font-size: var(--font-size-xl);
    margin-bottom: var(--spacing-sm);
  }
  
  .back-button {
    width: 100%;
    justify-content: center;
    height: 44px;
    font-size: var(--font-size-md);
  }
}

@media (max-width: 480px) {
  .payment-types-management-screen,
  .admin-payment-management-screen {
    padding: var(--spacing-sm);
    margin: var(--spacing-xs);
  }
  
  .screen-header {
    padding: var(--spacing-sm);
  }
  
  .screen-header h2 {
    font-size: var(--font-size-lg);
  }
  
  .back-button {
    height: 40px;
    font-size: var(--font-size-sm);
  }
}

/* Pending Requests Screen Styles */
.pending-requests-container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.pending-requests-container h3 {
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-align: center;
}

.no-requests {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.request-card {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.request-header {
  display: flex;
  align-items: center;
  padding: var(--spacing-xs);
}

.request-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.request-info p {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-disabled);
}

.request-info .request-date {
  font-size: var(--font-size-sm);
  color: var(--color-text-disabled);
  margin-top: var(--spacing-xs);
}

.request-actions {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  justify-content: flex-end;
}

.approve-btn {
  background-color: var(--color-success);
  border-color: var(--color-success);
}

.approve-btn:hover {
  background-color: var(--color-success-hover);
  border-color: var(--color-success-hover);
}

.reject-btn {
  background-color: var(--color-error);
  border-color: var(--color-error);
  color: var(--color-text-primary) !important;
}

.reject-btn:hover {
  background-color: var(--color-error-hover);
  border-color: var(--color-error-hover);
  color: var(--color-text-primary) !important;
}

.username-link {
  color: var(--color-primary);
  text-decoration: none;
  font-size: var(--font-size-md);
  line-height: 1.4;
  margin: 0;
  transition: color var(--transition-duration) ease;
}

.username-link:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}

/* Responsive adjustments for pending requests */
@media (max-width: 768px) {
  .pending-requests-container {
    padding: 0 var(--spacing-md);
  }
  
  .request-header {
    flex-direction: column;
    text-align: center;
  }
  
  .request-actions {
    flex-direction: row;
    gap: var(--spacing-sm);
  }
  
  .request-actions .ant-btn {
    flex: 1;
    min-width: 0;
  }
}

/* Test User Info Styles */
.test-user-info {
  background: var(--color-bg-secondary);
  border: var(--border-width) solid var(--color-border-light);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  width: 100%;
  max-width: 400px;
}

.test-user-info h3 {
  margin: 0 0 var(--spacing-md) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.test-user-details p {
  margin: var(--spacing-sm) 0;
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.test-user-details code {
  background: var(--color-primary-light);
  color: var(--color-primary);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--border-radius-sm);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: var(--font-size-sm);
}

.test-actions {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: var(--border-width) solid var(--color-border-light);
}

.test-actions .ant-btn {
  font-size: var(--font-size-sm);
  height: 32px;
  padding: var(--spacing-xs) var(--spacing-md);
}
/* Responsive adjustments for test user selector */
@media (max-width: 768px) {
  .test-user-selector {
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
  }
  
  .test-user-selector .ant-select {
    width: 100% !important;
  }
  
  .current-user-indicator {
    text-align: center;
    width: 100%;
  }
  
  .header-right {
    flex-direction: column;
    align-items: flex-end;
    gap: var(--spacing-xs);
  }
  
  .test-user-info {
    max-width: 100%;
  }
  
  .test-actions {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
  
  .test-actions .ant-btn {
    width: 100%;
  }
}
.pending-requests-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background-color: var(--color-error);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-circle);
  min-width: 16px;
  height: 16px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: center;
  border: var(--border-width-thick) solid var(--color-bg-primary);
  box-shadow: var(--shadow-sm);
}

.profile-button-container .ant-avatar {
  cursor: pointer;
}

.header-pending-badge {
  position: relative;
  pointer-events: none;
}

.header-pending-badge .ant-btn {
  pointer-events: auto;
}

.header-pending-badge .ant-badge-count {
  background: var(--color-error);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  border-radius: var(--border-radius-circle);
  box-shadow: var(--shadow-sm);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.pending-requests-button {
  cursor: pointer;
  transition: all var(--transition-duration) ease;
  background: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  color: var(--color-text-primary) !important;
  width: var(--avatar-size-sm) !important;
  height: var(--avatar-size-sm) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.pending-requests-button:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
  background: var(--color-primary-hover) !important;
  border-color: var(--color-primary-hover) !important;
}

.pending-requests-button .anticon {
  font-size: var(--font-size-lg);
}

.value-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.edit-number-container {
  display: flex;
  gap: var(--spacing-sm);
}

.edit-position-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.edit-position-input {
  width: 200px;
}

.edit-position-actions {
  display: flex;
  gap: var(--spacing-sm);
}

/* Team form styles */
.team-form {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.logo-upload-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.logo-preview {
  position: relative;
  display: inline-block;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: var(--border-width-thick) dashed var(--color-border-primary);
  padding: var(--spacing-sm);
}

.logo-preview-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: var(--border-radius-sm);
  display: block;
}

.logo-actions {
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  display: flex;
  gap: var(--spacing-xs);
  background: var(--color-overlay-dark);
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs);
}

.change-logo-btn,
.remove-logo-btn {
  color: var(--color-text-primary) !important;
  border: none !important;
  background: transparent !important;
  padding: var(--spacing-xs) !important;
  min-width: 24px !important;
  height: 24px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.change-logo-btn:hover,
.remove-logo-btn:hover {
  background: var(--color-overlay-light) !important;
}

.logo-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
}

.logo-upload .ant-btn {
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  border: var(--border-width-thick) dashed var(--color-border-primary);
  background: var(--color-bg-secondary);
}

.logo-upload .ant-btn:hover {
  border-color: var(--color-primary);
  background: var(--color-primary-light);
}

/* Form screen styles */
.form-screen {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: var(--spacing-xl);
}

.form-screen .ant-form-item {
  margin-bottom: var(--spacing-xl);
}

.form-screen .ant-input,
.form-screen .ant-textarea,
.form-screen .ant-select {
  border-radius: var(--border-radius-md);
}

.form-screen .ant-input:focus,
.form-screen .ant-textarea:focus,
.form-screen .ant-select:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Responsive adjustments for team forms */
@media (max-width: 768px) {
  .form-screen {
    padding: var(--spacing-lg);
  }
  
  .team-form {
    max-width: 100%;
  }
  
  .form-actions {
    display: flex;
    gap: var(--spacing-md);
    justify-content: flex-end;
  }
  
  .form-actions .cancel-btn {
    flex: 0 0 auto;
  }
  
  .form-actions .submit-btn {
    flex: 1;
    max-width: 200px;
  }
  
  .logo-preview-image {
    width: 100px;
    height: 100px;
  }
  
  .logo-upload .ant-btn {
    height: 100px;
  }
}

.events-section {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.birthday-notification {
  background: linear-gradient(135deg, #fff9e6 0%, #ffe6f0 100%);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  box-shadow: 0 2px 8px rgba(255, 182, 193, 0.3);
  border: 2px solid #ffb6c1;
}

.birthday-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.birthday-icon {
  font-size: 28px;
  animation: birthday-pulse 1.5s ease-in-out infinite;
}

.birthday-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: #d946a6;
}

.birthday-players {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.events-title {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

.no-team-selected {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.no-team-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.no-team-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

/* Team header styles */
.team-header-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

/* Team info screen styles */
.team-info-screen {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: var(--spacing-xl);
}

.team-info-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: 12px;
  position: relative;
}

.team-logo-section {
  flex-shrink: 0;
}

.team-basic-info {
  flex: 1;
}

.team-basic-info .team-name {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  cursor: default;
}

.team-basic-info .team-name:hover {
  color: var(--color-text-primary);
  text-decoration: none;
}

.team-city {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.team-info-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.info-section {
  background: var(--color-bg-primary);
  border-radius: 12px;
  padding: var(--spacing-xl);
}

.info-section h3 {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  border-bottom: var(--border-width) solid var(--color-border-light);
  padding-bottom: var(--spacing-sm);
}

.team-description {
  margin: 0;
  font-size: var(--font-size-lg);
  line-height: 1.6;
  color: var(--color-text-primary);
}

.social-networks {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.social-link {
  color: var(--color-primary);
  text-decoration: none;
  font-size: var(--font-size-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  transition: all var(--transition-duration) ease;
}

.social-link:hover {
  background: var(--color-primary-light);
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.team-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-lg);
}

.team-stats .stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-lg);
}

.team-stats .stat-label {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.team-stats .stat-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.admin-actions .ant-btn {
  height: 48px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  padding: 0px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .team-info-header {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-lg);
  }
  
  .team-basic-info .team-name {
    font-size: var(--font-size-xxl);
  }
  
  .team-stats {
    grid-template-columns: 1fr;
  }
  
  .team-info-screen {
    padding: var(--spacing-lg);
  }
  
  .info-section {
    padding: var(--spacing-lg);
  }

  .team-header-actions {
    position: absolute;
    top: auto;
    bottom: var(--spacing-lg);
    right: 50%;
    transform: translateX(50%);
  }
}

/* Statistics styles */
.statistics-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: var(--spacing-lg);
}

.stats-section h3 {
  margin: 0 0 var(--spacing-xl) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  border-bottom: var(--border-width-thick) solid var(--color-border-light);
  padding-bottom: var(--spacing-sm);
}

.month-selector {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-xl);
  gap: var(--spacing-sm);
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.stat-card {
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  text-align: center;
  border-left: var(--border-width-thick) solid var(--color-primary);
}

.stat-number {
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  margin-bottom: 0px;
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.events-list {
  margin-top: var(--spacing-xl);
}

.events-list h4 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.event-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-sm);
  border-left: var(--border-width-thick) solid var(--color-success);
}

.event-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.event-type {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.event-date {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.event-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.status-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.no-events {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.ranking-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.current-player-rank {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  border-radius: 12px;
  color: var(--color-text-primary);
}

.rank-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--color-overlay-light);
  border-radius: var(--border-radius-circle);
  width: 60px;
  height: 60px;
  justify-content: center;
}

.rank-number {
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.rank-label {
  font-size: var(--font-size-xs);
  opacity: 0.8;
}

.rank-details {
  flex: 1;
}

.rank-details .player-name {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-xs);
}

.rank-stats {
  font-size: var(--font-size-md);
  opacity: 0.9;
}

.team-ranking-list {
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
}

.team-ranking-list h4 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-md);
  margin-bottom: var(--spacing-sm);
  border-left: var(--border-width-thick) solid var(--color-primary);
}

.ranking-item:last-child {
  margin-bottom: 0;
}

.ranking-position {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-primary);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-circle);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
}

.position-number {
  line-height: 1;
}

.ranking-item .player-info {
  flex: 1;
}

.ranking-item .player-name {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: 2px;
}

.player-number {
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.ranking-item .player-stats {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.loading-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
}

/* Responsive adjustments for statistics */
@media (max-width: 768px) {
  .statistics-container {
    padding: var(--spacing-md);
  }
  
  .stats-summary {
    grid-template-columns: 1fr;
  }
  
  .month-selector {
    flex-direction: column;
    align-items: stretch;
  }
  
  .month-selector .ant-select {
    width: 100% !important;
  }
  
  .current-player-rank {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }
  
  .ranking-item {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-sm);
  }
  
  .ranking-position {
    align-self: center;
  }
}
.position-participation-settings {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  width: 100%;
  max-width: 400px;
}
.position-participation-settings p.setting-description {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.position-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
}

.position-checkboxes .ant-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

/* Position participation settings styles */
.position-participation-settings {
  margin-bottom: var(--spacing-lg);
}

.setting-description {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.5;
}
.position-checkboxes {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Notification settings styles */
.notification-settings {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.notification-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.notification-toggle-label {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.notification-time {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border);
}

.notification-time label {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.notification-hint {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-top: var(--spacing-xs);
}

/* Notification Tab Styles */
.section-description {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-md);
  line-height: 1.6;
}

.notification-card {
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.notification-card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, rgba(24, 144, 255, 0.05) 0%, rgba(24, 144, 255, 0.01) 100%);
  border-bottom: 1px solid var(--color-border);
}

.notification-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.notification-icon.event-icon {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: white;
}

.notification-icon.payment-icon {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
}

.notification-icon.statistics-icon {
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
}

.notification-card-title {
  flex: 1;
}

.notification-card-title h4 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.notification-card-title p {
  margin: 4px 0 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.notification-card-body {
  padding: var(--spacing-lg);
}

.notification-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

.detail-item {
  display: flex;
  align-items: flex-start;
}

.detail-label {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  min-width: 180px;
  flex-shrink: 0;
}

.detail-value {
  color: var(--color-text-secondary);
  flex: 1;
}

.time-picker-wrapper {
  flex: 1;
}

.notification-example {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  background: rgba(24, 144, 255, 0.03);
  border-radius: var(--border-radius-md);
  border: 1px dashed var(--color-primary);
}

.example-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-md);
}

.example-content {
  padding: var(--spacing-md);
  background: white;
  border-radius: var(--border-radius-sm);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: var(--font-size-sm);
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-line;
}

.notification-disabled-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.02);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.notification-notes {
  margin-top: var(--spacing-xl);
}

.notification-notes ul {
  margin: var(--spacing-sm) 0 0 0;
  padding-left: var(--spacing-lg);
}

.notification-notes li {
  margin-bottom: var(--spacing-sm);
  line-height: 1.6;
}

/* Responsive adjustments for notification tab */
@media (max-width: 768px) {
  .notification-card-header {
    flex-wrap: wrap;
  }

  .detail-item {
    flex-direction: column;
  }

  .detail-label {
    min-width: auto;
  }

  .notification-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
}

.position-checkboxes .ant-checkbox-wrapper {
  font-size: var(--font-size-md);
}

/* Responsive adjustments for position settings */
@media (max-width: 768px) {
  .position-checkboxes {
    gap: var(--spacing-sm);
  }
}

/* Team Players Screen Styles */
.team-players-screen {
  padding: var(--spacing-xl);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.team-players-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-xl);
}

.team-players-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-text-disabled);
}

.players-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.total-players {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xl);
}

.web-credentials-section {
  margin-bottom: var(--spacing-xl);
}

.web-credentials-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.web-credentials-info {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  flex: 1;
  min-width: 200px;
}

.web-credentials-icon {
  font-size: 28px;
  line-height: 1;
}

.web-credentials-title {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
}

.web-credentials-desc {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.web-credentials-resend-hint {
  margin-top: var(--spacing-sm);
}

.web-credentials-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
}

.issued-credentials-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border-secondary, #f0f0f0);
}

.issued-credentials-row:last-child {
  border-bottom: none;
}

.issued-credentials-name {
  font-weight: 600;
  margin-bottom: 8px;
}

.issued-credentials-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: var(--font-size-sm);
}

.issued-credentials-label {
  min-width: 52px;
  color: var(--color-text-secondary);
}

.issued-credentials-value {
  flex: 1;
  min-width: 0;
  word-break: break-all;
  padding: 2px 6px;
  background: var(--color-bg-secondary, #f5f5f5);
  border-radius: 4px;
}

.positions-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
}

.position-section {
  flex: 1;
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-sm);
}

.position-title {
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
}

.position-icon {
  font-size: var(--font-size-xxl);
  color: var(--color-primary);
}

.players-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.player-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-md);

}

.player-avatar {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-circle);
}

.player-info {
  flex: 1;
}

.player-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
}

.player-number {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  flex-shrink: 0;
}

.player-username {
  font-size: var(--font-size-md);
  color: var(--color-text-disabled);
}

.empty-players {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.empty-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

.team-tabs {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.team-tabs .ant-tabs-nav {
  margin-bottom: 0 !important;
}

.team-tabs .ant-tabs-content-holder {
  padding: var(--spacing-md) 0;
}

.team-tabs .ant-tabs-tab {
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  transition: all var(--transition-duration) ease;
}

.team-tabs .ant-tabs-tab:hover {
  background-color: var(--color-bg-hover);
}

.team-tabs .ant-tabs-tab.ant-tabs-tab-active {
  background-color: var(--color-bg-primary);
  border-bottom: var(--border-width-thick) solid var(--color-primary);
}

.team-tabs .ant-tabs-content {
  padding: var(--spacing-lg);
}

.team-tabs .ant-tabs-tabpane {
  padding: var(--spacing-lg);
}

.team-tabs .ant-tabs-tabpane:not(.ant-tabs-tabpane-active) {
  display: none;
}



.team-players-data .players-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.team-players-data .total-players {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.team-players-data .positions-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
}

.team-players-data .position-section {
  flex: 1;
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm);
  box-shadow: var(--shadow-sm);
}

.team-players-data .position-title {
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
}

.team-players-data .position-icon {
  font-size: var(--font-size-xxl);
  color: var(--color-primary);
}

.team-players-data .players-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.team-players-data .player-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
}

.team-players-data .player-avatar {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-circle);
}

.team-players-data .player-info {
  flex: 1;
}

.team-players-data .player-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
}

.team-players-data .player-number {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.team-players-data .player-username {
  font-size: var(--font-size-md);
  color: var(--color-text-disabled);
}

.team-players-data .empty-players {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.team-players-data .empty-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

.search-container {
  text-align: center;
  padding: var(--spacing-lg) var(--spacing-md);
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.search-input-section {
  margin-bottom: var(--spacing-xl);
}

.search-results {
  margin-top: var(--spacing-xl);
}

.team-search-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  border-bottom: var(--border-width) solid var(--color-border-light);
}

.team-search-item .ant-list-item-meta {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.team-search-item .ant-list-item-meta-avatar {
  flex-shrink: 0;
}

.team-search-item .ant-list-item-meta-content {
  flex: 1;
  min-width: 0;
}

.team-search-item .ant-list-item-actions {
  flex-shrink: 0;
}

.team-search-item .ant-list-item-actions .ant-btn {
  min-width: 60px;
  white-space: nowrap;
}

.team-search-item:last-child {
  border-bottom: none;
}

.team-name {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
}

.team-city,
.team-description {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.no-results {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.no-results-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.no-results-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

.search-placeholder {
  text-align: center;
  padding: 40px var(--spacing-xl);
  color: var(--color-text-disabled);
  font-style: italic;
}

.search-placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
}

.search-placeholder-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-text-disabled);
}

.search-description {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
}

.view-team-info-screen {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
}

.view-team-info-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
  padding: var(--spacing-xl);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-md);
}

.view-team-logo-section {
  flex-shrink: 0;
}

.view-team-logo {
  border: var(--border-width-thicker) solid var(--color-border-secondary);
  box-shadow: var(--shadow-lg);
}

.view-team-basic-info {
  flex: 1;
}

.view-team-basic-info .view-team-name {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xxxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  cursor: default;
}

.view-team-basic-info .view-team-name:hover {
  color: var(--color-text-primary);
  text-decoration: none;
}

.view-team-city {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.view-team-info-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.view-info-section {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
}

.view-info-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  border-bottom: var(--border-width-thick) solid var(--color-border-secondary);
  padding-bottom: var(--spacing-sm);
}

.view-team-description {
  margin: 0;
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
  color: var(--color-text-secondary);
}

.view-social-networks {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.view-social-link {
  color: var(--color-primary);
  text-decoration: none;
  font-size: var(--font-size-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-hover);
  border-radius: var(--border-radius-md);
  transition: all var(--transition-slow) ease;
}

.view-social-link:hover {
  background: var(--color-primary-light);
  color: var(--color-primary-hover);
  text-decoration: underline;
}

.view-team-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-lg);
}

.view-team-stats .view-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-bg-hover);
  border-radius: var(--border-radius-lg);
  border-left: var(--border-width-thick) solid var(--color-primary);
}

.view-team-stats .view-stat-label {
  font-size: var(--font-size-md);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.view-team-stats .view-stat-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.view-admin-actions {
  margin-top: var(--spacing-lg);
}

.view-admin-actions .ant-btn {
  height: 48px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-md);
}
/* Responsive adjustments */
@media (max-width: 768px) {
  .view-team-info-header {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-lg);
  }
  
  .view-team-basic-info .view-team-name {
    font-size: var(--font-size-xxl);
  }
  
  .view-team-stats {
    grid-template-columns: 1fr;
  }
  
  .view-team-info-screen {
    padding: var(--spacing-lg);
  }
  
  .view-info-section {
    padding: var(--spacing-lg);
  }
}

.team-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.team-city {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-md);
}
.status-badge {
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-xl);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.status-badge.pending {
  background-color: var(--color-warning-light);
  color: var(--color-warning-dark);
  border: var(--border-width) solid var(--color-warning-border);
}

.status-badge.rejected {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  border: var(--border-width) solid var(--color-error-border);
}

.participation-settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.participation-settings-form .ant-form-item {
  margin-bottom: var(--spacing-lg);
}
.participation-settings-form .ant-input,
.participation-settings-form .ant-select,
.participation-settings-form .ant-textarea {
  border-radius: var(--border-radius-md);
}

.participation-settings-form .ant-input:focus,
.participation-settings-form .ant-select:focus,
.participation-settings-form .ant-textarea:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.participation-settings-form .ant-input[disabled],
.participation-settings-form .ant-select[disabled],
.participation-settings-form .ant-textarea[disabled] {
  background-color: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  cursor: not-allowed;
}

.participation-settings-form .ant-button {
  height: var(--button-height);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-md);
}

.participation-settings-form .ant-button:hover {
  background-color: var(--color-success-hover);
  border-color: var(--color-success-hover);
}

.participation-settings-form .ant-button:focus {
  background-color: var(--color-success);
  border-color: var(--color-success);
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Team Info Tabs Styles */
.team-info-tabs {
  margin-top: var(--spacing-xl);
}

.team-info-tabs .ant-tabs-nav {
  margin-bottom: var(--spacing-xl);
}

.team-info-tabs .ant-tabs-tab {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  padding: var(--spacing-md) var(--spacing-xl);
}

.team-info-tabs .ant-tabs-tab-active {
  color: var(--color-primary);
}

/* Invitations Styles */
.invitations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-hover);
  border-radius: var(--border-radius-lg);
}

.invitations-header p {
  margin: 0;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-md);
}

.loading-invitations {
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-xl);
}

.loading-invitations p {
  margin-top: var(--spacing-lg);
  color: var(--color-text-tertiary);
}

.empty-invitations {
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-xl);
  color: var(--color-text-disabled);
}

.empty-invitations .empty-icon {
  font-size: 48px;
  color: var(--color-border-primary);
  margin-bottom: var(--spacing-lg);
}

.empty-invitations h3 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

.empty-invitations p {
  margin: 0;
  font-size: var(--font-size-md);
}

.invitations-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.invitation-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--spacing-xl);
  background: var(--color-bg-primary);
  border: var(--border-width) solid var(--color-border-secondary);
  border-radius: var(--border-radius-xl);
  transition: all var(--transition-normal) ease;
}

.invitation-item:hover {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-lg);
}

.invitation-info {
  flex: 1;
  min-width: 0;
}

.invitation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

.invitation-code {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.code-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.code-value {
  font-family: 'Courier New', monospace;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  background: var(--color-primary-light);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

.invitation-details {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.invitation-stat {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.invitation-stat .stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.invitation-stat .stat-value {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.invitation-link {
  margin-top: var(--spacing-md);
}

.link-input {
  font-family: 'Courier New', monospace;
  font-size: var(--font-size-sm);
}

.copy-btn {
  color: var(--color-primary);
  border: none;
  padding: var(--spacing-xs) var(--spacing-sm);
}

.copy-btn:hover {
  background: var(--color-primary-light);
  color: var(--color-primary-hover);
}

.invitation-actions {
  flex-shrink: 0;
  margin-left: var(--spacing-lg);
}

.form-help-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  margin-top: var(--spacing-xs);
  line-height: var(--line-height-normal);
}

/* Responsive adjustments for invitations */
@media (max-width: 768px) {
  .invitations-header {
    flex-direction: column;
    gap: var(--spacing-lg);
    text-align: center;
  }
  
  .invitation-item {
    flex-direction: column;
    gap: var(--spacing-lg);
  }
  
  .invitation-actions {
    margin-left: 0;
    align-self: flex-end;
  }
  
  .invitation-details {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}

.participation-settings-form .ant-button[disabled] {
  background-color: var(--color-bg-tertiary);
  border-color: var(--color-border-primary);
  cursor: not-allowed;
}

.telegram-sidebar {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-hover);
  padding: 0;
  margin: calc(-1 * var(--spacing-xl));
}

.sidebar-header {
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 0;
  margin-bottom: 0;
  box-shadow: var(--shadow-md);
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 120px;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex: 1;
  transition: all var(--transition-normal) ease;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  min-width: 0;
}

.user-name {
  margin: 0;
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-shadow: var(--shadow-sm);
  line-height: var(--line-height-tight);
  overflow: hidden;
  text-overflow: ellipsis;
}

.test-user-indicator {
  margin-top: var(--spacing-xs);
}

.test-user-indicator .ant-tag {
  margin: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}


.profile-avatar {
  border: var(--border-width-thicker) solid rgba(255,255,255,0.3);
  box-shadow: var(--shadow-md);
  flex-shrink: 0;
}


.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-menu-component {
  border: none !important;
  background: transparent !important;
  height: 100% !important;
}

.sidebar-menu-component .ant-menu-item-group-title {
  font-size: var(--font-size-xs) !important;
  font-weight: var(--font-weight-semibold) !important;
  color: var(--color-text-disabled) !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  line-height: var(--line-height-tight) !important;
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-sm) var(--spacing-lg) !important;
  margin: 0 !important;
  background: transparent !important;
  border: none !important;
}

.sidebar-menu-component .ant-menu-item {
  height: 60px !important;
  line-height: 60px !important;
  margin: 0 var(--spacing-sm) var(--spacing-xs) var(--spacing-sm) !important;
  border-radius: var(--border-radius-xl) !important;
  background: var(--color-bg-primary) !important;
  border: none !important;
  box-shadow: var(--shadow-sm) !important;
  transition: all var(--transition-normal) ease !important;
  display: flex !important;
  align-items: center !important;
  padding: 0 var(--spacing-lg) !important;
}

.sidebar-menu-component .ant-menu-item:hover {
  background-color: var(--color-bg-hover) !important;
  transform: translateY(-1px) !important;
  box-shadow: var(--shadow-lg) !important;
}

.sidebar-menu-component .ant-menu-item.ant-menu-item-selected {
  background-color: var(--color-primary-light) !important;
  color: var(--color-primary) !important;
  border: none !important;
}

.sidebar-menu-component .ant-menu-item .ant-menu-title-content {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
  font-size: var(--font-size-md) !important;
  font-weight: var(--font-weight-normal) !important;
  color: var(--color-text-secondary) !important;
  line-height: var(--line-height-normal) !important;
}

.sidebar-menu-component .ant-menu-item .anticon {
  font-size: var(--font-size-xl) !important;
  color: var(--color-primary) !important;
  margin-right: var(--spacing-md) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 32px !important;
  height: 32px !important;
  background: var(--color-primary-light) !important;
  border-radius: var(--border-radius-lg) !important;
}

.team-menu-item .ant-menu-title-content {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
}



.sidebar-menu-component .ant-menu-item.ant-menu-item-selected .ant-menu-title-content {
  color: var(--color-primary) !important;
  font-weight: var(--font-weight-medium) !important;
}

.sidebar-menu-component .ant-menu-item.ant-menu-item-selected .team-name {
  color: var(--color-primary) !important;
  font-weight: var(--font-weight-medium) !important;
}

.edit-team-btn {
  opacity: 0 !important;
  transition: all var(--transition-normal) ease !important;
  flex-shrink: 0 !important;
  margin-left: var(--spacing-sm) !important;
  width: 32px !important;
  height: 32px !important;
  border-radius: var(--border-radius-lg) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: none !important;
  box-shadow: none !important;
}

.team-menu-item:hover .edit-team-btn {
  opacity: 1 !important;
  background: var(--color-primary-light) !important;
}

.edit-team-btn:hover {
  background: var(--color-primary-hover) !important;
  color: var(--color-primary) !important;
  border: none !important;
  box-shadow: none !important;
}

.sidebar-menu-component .ant-menu-item.ant-menu-item-disabled {
  background: var(--color-bg-primary) !important;
  color: var(--color-text-disabled) !important;
  border: var(--border-width-thick) dashed var(--color-border-primary) !important;
  cursor: default !important;
}

.sidebar-menu-component .ant-menu-item.ant-menu-item-disabled:hover {
  background: var(--color-bg-primary) !important;
  transform: none !important;
  box-shadow: var(--shadow-sm) !important;
}

.pending-badge {
  position: absolute !important;
  top: 50% !important;
  right: var(--spacing-lg) !important;
  transform: translateY(-50%) !important;
  background-color: var(--color-error) !important;
  color: var(--color-text-primary) !important;
  border-radius: 10px !important;
  min-width: 18px !important;
  height: 18px !important;
  font-size: var(--font-size-xs) !important;
  font-weight: var(--font-weight-semibold) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: var(--border-width-thick) solid var(--color-bg-primary) !important;
  box-shadow: var(--shadow-sm) !important;
  z-index: 1 !important;
}

.sidebar-footer {
  padding: var(--spacing-lg) var(--spacing-lg);
  border-top: var(--border-width) solid var(--color-border-primary);
  background: var(--color-bg-primary);
  margin: 0;
  box-shadow: var(--shadow-sm);
}

.app-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  text-align: center;
}

.app-info h5 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: var(--line-height-tight);
}

.app-info p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-disabled);
  line-height: var(--line-height-tight);
}

.test-user-switcher {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.test-user-switcher span {
  flex: 1;
  margin-right: var(--spacing-sm);
}

.test-user-switcher .ant-select {
  flex-shrink: 0;
}

.menu-items {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-xl);
  transition: all var(--transition-normal) ease;
  cursor: pointer;
  border: none;
  min-height: 60px;
  box-shadow: var(--shadow-sm);
}

.menu-item:hover {
  background-color: var(--color-bg-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-lg);
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex: 1;
  min-width: 0;
  height: 100%;
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: var(--font-size-xl);
  color: var(--color-primary);
  flex-shrink: 0;
  background: var(--color-primary-light);
  border-radius: var(--border-radius-lg);
}

.menu-item-text {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  position: relative;
  min-width: 0;
  flex: 1;
  justify-content: center;
}

.menu-item-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  line-height: var(--line-height-normal);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-item {
  padding: 14px var(--spacing-lg);
}

.team-item .menu-item-content {
  gap: var(--spacing-md);
}

.team-item .ant-avatar {
  flex-shrink: 0;
  border: none;
}

.edit-team-btn {
  opacity: 0;
  transition: all var(--transition-normal) ease;
  flex-shrink: 0;
  margin-left: var(--spacing-sm);
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-item:hover .edit-team-btn {
  opacity: 1;
  background: var(--color-primary-light);
}

.edit-team-btn:hover {
  background: var(--color-primary-hover) !important;
  color: var(--color-primary) !important;
}

.empty-teams {
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-lg);
  color: var(--color-text-disabled);
  font-style: italic;
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-xl);
  border: var(--border-width-thick) dashed var(--color-border-primary);
  margin: var(--spacing-sm) 0;
}

.empty-teams p {
  margin: 0;
  font-size: var(--font-size-md);
}

.pending-badge {
  position: absolute;
  top: 50%;
  right: var(--spacing-lg);
  transform: translateY(-50%);
  background-color: var(--color-error);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-circle);
  min-width: 18px;
  height: 18px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: center;
  border: var(--border-width-thick) solid var(--color-bg-primary);
  box-shadow: var(--shadow-sm);
  z-index: 1;
}

.team-logo-icon {
  margin-right: var(--spacing-md) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 32px !important;
  height: 32px !important;
}

.team-header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.edit-team-header-btn {
  background-color: var(--color-primary);
  color: var(--color-text-primary);
  border: none;
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
}

.edit-team-header-btn:hover {
  background-color: var(--color-primary-hover);
}

/* Override Ant Design default card padding */
:deep(.event-card .ant-card-body),
:deep(.request-card .ant-card-body),
:deep(.profile-settings.card .ant-card-body),
:deep(.player-profile-screen .profile-details.card .ant-card-body),
:deep(.event-screen > .event-card .ant-card-body),
:deep(.event-tabs .ant-card-body) {
  padding: var(--spacing-lg) !important;
}

.event-card {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-xl);
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all var(--transition-slow) ease;
  border: var(--border-width) solid var(--color-border-secondary);
  margin-bottom: var(--spacing-lg);
}



.event-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: var(--border-width) solid var(--color-bg-tertiary);
}

.event-type-badge {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 6px var(--spacing-md);
  border-radius: 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: var(--shadow-sm);
}

.event-type-badge.game {
  background-color: var(--color-success);
  color: #fff;
}
.event-type-badge.training {
  background-color: var(--color-primary);
  color: #fff;
}

.badge-icon {
  font-size: var(--font-size-md);
  display: flex;
  align-items: center;
}

.badge-text {
  font-weight: var(--font-weight-semibold);
}

.repeating-indicator {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: inherit;
  opacity: 0.9;
}

.event-card-content {
  padding: var(--spacing-sm) var(--spacing-md);
  cursor: pointer;
}

.event-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-sm);
}
.event-info-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) 0;
}

.event-icon {
  font-size: var(--font-size-lg);
  color: var(--color-text-tertiary);
  flex-shrink: 0;
}

.event-text {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  line-height: var(--line-height-normal);
  flex: 1;
}

.event-date-highlight {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  background: var(--color-primary-light);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-md);
  display: inline-block;
  margin: 1px 0;
}
.comment-text {
  font-style: italic;
  background: var(--color-bg-hover);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  border-left: var(--border-width-thicker) solid var(--color-primary);
}

.opponent-text {
  font-weight: var(--font-weight-semibold);
  color: #d63384;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
}

.roster-text {
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  background: var(--color-primary-light);
}

.roster-default-badge {
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  margin-left: var(--spacing-xs);
}

.event-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: -20px;
  padding: var(--spacing-xs) 0;
  border-top: var(--border-width) solid var(--color-border-secondary);
  gap: var(--spacing-xl);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  color: gray;
}

.stat-item.participating {
  color: var(--color-success);
}

.stat-item.unknown {
  color: gray;
}

.stat-item.not-participating {
  color: var(--color-error);
}

.stat-number {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.event-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  
 
}



.user-participation-status {
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-circle);
  transition: all var(--transition-duration) ease;
  background: var(--color-overlay-light);
  border: var(--border-width-thick) solid transparent;
}

.user-participation-status:hover {
  background: var(--color-bg-primary);
  border-color: var(--color-primary);
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .event-card {
    margin-bottom: 12px;
  }
  
  .event-card-header {
    padding: 8px 0px 12px;
    margin: -10px 10px 0px 10px;
  }
  
 
  
  
  .stat-item {
    padding: 6px 8px;
    min-width: 50px;
  }
  
  .stat-number {
    font-size: 16px;
  }
}

/* Participation status badge colors */
.event-type-badge.status-participating {
  background-color: #52c41a;
  color: #fff;
}

.event-type-badge.status-not-participating {
  background-color: #ff4d4f;
  color: #fff;
}

.event-type-badge.status-unknown {
  background-color: #8c8c8c;
  color: #fff;
}

.badge-icon {
  font-size: 14px;
  display: flex;
  align-items: center;
}

.value-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.edit-number-container {
  width: 100%;
  padding-top: 8px;
}

.edit-number-input {
  width: 100%;
  margin-bottom: 12px;
}
.edit-number-input .ant-input-number-input {
  text-align: center;
  font-size: 18px;
}

.edit-number-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.edit-action-button {
  margin-left: 8px;
}

/* Icon button styles */
.edit-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.edit-icon-button:hover {
  background: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
}

.edit-icon-button .anticon {
  font-size: 18px;
  color: #1890ff;
}

.edit-icon-button:hover .anticon {
  color: #1890ff;
}

.settings-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.settings-icon-button:hover {
  background: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
}

.settings-icon-button .anticon {
  font-size: 18px;
  color: #1890ff;
}

.settings-icon-button:hover .anticon {
  color: #1890ff;
}

.add-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.add-icon-button:hover {
  background: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
}

.add-icon-button .anticon {
  font-size: 18px;
  color: #1890ff;
}

.add-icon-button:hover .anticon {
  color: #1890ff;
}

.delete-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.delete-icon-button:hover {
  background: #fff2f0;
  border-color: #ff4d4f;
  color: #ff4d4f;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 77, 79, 0.2);
}

.delete-icon-button .anticon {
  font-size: 20px;
  color: #ff4d4f;
}

.delete-icon-button:hover .anticon {
  color: #ff4d4f;
}

/* Header button specific styles */
.header-button.edit-icon-button,
.header-button.delete-icon-button,
.header-button.settings-icon-button,
.header-button.add-icon-button {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.header-button.edit-icon-button .anticon,
.header-button.delete-icon-button .anticon,
.header-button.settings-icon-button .anticon,
.header-button.add-icon-button .anticon {
  font-size: 20px;
}

/* Back button styles */
.back-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.back-icon-button:hover {
  background: #f5f5f5;
  color: #262626;
  transform: translateY(-1px);
}

.back-icon-button .anticon {
  font-size: 20px;
  color: #262626;
}

.back-icon-button:hover .anticon {
  color: #262626;
}

/* Header button specific styles for back button */
.header-button.back-icon-button {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.header-button.back-icon-button .anticon {
  font-size: 20px;
}

/* Calendar nav button specific styles */
.calendar-nav-button.back-icon-button {
  width: 28px;
  height: 28px;
  border-radius: 6px;
}

.calendar-nav-button.back-icon-button .anticon {
  font-size: 16px;
}

/* Menu button styles */
.menu-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.menu-icon-button:hover {
  background: #f5f5f5;
  color: #262626;
  transform: translateY(-1px);
}

.menu-icon-button .anticon {
  font-size: 20px;
  color: #262626;
}

.menu-icon-button:hover .anticon {
  color: #262626;
}

/* Calendar button styles */
.calendar-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.calendar-icon-button:hover {
  background: #e6f7ff;
  color: #1890ff;
  transform: translateY(-1px);
}

.calendar-icon-button .anticon {
  font-size: 20px;
  color: #1890ff;
}

.calendar-icon-button:hover .anticon {
  color: #1890ff;
}

/* Plus button styles */
.plus-icon-button {
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.plus-icon-button:hover {
  background: #f6ffed;
  color: #52c41a;
  transform: translateY(-1px);
}

.plus-icon-button .anticon {
  font-size: 20px;
  color: #52c41a;
}

.plus-icon-button:hover .anticon {
  color: #52c41a;
}

/* Header button specific styles for menu, calendar, and plus buttons */
.header-button.menu-icon-button,
.header-button.calendar-icon-button,
.header-button.plus-icon-button {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.header-button.menu-icon-button .anticon,
.header-button.calendar-icon-button .anticon,
.header-button.plus-icon-button .anticon {
  font-size: 20px;
}

/* Team avatar button styles */
.team-avatar-button {
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.team-avatar-button:hover {
  background: #f5f5f5;
  transform: translateY(-1px);
}

.team-avatar-button .ant-avatar {
  border: var(--border-width-thick) solid transparent;
  transition: all var(--transition-duration) ease;
}

.team-avatar-button:hover .ant-avatar {
  border-color: var(--color-primary);
  transform: scale(1.05);
}

/* Mobile-adaptive popconfirm styles */
:deep(.ant-popconfirm) {
  max-width: 90vw;
  min-width: 280px;
}

  :deep(.ant-popconfirm-inner) {
    padding: var(--spacing-lg);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-lg);
  }

  :deep(.ant-popconfirm-title) {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-sm);
    line-height: var(--line-height-tight);
  }

  :deep(.ant-popconfirm-description) {
    font-size: var(--font-size-md);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    margin-bottom: var(--spacing-md);
  }

  :deep(.ant-popconfirm-buttons) {
    display: flex;
    gap: var(--spacing-sm);
    margin-top: var(--spacing-lg);
  }

  :deep(.ant-popconfirm-buttons .ant-btn) {
    flex: 1;
    height: var(--button-height);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-medium);
    border-radius: var(--border-radius-md);
    border: none;
  }

:deep(.ant-popconfirm-buttons .ant-btn-primary) {
  background: #ff4d4f;
  color: white;
}

:deep(.ant-popconfirm-buttons .ant-btn-primary:hover) {
  background: var(--color-error-hover);
}

:deep(.ant-popconfirm-buttons .ant-btn-default) {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  border: var(--border-width) solid var(--color-border-primary);
}

:deep(.ant-popconfirm-buttons .ant-btn-default:hover) {
  background: var(--color-primary-light);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Radio group styles for repeating events */
:deep(.ant-radio-group) {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 100%;
}

  :deep(.ant-radio-button-wrapper) {
    width: 100%;
    text-align: center;
    height: var(--button-height);
    line-height: calc(var(--button-height) - 2px);
    font-size: var(--font-size-md);
    border-radius: var(--border-radius-lg);
    border: var(--border-width) solid var(--color-border-primary);
    margin-right: 0;
    margin-bottom: var(--spacing-sm);
  }

:deep(.ant-radio-button-wrapper:first-child) {
  border-radius: var(--border-radius-lg);
}

:deep(.ant-radio-button-wrapper:last-child) {
  border-radius: var(--border-radius-lg);
}

:deep(.ant-radio-button-wrapper-checked) {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-text-primary);
}

:deep(.ant-radio-button-wrapper-checked:hover) {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
  color: var(--color-text-primary);
}

/* Mobile-specific adjustments */
@media (max-width: 480px) {
  :deep(.ant-popconfirm) {
    max-width: 95vw;
    min-width: 260px;
  }
  
  :deep(.ant-popconfirm-inner) {
    padding: var(--spacing-lg);
  }
  
  :deep(.ant-popconfirm-title) {
    font-size: var(--font-size-md);
  }
  
  :deep(.ant-popconfirm-description) {
    font-size: var(--font-size-sm);
  }
  
  :deep(.ant-popconfirm-buttons .ant-btn) {
    height: var(--button-height);
    font-size: var(--font-size-md);
  }
  
  :deep(.ant-radio-button-wrapper) {
    height: var(--button-height);
    line-height: calc(var(--button-height) - 2px);
    font-size: var(--font-size-sm);
  }
}

/* Dropdown menu styles for player selection */
.ant-dropdown-menu {
  max-height: 300px !important;
  overflow-y: auto !important;
  border-radius: var(--border-radius-lg) !important;
  box-shadow: var(--shadow-lg) !important;
}

.ant-dropdown-menu::-webkit-scrollbar {
  width: 6px !important;
}

.ant-dropdown-menu::-webkit-scrollbar-track {
  background: var(--color-bg-tertiary) !important;
  border-radius: 3px !important;
}

.ant-dropdown-menu::-webkit-scrollbar-thumb {
  background: var(--color-border-primary) !important;
  border-radius: 3px !important;
}

.ant-dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-tertiary) !important;
}

.ant-dropdown-menu-item {
  padding: var(--spacing-md) var(--spacing-lg) !important;
  display: flex !important;
  align-items: center !important;
  gap: var(--spacing-md) !important;
  transition: all var(--transition-normal) ease !important;
}

.ant-dropdown-menu-item:hover {
  background: var(--color-primary-light) !important;
}

.ant-dropdown-menu-item .ant-avatar {
  flex-shrink: 0 !important;
}

.ant-dropdown-menu-item-disabled {
  color: var(--color-text-disabled) !important;
  cursor: not-allowed !important;
}

.ant-dropdown-menu-item-disabled:hover {
  background: transparent !important;
}

/* Mobile adjustments for dropdown menu */
@media (max-width: 480px) {
  .ant-dropdown-menu {
    max-height: 250px !important;
  }
  
  .ant-dropdown-menu-item {
    padding: var(--spacing-sm) var(--spacing-md) !important;
  }
  
  .ant-dropdown-menu-item .ant-avatar {
    width: 36px !important;
    height: 36px !important;
  }
}

/* Notification section styles */
.notification-section {
  margin-bottom: var(--spacing-lg);
}

.notification-card {
  background: linear-gradient(135deg, var(--color-warning-light) 0%, var(--color-warning-lighter) 100%);
  border: var(--border-width) solid var(--color-warning-border);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-sm);
}

.notification-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
}

.notification-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex: 1;
}

.notification-icon {
  font-size: var(--font-size-xxl);
  color: var(--color-warning);
  background: var(--color-warning-light);
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-lg);
}

.notification-text {
  flex: 1;
}

.notification-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
}

.notification-subtitle {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
}

.notification-button {
  background: var(--color-warning);
  border-color: var(--color-warning);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  height: var(--button-height);
  padding: 0 var(--spacing-xl);
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  transition: all var(--transition-normal) ease;
  white-space: nowrap;
}

.notification-button:hover {
  background: var(--color-warning-hover);
  border-color: var(--color-warning-hover);
  color: var(--color-text-primary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.notification-button:active {
  transform: translateY(0);
}

.notification-button .anticon {
  font-size: var(--font-size-lg);
}
/* Mobile responsive styles for notification section */
@media (max-width: 480px) {
  .notification-content {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-md);
  }
  
  .notification-info {
    justify-content: center;
  }
  
  .notification-button {
    width: 100%;
    justify-content: center;
    height: 48px;
    font-size: var(--font-size-lg);
  }
  
  .notification-title {
    font-size: var(--font-size-md);
  }
  
  .notification-subtitle {
    font-size: var(--font-size-sm);
  }
}

.captain-badge {
  font-size: var(--font-size-md);
  color: #fff;
  background: #1976d2;
  padding: 2px 6px;
  border-radius: 8px;
  margin-top: 4px;
  display: inline-block;
  font-weight: bold;
}
.assistant-badge {
  font-size: var(--font-size-md);
  color: #fff;
  background: #1976d2;
  padding: 2px 6px;
  border-radius: 8px;
  margin-top: 4px;
  display: inline-block;
  font-weight: bold;
}

.player-name {
  display: flex;
  align-items: center;
  gap: 4px;
}

.player-name-text {
  /* Убираем flex: 1 чтобы имя не растягивалось */
  flex: none;
}

.captain-status {
  font-size: var(--font-size-sm);
  color: #fff;
  background: #1976d2;
  padding: 1px 4px;
  border-radius: 4px;
  font-weight: bold;
  flex-shrink: 0;
}
.assistant-status {
  font-size: var(--font-size-sm);
  color: #fff;
  background: #1976d2;
  padding: 1px 4px;
  border-radius: 4px;
  font-weight: bold;
  flex-shrink: 0;
}

/* Центрирование статуса игрока */
.captain-status,
.assistant-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
}

.edit-role-container,
.edit-captain-status-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-role-input,
.edit-captain-status-input {
  width: 200px;
}

.edit-role-actions,
.edit-captain-status-actions {
  display: flex;
  gap: 8px;
}

/* Разделительные линии между игроками */
.ant-list-item {
  position: relative;
  padding: var(--spacing-sm) 0 !important;
}

.ant-list-item:not(:last-child)::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80%;
  height: 1px;
  background-color: var(--color-border-light);
}

.ant-list-item:hover {
  background-color: var(--color-bg-secondary) !important;
  border-radius: var(--border-radius-md) !important;
}

/* Player Info Popup Styles */
.player-info-popup {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: var(--spacing-lg);
  min-width: 120px;
  max-width: 320px;
}

.player-info-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--color-border-light);
}

.player-info-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.player-info-popup .player-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.2;
}

.player-info-popup .player-number {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.player-info-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.info-label {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
  min-width: 60px;
}

.info-value {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  text-align: right;
}

.player-info-actions {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border-light);
}

.player-info-actions .ant-btn {
  margin-top: var(--spacing-sm);
}

/* Mobile responsive styles for player info popup */
@media (max-width: 480px) {
  .player-info-popup {
    min-width: 120px;
    max-width: 280px;
    padding: var(--spacing-md);
  }
  
  .player-info-header {
    margin-bottom: var(--spacing-md);
    padding-bottom: var(--spacing-sm);
  }
  
  .player-info-popup .player-name {
    font-size: var(--font-size-md);
  }
  
  .player-info-popup .player-number {
    font-size: var(--font-size-sm);
  }
  
  .info-row {
    gap: var(--spacing-sm);
  }
  
  .info-label,
  .info-value {
    font-size: var(--font-size-sm);
  }
}



/* Team settings container */
.team-settings-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

@media (max-width: 768px) {
  .team-settings-container {
    gap: var(--spacing-lg);
  }
}
</style>